// src/sdk.ts
import {
  CachedContent as CachedContent11,
  cacheTime24h as cacheTime24h11,
  getFutureTime as getFutureTime11,
  patchFixSuiObjectId
} from "@cetusprotocol/cetus-sui-clmm-sdk";
import { isValidSuiAddress } from "@mysten/sui.js/utils";

// src/modules/boosterModule.ts
import {
  CachedContent,
  cacheTime24h,
  cacheTime5min,
  CLOCK_ADDRESS,
  d as d2,
  extractStructTagFromType as extractStructTagFromType2,
  getFutureTime,
  getMoveObjectType,
  getObjectFields as getObjectFields2,
  getPackagerConfigs
} from "@cetusprotocol/cetus-sui-clmm-sdk";
import { TransactionBlock } from "@mysten/sui.js/transactions";

// src/utils/booster.ts
import { buildPosition, d, extractStructTagFromType, getObjectFields, getObjectId } from "@cetusprotocol/cetus-sui-clmm-sdk";

// src/types/booster_type.ts
var BoosterRouterModule = "router";
var CONFIG_PERCENT_MULTIPER = 1e4;

// src/utils/booster.ts
var BoosterUtil = class {
  static buildPoolImmutables(data) {
    const { fields } = data.value.fields.value;
    const pool = {
      clmm_pool_id: extractStructTagFromType(fields.clmm_pool_id).address,
      booster_type: extractStructTagFromType(fields.booster_type.fields.name).source_address,
      pool_id: extractStructTagFromType(fields.pool_id).address,
      coinTypeA: extractStructTagFromType(fields.coin_type_a.fields.name).source_address,
      coinTypeB: extractStructTagFromType(fields.coin_type_b.fields.name).source_address
    };
    return pool;
  }
  static buildPoolState(data) {
    const fields = getObjectFields(data);
    const lockMultipliers = [];
    fields.config.fields.contents.forEach((item) => {
      lockMultipliers.push({
        lock_day: Number(item.fields.key),
        multiplier: Number(d(item.fields.value).div(CONFIG_PERCENT_MULTIPER))
      });
    });
    const pool = {
      basic_percent: Number(d(fields.basic_percent).div(CONFIG_PERCENT_MULTIPER)),
      balances: {
        balances_handle: fields.balances.fields.id.id,
        size: fields.balances.fields.size
      },
      config: lockMultipliers,
      lock_positions: {
        lock_positions_handle: fields.lock_positions.fields.id.id,
        size: fields.lock_positions.fields.size
      },
      is_open: fields.is_open,
      index: Number(fields.index),
      pool_id: getObjectId(data)
    };
    return pool;
  }
  static buildLockNFT(data) {
    const locked_nft_id = extractStructTagFromType(getObjectId(data)).address;
    const lock_clmm_position = buildPosition(data);
    const lockNFT = {
      lock_clmm_position,
      locked_nft_id
    };
    return lockNFT;
  }
  static buildLockPositionInfo(data) {
    const fields = getObjectFields(data);
    const { value } = fields.value.fields;
    const lockNFT = {
      type: value.type,
      position_id: value.fields.position_id,
      start_time: Number(value.fields.start_time),
      lock_period: Number(value.fields.lock_period),
      end_time: Number(value.fields.end_time),
      growth_rewarder: value.fields.growth_rewarder,
      rewarder_owned: value.fields.rewarder_owned,
      is_settled: value.fields.is_settled
    };
    return lockNFT;
  }
  static isLocked(lock) {
    return lock.end_time > Date.parse((/* @__PURE__ */ new Date()).toString()) / 1e3;
  }
};

// src/modules/boosterModule.ts
var BoosterModule = class {
  _sdk;
  _cache = {};
  constructor(sdk) {
    this._sdk = sdk;
  }
  get sdk() {
    return this._sdk;
  }
  /**
   * Gets a list of booster pool immutables.
   *
   * @param forceRefresh Whether to force a refresh of the cache.
   * @returns array of PoolImmutable objects.
   */
  async getPoolImmutables(forceRefresh = false) {
    const { booster } = this._sdk.sdkOptions;
    const cacheKey = `${booster.package_id}_getPoolImmutables`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    const allPool = [];
    if (cacheData !== void 0) {
      allPool.push(...cacheData);
    } else {
      const simplePoolIds = [];
      const result = await this._sdk.ClmmSDK.fullClient.getDynamicFieldsByPage(getPackagerConfigs(booster).booster_pool_handle);
      result.data?.forEach((item) => {
        simplePoolIds.push(item.objectId);
      });
      const simpleDatas = await this._sdk.ClmmSDK.fullClient.batchGetObjects(simplePoolIds, {
        showContent: true
      });
      for (const item of simpleDatas) {
        const fields = getObjectFields2(item);
        if (fields) {
          const poolImmutables = BoosterUtil.buildPoolImmutables(fields);
          this.updateCache(`${poolImmutables.pool_id}_getPoolImmutable`, poolImmutables, cacheTime24h);
          allPool.push(poolImmutables);
        }
      }
    }
    this.updateCache(cacheKey, allPool, cacheTime24h);
    return allPool;
  }
  /**
   * Gets a pool immutables by its object ID.
   *
   * @param poolObjectId The object ID of the pool to get.
   * @returns A promise that resolves to a Pool object.
   */
  async getPoolImmutable(poolObjectId) {
    const { booster } = this._sdk.sdkOptions;
    const cacheKey = `${poolObjectId}_getPoolImmutable`;
    const cacheData = this.getCache(cacheKey);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const result = await this._sdk.ClmmSDK.fullClient.getDynamicFieldObject({
      parentId: getPackagerConfigs(booster).booster_pool_handle,
      name: {
        type: "0x2::object::ID",
        value: poolObjectId
      }
    });
    const fields = getObjectFields2(result);
    const poolImmutables = BoosterUtil.buildPoolImmutables(fields);
    this.updateCache(cacheKey, poolImmutables, cacheTime24h);
    return poolImmutables;
  }
  /**
   * Gets a list of booster pools.
   *
   * @returns array of Pool objects.
   */
  async getPools() {
    const allPool = [];
    const poolImmutables = await this.getPoolImmutables();
    const poolObjectIds = poolImmutables.map((item) => {
      return item.pool_id;
    });
    const objectDataResponses = await this._sdk.ClmmSDK.fullClient.batchGetObjects(poolObjectIds, { showType: true, showContent: true });
    for (const suiObj of objectDataResponses) {
      const poolState = BoosterUtil.buildPoolState(suiObj);
      if (poolState) {
        const pool = {
          ...await this.getPoolImmutable(poolState.pool_id),
          ...poolState
        };
        allPool.push(pool);
        const cacheKey = `${pool.pool_id}_getPoolObject`;
        this.updateCache(cacheKey, pool, cacheTime24h);
      }
    }
    return allPool;
  }
  /**
   * Gets a pool by its object ID.
   *
   * @param poolObjectId The object ID of the pool to get.
   * @param forceRefresh Whether to force a refresh of the cache.
   * @returns A promise that resolves to a Pool object.
   */
  async getPool(poolObjectId, forceRefresh = true) {
    const cacheKey = `${poolObjectId}_getPoolObject`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    const poolImmutables = await this.getPoolImmutable(poolObjectId);
    if (cacheData !== void 0) {
      return {
        ...poolImmutables,
        ...cacheData
      };
    }
    const objects = await this._sdk.ClmmSDK.fullClient.getObject({
      id: poolObjectId,
      options: { showContent: true, showType: true }
    });
    const poolState = BoosterUtil.buildPoolState(objects);
    const pool = {
      ...poolImmutables,
      ...poolState
    };
    this.updateCache(cacheKey, pool, cacheTime24h);
    return pool;
  }
  async getPoolHandleId(booster_config_id) {
    const reault = await this._sdk.ClmmSDK.fullClient.getObject({ id: booster_config_id, options: { showContent: true } });
    const fields = getObjectFields2(reault);
    if (fields) {
      return fields.list.fields.id.id;
    }
    return "";
  }
  /**
   * Gets the initial factory event
   *
   * @returns  the initial factory event.
   */
  async getBoosterInitConfigs() {
    const { package_id } = this.sdk.sdkOptions.booster;
    const initEventObjects = (await this._sdk.ClmmSDK.fullClient.queryEventsByPage({ MoveEventType: `${package_id}::config::InitEvent` }))?.data;
    const initEvent = {
      booster_config_id: "",
      booster_pool_handle: ""
    };
    if (initEventObjects.length > 0) {
      initEventObjects.forEach((item) => {
        const fields = item.parsedJson;
        if (fields) {
          initEvent.booster_config_id = fields.config_id;
        }
      });
    }
    initEvent.booster_pool_handle = await this.getPoolHandleId(initEvent.booster_config_id);
    return initEvent;
  }
  /**
   * Gets the booster positions for the given account address.
   *
   * @param {SuiAddressType} accountAddress The account address to get the booster positions for.
   * @param {string} clmm_pool_id The ID of the CLMM pool.
   * @param {string} lock_positions_handle The handle of the lock positions.
   * @returns {Promise<BoosterPositionInfo[]>} A promise that resolves to an array of booster position information objects.
   */
  async getOwnerBoosterPositions(accountAddress, clmm_pool_id, lock_positions_handle) {
    const { booster } = this.sdk.sdkOptions;
    const lockNfts = [];
    const boosterList = [];
    const filterType = `${booster.package_id}::lock_nft::LockNFT<${this._sdk.ClmmSDK.Position.buildPositionType()}>`;
    const ownerRes = await this._sdk.ClmmSDK.fullClient.getOwnedObjectsByPage(accountAddress, {
      options: { showType: true, showContent: true, showOwner: true },
      filter: { StructType: filterType }
    });
    for (const item of ownerRes.data) {
      const type = extractStructTagFromType2(getMoveObjectType(item)).source_address;
      if (type === filterType) {
        if (item.data) {
          const lockCetus = BoosterUtil.buildLockNFT(item);
          this.updateCache(`${lockCetus.locked_nft_id}_getBoosterPositionById`, lockCetus, cacheTime24h);
          if (lockCetus) {
            if (clmm_pool_id === void 0 || clmm_pool_id === lockCetus.lock_clmm_position.pool) {
              lockNfts.push(lockCetus);
            }
          }
        }
      }
    }
    const infos = await this.getLockPositionInfos(
      lock_positions_handle,
      lockNfts.map((item) => item.locked_nft_id)
    );
    for (const nft of lockNfts) {
      for (const info of infos) {
        if (nft.lock_clmm_position.pos_object_id === info.position_id) {
          boosterList.push({ ...nft, ...info });
          break;
        }
      }
    }
    return boosterList;
  }
  /**
   * Get booster position information based on the locked NFT ID
   * @param lock_positions_handle
   * @param locked_nft_id
   * @returns
   */
  async getBoosterPosition(lock_positions_handle, locked_nft_id) {
    const cacheKey = `${locked_nft_id}_getBoosterPositionById`;
    const cacheData = this.getCache(cacheKey);
    let lockNFT;
    if (cacheData !== void 0) {
      lockNFT = cacheData;
    } else {
      const result = await this._sdk.ClmmSDK.fullClient.getObject({
        id: locked_nft_id,
        options: { showContent: true, showOwner: true }
      });
      lockNFT = BoosterUtil.buildLockNFT(result);
      this.updateCache(cacheKey, lockNFT, cacheTime24h);
    }
    const lockPositionInfo = await this.getLockPositionInfo(lock_positions_handle, locked_nft_id);
    return {
      ...lockNFT,
      ...lockPositionInfo
    };
  }
  /**
   * Gets the lock position information objects for the given lock positions handle and lock NFT IDs.
   *
   * @param {SuiObjectIdType} lock_positions_handle The handle of the lock positions.
   * @param {SuiObjectIdType[]} lock_nft_ids An array of lock NFT IDs.
   * @returns {Promise<LockPositionInfo[]>} A promise that resolves to an array of lock position information objects.
   */
  async getLockPositionInfos(lock_positions_handle, lock_nft_ids = []) {
    const result = await this._sdk.ClmmSDK.fullClient.getDynamicFieldsByPage(lock_positions_handle);
    const objectIds = [];
    const positionList = [];
    result.data?.forEach((item) => {
      if (lock_nft_ids.length > 0) {
        if (lock_nft_ids.includes(item.name.value)) {
          objectIds.push(item.objectId);
        }
      } else {
        objectIds.push(item.objectId);
      }
    });
    if (objectIds.length > 0) {
      const results = await this._sdk.ClmmSDK.fullClient.batchGetObjects(objectIds, { showContent: true });
      results.forEach((data) => {
        const position = BoosterUtil.buildLockPositionInfo(data);
        if (position) {
          positionList.push(position);
        }
      });
    }
    return positionList;
  }
  /**
   * Gets the lock position information for the given lock positions handle and lock NFT ID.
   *
   * @param {SuiObjectIdType} lock_positions_handle The handle of the lock positions.
   * @param {SuiObjectIdType} lock_nft_id The ID of the lock NFT.
   * @returns {Promise<LockPositionInfo>} A promise that resolves to the lock position information object.
   */
  async getLockPositionInfo(lock_positions_handle, lock_nft_id) {
    const result = await this._sdk.ClmmSDK.fullClient.getDynamicFieldObject({
      parentId: lock_positions_handle,
      name: {
        type: "0x2::object::ID",
        value: lock_nft_id
      }
    });
    return BoosterUtil.buildLockPositionInfo(result);
  }
  /**
   * Calculates the XCetus rewarder for the given CLMM rewarders, booster pool, and lock position information.
   *
   * @param {RewarderAmountOwed[]} clmmRewarders The CLMM rewarders.
   * @param {BoosterPool} boosterPool The booster pool.
   * @param {LockPositionInfo} lockPositionInfo The lock position information.
   * @returns {string} The XCetus rewarder.
   */
  calculateXCetusRewarder(clmmRewarders, boosterPool, lockPositionInfo) {
    let multiplier = boosterPool.basic_percent;
    let rewarder_now = "0";
    clmmRewarders.forEach((item) => {
      if (item.coin_address === boosterPool.booster_type) {
        console.log("find ", boosterPool.booster_type);
        rewarder_now = item.amount_owed.toString();
      }
    });
    if (!lockPositionInfo.is_settled) {
      boosterPool.config.forEach((item) => {
        if (item.lock_day === lockPositionInfo.lock_period) {
          multiplier = item.multiplier;
        }
      });
    }
    const xcetus_amount = d2(rewarder_now).sub(lockPositionInfo.growth_rewarder).mul(multiplier);
    const xcetus_reward_amount = d2(lockPositionInfo.rewarder_owned).add(xcetus_amount);
    return xcetus_reward_amount.toString();
  }
  /**
   * Creates a transaction block for locking a position.
   *
   * @param {LockPositionParams} params The parameters for the lock position.
   * @returns {TransactionBlock} The transaction block.
   */
  lockPositionPayload(params) {
    const { booster } = this.sdk.sdkOptions;
    const { clmm_pool } = this.sdk.ClmmSDK.sdkOptions;
    const tx = new TransactionBlock();
    tx.moveCall({
      target: `${booster.published_at}::${BoosterRouterModule}::lock_position`,
      typeArguments: [params.booster_type, params.coinTypeA, params.coinTypeB],
      arguments: [
        tx.pure(getPackagerConfigs(booster).booster_config_id),
        tx.pure(getPackagerConfigs(clmm_pool).global_config_id),
        tx.pure(params.booster_pool_id),
        tx.pure(params.clmm_pool_id),
        tx.pure(params.clmm_position_id),
        tx.pure(params.lock_day),
        tx.pure(CLOCK_ADDRESS)
      ]
    });
    return tx;
  }
  /**
   * Creates a transaction block for canceling a lock position.
   *
   * @param {CancelParams} params The parameters for the cancel lock position.
   * @returns {TransactionBlock} The transaction block.
   */
  canceLockPositionPayload(params) {
    const { booster } = this.sdk.sdkOptions;
    const tx = new TransactionBlock();
    tx.moveCall({
      target: `${booster.published_at}::${BoosterRouterModule}::cancel_lock`,
      typeArguments: [params.booster_type],
      arguments: [
        tx.pure(getPackagerConfigs(booster).booster_config_id),
        tx.pure(params.booster_pool_id),
        tx.pure(params.lock_nft_id),
        tx.pure(CLOCK_ADDRESS)
      ]
    });
    return tx;
  }
  /**
   * Redeem the rewarder, get back the Clmm Position if the lock time ends.
   * @param params
   * @returns
   */
  redeemPayload(params) {
    const { booster, xcetus } = this.sdk.sdkOptions;
    const { clmm_pool } = this.sdk.ClmmSDK.sdkOptions;
    const tx = new TransactionBlock();
    const xetusConfig = getPackagerConfigs(xcetus);
    tx.moveCall({
      target: `${booster.published_at}::${BoosterRouterModule}::redeem`,
      typeArguments: [params.booster_type, params.coinTypeA, params.coinTypeB],
      arguments: [
        tx.pure(getPackagerConfigs(booster).booster_config_id),
        tx.pure(getPackagerConfigs(clmm_pool).global_config_id),
        tx.pure(params.booster_pool_id),
        tx.pure(params.lock_nft_id),
        tx.pure(params.clmm_pool_id),
        tx.pure(xetusConfig.lock_manager_id),
        tx.pure(xetusConfig.xcetus_manager_id),
        tx.pure(params.ve_nft_id),
        tx.pure(CLOCK_ADDRESS)
      ]
    });
    return tx;
  }
  updateCache(key, data, time = cacheTime5min) {
    let cacheData = this._cache[key];
    if (cacheData) {
      cacheData.overdueTime = getFutureTime(time);
      cacheData.value = data;
    } else {
      cacheData = new CachedContent(data, getFutureTime(time));
    }
    this._cache[key] = cacheData;
  }
  getCache(key, forceRefresh = false) {
    const cacheData = this._cache[key];
    if (!forceRefresh && cacheData?.isValid()) {
      return cacheData.value;
    }
    delete this._cache[key];
    return void 0;
  }
};

// src/modules/launchpadModule.ts
import {
  CachedContent as CachedContent2,
  cacheTime24h as cacheTime24h2,
  extractStructTagFromType as extractStructTagFromType4,
  buildPosition as buildPosition2,
  TransactionUtil,
  CLOCK_ADDRESS as CLOCK_ADDRESS2,
  TickMath,
  d as d4,
  CoinAssist,
  composeType,
  getFutureTime as getFutureTime2,
  cacheTime5min as cacheTime5min2,
  getPackagerConfigs as getPackagerConfigs2,
  getObjectPreviousTransactionDigest,
  getObjectType,
  getObjectOwner,
  getObjectFields as getObjectFields4,
  getMoveObjectType as getMoveObjectType3
} from "@cetusprotocol/cetus-sui-clmm-sdk";
import { TransactionBlock as TransactionBlock2 } from "@mysten/sui.js/transactions";
import { normalizeSuiAddress, normalizeSuiObjectId as normalizeSuiObjectId2 } from "@mysten/sui.js/utils";

// src/utils/launchpad.ts
import BN, { min } from "bn.js";
import { d as d3, extractStructTagFromType as extractStructTagFromType3, getMoveObjectType as getMoveObjectType2, getObjectFields as getObjectFields3, getObjectId as getObjectId2 } from "@cetusprotocol/cetus-sui-clmm-sdk";
import { normalizeSuiObjectId } from "@mysten/sui.js/utils";

// src/types/luanchpa_type.ts
var LaunchpadRouterModule = "router";
var CONST_DENOMINATOR = 1e9;
var LaunchpadPoolActivityState = /* @__PURE__ */ ((LaunchpadPoolActivityState2) => {
  LaunchpadPoolActivityState2["Upcoming"] = "Upcoming";
  LaunchpadPoolActivityState2["Live"] = "Live";
  LaunchpadPoolActivityState2["Settle"] = "Settle";
  LaunchpadPoolActivityState2["Ended"] = "Ended";
  LaunchpadPoolActivityState2["Failed"] = "Failed";
  LaunchpadPoolActivityState2["Canceled"] = "Canceled";
  return LaunchpadPoolActivityState2;
})(LaunchpadPoolActivityState || {});

// src/utils/launchpad.ts
var LauncpadUtil = class {
  static priceRealToFix(price, saleDecimals, raiseDecimals) {
    const subDecimals = d3(saleDecimals - raiseDecimals).toNumber();
    return Number(
      d3(price).div(d3(10).pow(d3(subDecimals))).toString()
    );
  }
  static priceFixToReal(price, saleDecimals, raiseDecimals) {
    const subDecimals = d3(saleDecimals - raiseDecimals).toNumber();
    return Number(
      d3(price).mul(d3(10).pow(d3(subDecimals))).toString()
    );
  }
  static raiseTotalAmount(pool, saleDecimals, raiseDecimals) {
    const subDecimals = d3(saleDecimals - raiseDecimals).abs().toNumber();
    if (saleDecimals > raiseDecimals) {
      return Number(
        d3(pool.total_supply).mul(pool.current_price).div(d3(10).pow(d3(subDecimals))).toString()
      );
    }
    return Number(
      d3(pool.total_supply).mul(pool.current_price).mul(d3(10).pow(d3(subDecimals))).toString()
    );
  }
  static buildLaunchPadPool(objects) {
    const type = getMoveObjectType2(objects);
    const formatType = extractStructTagFromType3(type);
    const fields = getObjectFields3(objects);
    const pool = {
      coin_type_sale: formatType.type_arguments[0],
      coin_type_raise: formatType.type_arguments[1],
      pool_address: normalizeSuiObjectId(getObjectId2(objects)),
      is_settle: fields.is_settle,
      current_price: d3(fields.initialize_price).div(d3(CONST_DENOMINATOR)).toString(),
      price: fields.initialize_price,
      min_price: "0",
      max_price: "0",
      sale_coin_amount: fields.sale_coin,
      raise_coin_amount: fields.raise_coin,
      total_purchase_amount: fields.reality_raise_total,
      total_supply: fields.sale_total,
      min_purchase: fields.min_purchase,
      max_purchase: fields.max_purchase,
      complete_raise_limit: fields.least_raise_amount,
      softcap: fields.softcap,
      max_raise_amount: fields.hardcap,
      sale_decimals: 0,
      raise_decimals: 0,
      liquidity_injection_ratio: Number(fields.liquidity_rate) / 1e4,
      is_cancel: fields.is_cancel,
      duration_summary: {
        start_time: Number(fields.duration_manager.fields.start_time),
        end_time: 0,
        settle_end_time: 0,
        purchase_duration: Number(fields.duration_manager.fields.activity_duration),
        settle_duration: Number(fields.duration_manager.fields.settle_duration),
        lock_up_duration: Number(fields.duration_manager.fields.locked_duration)
      },
      white_summary: {
        white_handle: fields.white_list.fields.users.fields.id.id,
        protection_hard_cap: fields.white_list.fields.hard_cap_total,
        protection_limit_used: fields.white_list.fields.purchase_total,
        size: Number(fields.white_list.fields.users.fields.size)
      },
      unused_sale: fields.unused_sale,
      harvest_raise: fields.harvest_raise,
      tick_spacing: Number(fields.tick_spacing),
      owner: fields.recipient,
      purchase_summary: {
        purchase_handle: fields.purchase_list.fields.id.id,
        size: Number(fields.purchase_list.fields.size)
      },
      pool_status: "Failed" /* Failed */
    };
    LauncpadUtil.updatePoolStatus(pool);
    return pool;
  }
  static updatePoolStatus(pool) {
    const now = Number(d3(Date.now() / 1e3).toFixed(0));
    const { duration_summary } = pool;
    const end_activity_time = duration_summary.start_time + duration_summary.purchase_duration;
    const end_settle_time = end_activity_time + duration_summary.settle_duration;
    duration_summary.end_time = end_activity_time;
    duration_summary.settle_end_time = end_settle_time;
    if (pool.is_settle) {
      pool.pool_status = "Ended" /* Ended */;
    } else if (pool.is_cancel) {
      pool.pool_status = "Canceled" /* Canceled */;
    } else if (now < duration_summary.start_time) {
      pool.pool_status = "Upcoming" /* Upcoming */;
    } else if (now > duration_summary.start_time && now < end_activity_time) {
      pool.pool_status = "Live" /* Live */;
    } else if (now > end_activity_time && now < end_settle_time) {
      const raise_value = pool.raise_coin_amount;
      if (Number(raise_value) < Number(pool.complete_raise_limit)) {
        pool.pool_status = "Failed" /* Failed */;
      } else {
        pool.pool_status = "Settle" /* Settle */;
      }
    }
    return pool;
  }
  /**
   * calculate Pool Price
   * @param sdk
   * @param pool
   */
  static async calculatePoolPrice(sdk, pool) {
    const coinInfos = await sdk.ClmmSDK.CetusConfig.getTokenListByCoinTypes([pool.coin_type_sale, pool.coin_type_raise]);
    const saleDecimals = coinInfos[pool.coin_type_sale].decimals;
    const raiseDecimals = coinInfos[pool.coin_type_raise].decimals;
    pool.min_price = this.priceFixToReal(Number(d3(pool.softcap).div(d3(pool.total_supply))), saleDecimals, raiseDecimals).toString();
    pool.max_price = this.priceFixToReal(Number(d3(pool.max_raise_amount).div(d3(pool.total_supply))), saleDecimals, raiseDecimals).toString();
    pool.current_price = this.priceFixToReal(Number(pool.current_price), saleDecimals, raiseDecimals).toString();
    pool.sale_decimals = saleDecimals;
    pool.raise_decimals = raiseDecimals;
  }
  /**
   * https://git.cplus.link/cetus/cetus-launchpad/-/blob/whitelist/sui/IDO/sources/pool.move#L887
   * withdraw_sale_internal
   * @param pool
   * @returns
   */
  static async getWithdrawRaise(pool) {
    if (pool.pool_status === "Ended" /* Ended */) {
      return pool.harvest_raise;
    }
    return "0";
  }
  /**
   * https://git.cplus.link/cetus/cetus-launchpad/-/blob/whitelist/sui/IDO/sources/pool.move#L906
   * withdraw_raise_internal
   * @param pool
   * @returns
   */
  static async getWithdrawSale(pool) {
    if (pool.pool_status === "Ended" /* Ended */) {
      return pool.unused_sale;
    }
    return pool.sale_coin_amount;
  }
  /**
   * https://m8bj5905cd.larksuite.com/docx/V5AKdlbm3o3muFxh2dwu5C9RsTb
   * $$raiseAmount=min(totalRaised，hardcap)$$
   * @param sdk
   * @param pool
   * @returns
   */
  static async getHistoryWithdrawRaise(sdk, pool) {
    if (pool.pool_status === "Ended" /* Ended */) {
      if (d3(pool.harvest_raise).equals(d3(0))) {
        const settleEvent = await sdk.Launchpad.getSettleEvent(pool.pool_address);
        if (settleEvent) {
          pool.harvest_raise = settleEvent.unused_raise;
        }
      }
      const minAmount = min(new BN(pool.total_purchase_amount), new BN(pool.max_raise_amount));
      return d3(minAmount.toString()).mul(1 - pool.liquidity_injection_ratio).toString();
    }
    return "0";
  }
  static async getHistoryWithdrawSale(sdk, pool) {
    if (pool.pool_status === "Ended" /* Ended */) {
      const settleEvent = await sdk.Launchpad.getSettleEvent(pool.pool_address);
      if (settleEvent) {
        pool.unused_sale = settleEvent.unused_sale;
      }
      return pool.unused_sale;
    }
    return "0";
  }
  /**
   * https://m8bj5905cd.larksuite.com/docx/V5AKdlbm3o3muFxh2dwu5C9RsTb
   * Returning the user's assets in excess
   * @param sdk
   * @param pool
   * @returns
   */
  static async getOverrecruitReverseAmount(sdk, pool) {
    const purchaseMarks = await sdk.Launchpad.getPurchaseMarks(sdk.ClmmSDK.senderAddress, [pool.pool_address], false);
    if (purchaseMarks.length > 0) {
      const userStakeAmount = purchaseMarks[0].purchased_raise_amount;
      const userProtectAmount = (await sdk.Launchpad.getPurchaseAmount(pool.white_summary.white_handle, sdk.ClmmSDK.senderAddress)).safe_purchased_amount;
      const { protection_limit_used: white_purchase_total } = pool.white_summary;
      return d3(userStakeAmount).sub(userProtectAmount).div(d3(pool.total_purchase_amount).sub(white_purchase_total)).mul(d3(pool.total_purchase_amount).sub(pool.max_raise_amount)).toString();
    }
    return "0";
  }
  /**
   * https://m8bj5905cd.larksuite.com/docx/V5AKdlbm3o3muFxh2dwu5C9RsTb
   * @param sdk
   * @param pool
   * @returns
   */
  static async getCanPurchaseAmount(sdk, pool) {
    const overrecruitReverseAmount = await LauncpadUtil.getOverrecruitReverseAmount(sdk, pool);
    const purchaseMarks = await sdk.Launchpad.getPurchaseMarks(sdk.ClmmSDK.senderAddress, [pool.pool_address], false);
    if (purchaseMarks) {
      const userStakeAmount = purchaseMarks[0].purchased_raise_amount;
      d3(userStakeAmount).sub(overrecruitReverseAmount).div(pool.current_price);
    }
    return "0";
  }
};

// src/modules/launchpadModule.ts
var LaunchpadModule = class {
  _sdk;
  _cache = {};
  constructor(sdk) {
    this._sdk = sdk;
  }
  get sdk() {
    return this._sdk;
  }
  /**
   * Gets the immutables for all launchpad pools.
   *
   * @param {string[]} assignPools An array of pool addresses to filter the results by.
   * @param {number} offset The offset to start the results at.
   * @param {number} limit The number of results to return.
   * @param {boolean} forceRefresh Whether to force a refresh of the cache.
   * @returns {Promise<LaunchpadPoolImmutables[]>} A promise that resolves to the immutables for all launchpad pools.
   */
  async getPoolImmutables(assignPools = [], offset = 0, limit = 100, forceRefresh = false) {
    const { package_id } = this._sdk.sdkOptions.launchpad;
    if (package_id === void 0) {
      throw Error("sdk.sdkOptions.launchpad is undefined");
    }
    const cacheKey = `${package_id}_getPoolImmutables`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    const allPools = [];
    const filterPools = [];
    if (cacheData !== void 0) {
      allPools.push(...cacheData);
    }
    if (allPools.length === 0) {
      try {
        const objects = (await this._sdk.ClmmSDK.fullClient.queryEventsByPage({ MoveEventType: `${package_id}::factory::CreatePoolEvent` }))?.data;
        objects.forEach((object) => {
          const fields = object.parsedJson;
          if (fields) {
            allPools.push({
              pool_address: fields.pool_id,
              coin_type_sale: extractStructTagFromType4(fields.sale_coin.name).full_address,
              coin_type_raise: extractStructTagFromType4(fields.raise_coin.name).full_address
            });
          }
        });
        this.updateCache(cacheKey, allPools, cacheTime24h2);
      } catch (error) {
        console.log("getPoolImmutables", error);
      }
    }
    const hasassignPools = assignPools.length > 0;
    for (let index = 0; index < allPools.length; index += 1) {
      const item = allPools[index];
      if (hasassignPools && !assignPools.includes(item.pool_address)) {
        continue;
      }
      if (!hasassignPools) {
        const itemIndex = index;
        if (itemIndex < offset || itemIndex >= offset + limit) {
          continue;
        }
      }
      filterPools.push(item);
    }
    return filterPools;
  }
  /**
   * Gets a list of pool immutables.
   *
   * @param paginationArgs The cursor and limit to start at.
   * @returns array of PoolImmutable objects.
   */
  async getPoolImmutablesWithPage(paginationArgs = "all", forceRefresh = false) {
    const { package_id } = this._sdk.sdkOptions.launchpad;
    const cacheAllKey = `${package_id}_getPoolImmutables`;
    const allPools = [];
    const dataPage = {
      data: [],
      hasNextPage: false
    };
    const queryAll = paginationArgs === "all";
    if (queryAll) {
      const cacheDate = this.getCache(cacheAllKey, forceRefresh);
      if (cacheDate) {
        allPools.push(...cacheDate);
      }
    }
    if (allPools.length === 0) {
      try {
        const objects = await this._sdk.ClmmSDK.fullClient.queryEventsByPage(
          { MoveEventType: `${package_id}::factory::CreatePoolEvent` },
          paginationArgs
        );
        dataPage.hasNextPage = objects.hasNextPage;
        dataPage.nextCursor = objects.nextCursor;
        objects.data.forEach((object) => {
          const fields = object.parsedJson;
          if (fields) {
            allPools.push({
              pool_address: fields.pool_id,
              coin_type_sale: extractStructTagFromType4(fields.sale_coin.name).full_address,
              coin_type_raise: extractStructTagFromType4(fields.raise_coin.name).full_address
            });
          }
        });
      } catch (error) {
        console.log("getPoolImmutables", error);
      }
    }
    dataPage.data = allPools;
    if (queryAll) {
      this.updateCache(cacheAllKey, allPools, cacheTime24h2);
    }
    return dataPage;
  }
  /**
   * Gets all launchpad pools.
   *
   * @param {string[]} assignPools An array of pool addresses to filter the results by.
   * @param {number} offset The offset to start the results at.
   * @param {number} limit The number of results to return.
   * @returns {Promise<LaunchpadPool[]>} A promise that resolves to an array of launchpad pools.
   */
  async getPools(assignPools = [], offset = 0, limit = 100) {
    const allPool = [];
    let poolObjectIds = [];
    if (assignPools.length > 0) {
      poolObjectIds = [...assignPools];
    } else {
      const poolImmutables = await this.getPoolImmutables([], offset, limit, false);
      poolImmutables.forEach((item) => {
        poolObjectIds.push(item.pool_address);
      });
    }
    const objectDataResponses = await this._sdk.ClmmSDK.fullClient.batchGetObjects(poolObjectIds, { showType: true, showContent: true });
    for (const suiObj of objectDataResponses) {
      const pool = LauncpadUtil.buildLaunchPadPool(suiObj);
      await LauncpadUtil.calculatePoolPrice(this._sdk, pool);
      allPool.push(pool);
      const cacheKey = `${pool.pool_address}_getPoolObject`;
      this.updateCache(cacheKey, pool, cacheTime24h2);
    }
    return allPool;
  }
  /**
   * Gets a launchpad pool.
   *
   * @param {string} poolObjectId The ID of the launchpad pool.
   * @param {boolean} forceRefresh Whether to force a refresh of the cache.
   * @returns {Promise<LaunchpadPool>} A promise that resolves to the launchpad pool.
   */
  async getPool(poolObjectId, forceRefresh = true) {
    const cacheKey = `${poolObjectId}_getPoolObject`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData !== void 0) {
      const pool2 = cacheData;
      LauncpadUtil.updatePoolStatus(pool2);
      return pool2;
    }
    const objects = await this._sdk.ClmmSDK.fullClient.getObject({
      id: poolObjectId,
      options: { showContent: true, showType: true }
    });
    const pool = LauncpadUtil.buildLaunchPadPool(objects);
    await LauncpadUtil.calculatePoolPrice(this._sdk, pool);
    this.updateCache(cacheKey, pool);
    return pool;
  }
  /**
   * Gets the init factory event for the launchpad.
   *
   * @param {boolean} forceRefresh Whether to force a refresh of the cache.
   * @returns {Promise<LaunchpadCinfig>} A promise that resolves to the init factory event.
   */
  async getInitConfig(forceRefresh = false) {
    const packageObjectId = this._sdk.sdkOptions.launchpad.package_id;
    const cacheKey = `${packageObjectId}_getInitEvent`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const packageObject = await this._sdk.ClmmSDK.fullClient.getObject({ id: packageObjectId, options: { showPreviousTransaction: true } });
    const previousTx = getObjectPreviousTransactionDigest(packageObject);
    const objects = (await this._sdk.ClmmSDK.fullClient.queryEventsByPage({ Transaction: previousTx }))?.data;
    const initEvent = {
      pools_id: "",
      admin_cap_id: "",
      config_cap_id: ""
    };
    if (objects.length > 0) {
      objects.forEach((item) => {
        const fields = item.parsedJson;
        if (fields) {
          switch (extractStructTagFromType4(item.type).full_address) {
            case `${packageObjectId}::config::InitConfigEvent`:
              initEvent.admin_cap_id = fields.admin_cap_id;
              initEvent.config_cap_id = fields.config_cap_id;
              break;
            case `${packageObjectId}::factory::InitFactoryEvent`:
              initEvent.pools_id = fields.pools_id;
              break;
            default:
              break;
          }
        }
      });
      this.updateCache(cacheKey, initEvent, cacheTime24h2);
    }
    return initEvent;
  }
  /**
   * Gets the lock NFT for the given NFT ID.
   *
   * @param {SuiObjectIdType} nft_id The ID of the NFT.
   * @param {boolean} forceRefresh Whether to force a refresh of the cache.
   * @returns {Promise<Position | undefined>} A promise that resolves to the lock NFT or undefined if the NFT is not found.
   */
  async getLockNFT(nft_id, forceRefresh = false) {
    const cacheKey = `${nft_id}_getLockNFT`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const objects = await this._sdk.ClmmSDK.fullClient.getObject({
      id: nft_id,
      options: {
        showType: true,
        showContent: true,
        showDisplay: true,
        showOwner: true
      }
    });
    if (objects.error) {
      return void 0;
    }
    return buildPosition2(objects);
  }
  /**
   * Gets a list of lock NFTs for the given pool type and recipient.
   *
   * @param {SuiAddressType} poolType The type of the pool.
   * @param {SuiObjectIdType} recipient The recipient of the lock NFTs.
   * @returns {Promise<Position[]>} A promise that resolves to an array of lock NFTs.
   */
  async getLockNFTList(recipient) {
    const { package_id } = this._sdk.ClmmSDK.sdkOptions.clmm_pool;
    const { sdkOptions } = this._sdk;
    const result = [];
    try {
      const objects = (await this._sdk.ClmmSDK.fullClient.queryEventsByPage({
        MoveEventType: `${sdkOptions.launchpad.package_id}::lock::LockNFTEvent`
      }))?.data;
      for (const object of objects) {
        const fields = object.parsedJson;
        if (fields) {
          const lockNFTEvent = {
            locked_time: Number(fields.locked_time),
            end_lock_time: Number(fields.end_lock_time),
            nft_type: fields.nft_type_name.name,
            lock_nft_id: fields.lock_nft_id,
            recipient: fields.recipient
          };
          if (recipient === lockNFTEvent.recipient && `${package_id}` === extractStructTagFromType4(lockNFTEvent.nft_type).address) {
            const lockNftInfo = await this.getLockNFT(lockNFTEvent.lock_nft_id);
            if (lockNftInfo) {
              const nftTypeA = extractStructTagFromType4(lockNftInfo.coin_type_a).full_address;
              const nftTypeB = extractStructTagFromType4(lockNftInfo.coin_type_b).full_address;
              result.push({
                ...lockNftInfo,
                coin_type_a: nftTypeA,
                coin_type_b: nftTypeB,
                ...lockNFTEvent
              });
            }
          }
        }
      }
      return result;
    } catch (error) {
      console.log("getLockNFTList:", error);
      return [];
    }
  }
  /**
   * Creates a transaction payload for creating a launchpad pool.
   *
   * @param {CreateLaunchpadPoolParams} params The parameters for creating the pool.
   * @returns {Promise<TransactionBlock>} A promise that resolves to the transaction payload.
   */
  async creatPoolTransactionPayload(params) {
    const { launchpad } = this.sdk.sdkOptions;
    const launchpadEvent = getPackagerConfigs2(launchpad);
    this.assertLuanchpadConfig();
    if (this._sdk.ClmmSDK.senderAddress.length === 0) {
      throw Error("this config sdk senderAddress is empty");
    }
    const fixPrice = LauncpadUtil.priceRealToFix(Number(params.initialize_price), params.sale_decimals, params.raise_decimals);
    const tx = new TransactionBlock2();
    const min_sale_amount = d4(params.total_supply).add(d4(params.total_supply).mul(d4(params.liquidity_rate))).toNumber();
    const primaryCoinInputs = await TransactionUtil.syncBuildCoinInputForAmount(
      this._sdk.ClmmSDK,
      tx,
      BigInt(min_sale_amount),
      params.coin_type_sale
    );
    const args = [
      tx.pure(launchpadEvent.admin_cap_id),
      tx.pure(launchpadEvent.config_cap_id),
      tx.pure(launchpadEvent.pools_id),
      tx.pure(normalizeSuiAddress(params.recipient)),
      tx.pure((fixPrice * CONST_DENOMINATOR).toString()),
      primaryCoinInputs,
      tx.pure(params.total_supply.toString()),
      tx.pure(params.min_purchase.toString()),
      tx.pure(params.max_purchase.toString()),
      tx.pure(params.least_raise_amount.toString()),
      tx.pure(params.hardcap.toString()),
      tx.pure((params.liquidity_rate * 1e3).toString()),
      tx.pure(params.start_time.toString()),
      tx.pure(params.activity_duration.toString()),
      tx.pure(params.settle_duration.toString()),
      tx.pure(params.locked_duration.toString()),
      tx.pure(params.tick_spacing.toString()),
      tx.pure(CLOCK_ADDRESS2)
    ];
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    tx.moveCall({
      target: `${launchpad.published_at}::${LaunchpadRouterModule}::create_launch_pool`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  /**
   * Creates a transaction payload for purchasing tokens from a launchpad pool.
   *
   * @param {PurchaseParams} params The parameters for the purchase.
   * @returns {Promise<TransactionBlock>} A promise that resolves to the transaction payload.
   */
  async creatPurchasePayload(params) {
    const { launchpad } = this.sdk.sdkOptions;
    if (this._sdk.ClmmSDK.senderAddress.length === 0) {
      throw Error("this config sdk senderAddress is empty");
    }
    const tx = new TransactionBlock2();
    const primaryCoinInputs = await TransactionUtil.syncBuildCoinInputForAmount(
      this._sdk.ClmmSDK,
      tx,
      BigInt(params.purchase_amount),
      params.coin_type_raise
    );
    const purchaseMark = (await this.getPurchaseMarks(this._sdk.ClmmSDK.senderAddress, [params.pool_address], false))[0];
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    let args;
    if (purchaseMark) {
      args = [
        tx.pure(params.pool_address),
        tx.pure(getPackagerConfigs2(launchpad).config_cap_id),
        tx.pure(purchaseMark.id),
        primaryCoinInputs,
        tx.pure(params.purchase_amount.toString()),
        tx.pure(CLOCK_ADDRESS2)
      ];
    } else {
      args = [
        tx.pure(params.pool_address),
        tx.pure(getPackagerConfigs2(launchpad).config_cap_id),
        primaryCoinInputs,
        tx.pure(params.purchase_amount.toString()),
        tx.pure(CLOCK_ADDRESS2)
      ];
    }
    tx.moveCall({
      target: `${launchpad.published_at}::${LaunchpadRouterModule}::${purchaseMark === void 0 ? "create_and_purchase" : "purchase"}`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  /**
   * Creates a transaction payload for claiming tokens from a launchpad pool.
   *
   * @param {ClaimParams} params The parameters for the claim.
   * @returns {Promise<TransactionBlock>} A promise that resolves to the transaction payload.
   */
  async creatClaimPayload(params) {
    const { launchpad } = this.sdk.sdkOptions;
    if (this._sdk.ClmmSDK.senderAddress.length === 0) {
      throw Error("this config sdk senderAddress is empty");
    }
    const purchaseMark = (await this.getPurchaseMarks(this._sdk.ClmmSDK.senderAddress, [params.pool_address], false))[0];
    const tx = new TransactionBlock2();
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    const args = [
      tx.pure(params.pool_address),
      tx.pure(getPackagerConfigs2(launchpad).config_cap_id),
      tx.pure(purchaseMark?.id),
      tx.pure(CLOCK_ADDRESS2)
    ];
    tx.moveCall({
      target: `${launchpad.published_at}::${LaunchpadRouterModule}::claim`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  /**
   * Creates a transaction payload for settling tokens from a launchpad pool.
   *
   * @param {SettleParams} params The parameters for the settlement.
   * @returns {Promise<TransactionBlock>} A promise that resolves to the transaction payload.
   */
  async creatSettlePayload(params) {
    const { launchpad } = this.sdk.sdkOptions;
    const { clmm_pool } = this.sdk.ClmmSDK.sdkOptions;
    if (this._sdk.ClmmSDK.senderAddress.length === 0) {
      throw Error("this config sdk senderAddress is empty");
    }
    this.assertLuanchpadConfig();
    const clmmEvent = getPackagerConfigs2(clmm_pool);
    const { clmm_args } = params;
    const tx = new TransactionBlock2();
    tx.setSender(this.sdk.ClmmSDK.senderAddress);
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    if (clmm_args) {
      const initialize_sqrt_price = clmm_args.opposite ? TickMath.priceToSqrtPriceX64(
        d4(1).div(clmm_args.current_price),
        // 0.01
        clmm_args.raise_decimals,
        clmm_args.sale_decimals
      ).toString() : TickMath.priceToSqrtPriceX64(
        d4(clmm_args.current_price),
        // 0.01
        clmm_args.sale_decimals,
        clmm_args.raise_decimals
      ).toString();
      const a2b = BigInt(initialize_sqrt_price) < BigInt(clmm_args.clmm_sqrt_price);
      const needCoinType = clmm_args.opposite ? a2b ? params.coin_type_raise : params.coin_type_sale : a2b ? params.coin_type_sale : params.coin_type_raise;
      const coinAssets = await this._sdk.ClmmSDK.getOwnerCoinAssets(this._sdk.ClmmSDK.senderAddress, needCoinType);
      let needAmount = CoinAssist.calculateTotalBalance(coinAssets);
      if (CoinAssist.isSuiCoin(needCoinType)) {
        needAmount -= BigInt(2e8);
      }
      const primaryCoinInputsR = TransactionUtil.buildCoinForAmount(tx, coinAssets, needAmount, needCoinType);
      const primaryCoinInputs = primaryCoinInputsR.targetCoin;
      const funName = clmm_args.opposite ? a2b ? "settle_with_reverse_clmm_only_with_a" : "settle_with_reverse_clmm_only_with_b" : a2b ? "settle_only_with_a" : "settle_only_with_b";
      const args = [
        tx.pure(params.pool_address),
        tx.pure(getPackagerConfigs2(launchpad).config_cap_id),
        tx.pure(clmm_args.clmm_pool_address),
        tx.pure(clmmEvent.global_config_id),
        tx.pure(initialize_sqrt_price),
        primaryCoinInputs,
        tx.pure(CLOCK_ADDRESS2)
      ];
      tx.moveCall({
        target: `${launchpad.published_at}::${LaunchpadRouterModule}::${funName}`,
        typeArguments,
        arguments: args
      });
    } else {
      tx.moveCall({
        target: `${launchpad.published_at}::${LaunchpadRouterModule}::settle`,
        typeArguments,
        arguments: [tx.pure(params.pool_address), tx.pure(getPackagerConfigs2(launchpad).config_cap_id), tx.pure(CLOCK_ADDRESS2)]
      });
    }
    return tx;
  }
  /**
   * Creates a transaction payload for withdrawing tokens from a launchpad pool.
   *
   * @param {WithdrawParams} params The parameters for the withdrawal.
   * @returns {TransactionBlock} The transaction payload.
   */
  creatWithdrawPayload(params) {
    const { launchpad } = this.sdk.sdkOptions;
    const tx = new TransactionBlock2();
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    const args = [tx.object(params.pool_address), tx.object(getPackagerConfigs2(launchpad).config_cap_id), tx.object(CLOCK_ADDRESS2)];
    if (params.sale_amount > BigInt(0)) {
      tx.moveCall({
        target: `${launchpad.published_at}::${LaunchpadRouterModule}::withdraw_sale`,
        typeArguments,
        arguments: args
      });
    }
    if (params.raise_amount > BigInt(0)) {
      tx.moveCall({
        target: `${launchpad.published_at}::${LaunchpadRouterModule}::withdraw_raise`,
        typeArguments,
        arguments: args
      });
    }
    return tx;
  }
  /**
   * Creates a transaction payload for adding a user to the whitelist.
   *
   * @param {AddUserToWhitelistParams} params The parameters for the addition.
   * @returns {TransactionBlock} The transaction payload.
   */
  addUserToWhitelisPayload(params) {
    const { launchpad } = this.sdk.sdkOptions;
    this.assertLuanchpadConfig();
    const tx = new TransactionBlock2();
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    params.user_addrs.forEach((user_addr) => {
      const args = [
        tx.object(getPackagerConfigs2(launchpad).admin_cap_id),
        tx.object(getPackagerConfigs2(launchpad).config_cap_id),
        tx.object(params.pool_address),
        tx.object(user_addr),
        tx.pure(params.safe_limit_amount),
        tx.object(CLOCK_ADDRESS2)
      ];
      tx.moveCall({
        target: `${launchpad.published_at}::${LaunchpadRouterModule}::add_user_to_whitelist`,
        typeArguments,
        arguments: args
      });
    });
    return tx;
  }
  /**
   * Creates a transaction payload for updating the whitelist cap.
   *
   * @param {UpdateWhitelistCapParams} params The parameters for the update.
   * @returns {TransactionBlock} The transaction payload.
   */
  updateWhitelistCapPayload(params) {
    const { launchpad } = this.sdk.sdkOptions;
    this.assertLuanchpadConfig();
    const tx = new TransactionBlock2();
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    if (params.safe_limit_amount > 0) {
      tx.moveCall({
        target: `${launchpad.published_at}::${LaunchpadRouterModule}::update_whitelist_member_safe_limit_amount`,
        typeArguments,
        arguments: [
          tx.object(getPackagerConfigs2(launchpad).admin_cap_id),
          tx.object(getPackagerConfigs2(launchpad).config_cap_id),
          tx.object(params.pool_address),
          tx.pure(params.white_list_member),
          tx.pure(params.safe_limit_amount),
          tx.object(CLOCK_ADDRESS2)
        ]
      });
    }
    if (params.hard_cap_total > 0) {
      tx.moveCall({
        target: `${launchpad.published_at}::${LaunchpadRouterModule}::update_whitelist_hard_cap_total`,
        typeArguments,
        arguments: [
          tx.object(getPackagerConfigs2(launchpad).admin_cap_id),
          tx.object(getPackagerConfigs2(launchpad).config_cap_id),
          tx.object(params.pool_address),
          tx.pure(params.hard_cap_total),
          tx.object(CLOCK_ADDRESS2)
        ]
      });
    }
    return tx;
  }
  /**
   * Creates a transaction payload for removing a user from the whitelist.
   *
   * @param {RemoveWhitelistParams} params The parameters for the removal.
   * @returns {TransactionBlock} The transaction payload.
   */
  creatRemoveWhitelistPayload(params) {
    const { launchpad } = this.sdk.sdkOptions;
    this.assertLuanchpadConfig();
    const tx = new TransactionBlock2();
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    params.user_addrs.forEach((user_addr) => {
      const args = [
        tx.object(getPackagerConfigs2(launchpad).admin_cap_id),
        tx.object(getPackagerConfigs2(launchpad).config_cap_id),
        tx.object(params.pool_address),
        tx.object(user_addr),
        tx.object(CLOCK_ADDRESS2)
      ];
      tx.moveCall({
        target: `${launchpad.published_at}::${LaunchpadRouterModule}::remove_user_from_whitelist`,
        typeArguments,
        arguments: args
      });
    });
    return tx;
  }
  /**
   * Creates a transaction payload for canceling a pool.
   *
   * @param {CancelLaunchPadParams} params The parameters for the cancellation.
   * @returns {TransactionBlock} The transaction payload.
   */
  creatCancelPoolPayload(params) {
    const { launchpad } = this.sdk.sdkOptions;
    this.assertLuanchpadConfig();
    const tx = new TransactionBlock2();
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    const args = [
      tx.pure(getPackagerConfigs2(launchpad).admin_cap_id),
      tx.pure(getPackagerConfigs2(launchpad).config_cap_id),
      tx.pure(params.pool_address),
      tx.pure(CLOCK_ADDRESS2)
    ];
    tx.moveCall({
      target: `${launchpad.published_at}::${LaunchpadRouterModule}::cancel`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  /**
   * Creates a transaction payload for updating the recipient address.
   *
   * @param {UpdateRecipientParams} params The parameters for the update.
   * @returns {TransactionBlock} The transaction payload.
   */
  updateRecipientPayload(params) {
    const { launchpad } = this.sdk.sdkOptions;
    this.assertLuanchpadConfig();
    const tx = new TransactionBlock2();
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    const args = [
      tx.pure(getPackagerConfigs2(launchpad).admin_cap_id),
      tx.pure(getPackagerConfigs2(launchpad).config_cap_id),
      tx.pure(params.pool_address),
      tx.pure(params.new_recipient),
      tx.pure(CLOCK_ADDRESS2)
    ];
    tx.moveCall({
      target: `${launchpad.published_at}::${LaunchpadRouterModule}::update_recipient_address`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  /**
   * Creates a transaction payload for updating the pool duration.
   *
   * @param {UpdatePoolDurationParams} params The parameters for the update.
   * @returns {TransactionBlock} The transaction payload.
   */
  updatePoolDuractionPayload(params) {
    const { launchpad } = this.sdk.sdkOptions;
    this.assertLuanchpadConfig();
    const tx = new TransactionBlock2();
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    const args = [
      tx.pure(getPackagerConfigs2(launchpad).admin_cap_id),
      tx.pure(getPackagerConfigs2(launchpad).config_cap_id),
      tx.pure(params.pool_address),
      tx.pure(params.activity_duration),
      tx.pure(params.settle_duration),
      tx.pure(params.lock_duration),
      tx.pure(CLOCK_ADDRESS2)
    ];
    tx.moveCall({
      target: `${launchpad.published_at}::${LaunchpadRouterModule}::update_pool_duration`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  /**
   * Creates a transaction payload for unlocking an NFT.
   *
   * @param {UnlockNftParams} params The parameters for the unlock.
   * @returns {TransactionBlock} The transaction payload.
   */
  creatUnlockNftPayload(params) {
    const { launchpad } = this.sdk.sdkOptions;
    if (getPackagerConfigs2(launchpad) === void 0) {
      throw Error("getPackagerConfigs(launchpad)  is null");
    }
    const tx = new TransactionBlock2();
    const typeArguments = [params.nft_type];
    const args = [tx.pure(params.lock_nft), tx.pure(CLOCK_ADDRESS2)];
    tx.moveCall({
      target: `${launchpad.published_at}::lock::unlock_nft`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  /**
   * Determines if the given wallet address is the admin cap for the launchpad.
   *
   * @param {SuiObjectIdType} walletAddress The wallet address to check.
   * @returns {Promise<boolean>} A promise that resolves to true if the wallet address is the admin cap, or false otherwise.
   */
  async isAdminCap(walletAddress) {
    const { launchpad } = this._sdk.sdkOptions;
    if (getPackagerConfigs2(launchpad) === void 0) {
      throw Error("launchpad config is empty");
    }
    const cacheKey = `${walletAddress}_isAdminCap`;
    const cacheData = this.getCache(cacheKey);
    if (cacheData) {
      return cacheData;
    }
    const object = await this._sdk.ClmmSDK.fullClient.getObject({
      id: getPackagerConfigs2(launchpad).admin_cap_id,
      options: { showType: true, showOwner: true }
    });
    console.log(object);
    const type = getObjectType(object);
    const owner = getObjectOwner(object);
    let isAdminCap = false;
    if (owner && type && extractStructTagFromType4(type).source_address === `${launchpad.package_id}::config::AdminCap`) {
      const addressOwner = owner;
      isAdminCap = normalizeSuiAddress(addressOwner.AddressOwner) === normalizeSuiAddress(walletAddress);
    }
    this.updateCache(cacheKey, isAdminCap, cacheTime24h2);
    return isAdminCap;
  }
  /**
   * Determines if the given wallet address is whitelisted.
   *
   * @param {SuiObjectIdType} whitetHandle The handle of the whitelist object.
   * @param {SuiObjectIdType} walletAddress The wallet address to check.
   * @returns {Promise<boolean>} A promise that resolves to true if the wallet address is whitelisted, or false otherwise.
   */
  async isWhiteListUser(whitetHandle, walletAddress) {
    const name = {
      type: "address",
      value: walletAddress
    };
    try {
      const res = await this._sdk.ClmmSDK.fullClient.getDynamicFieldObject({ parentId: whitetHandle, name });
      if (res && res.data) {
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }
  /**
   * Gets the purchase amount for the given wallet address.
   *
   * @param {SuiObjectIdType} purchaseHandle The handle of the purchase object.
   * @param {SuiObjectIdType} walletAddress The wallet address to get the purchase amount for.
   * @returns {Promise<{ safe_limit_amount: string; safe_purchased_amount: string }>} A promise that resolves to an object with the purchase amount fields.
   */
  async getPurchaseAmount(purchaseHandle, walletAddress) {
    const name = {
      type: "address",
      value: walletAddress
    };
    try {
      const result = await this._sdk.ClmmSDK.fullClient.getDynamicFieldObject({ parentId: purchaseHandle, name });
      const fields = getObjectFields4(result);
      console.log(fields);
      if (fields) {
        return fields?.value?.fields;
      }
    } catch (error) {
    }
    return { safe_limit_amount: "0", safe_purchased_amount: "0" };
  }
  /**
   * Gets the purchase marks for the given account address.
   *
   * @param {string} accountAddress The wallet address to get the purchase marks for.
   * @param {SuiObjectIdType[]} poolAddressArray An array of pool addresses to filter the purchase marks by.
   * @param {boolean} forceRefresh Whether to force a refresh of the cache.
   * @returns {Promise<PurchaseMark[]>} A promise that resolves to an array of purchase marks.
   */
  async getPurchaseMarks(accountAddress, poolAddressArray = [], forceRefresh = true) {
    const { launchpad } = this._sdk.sdkOptions;
    const cacheKey = `${poolAddressArray}_getPurchaseMark`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (!forceRefresh && cacheData !== void 0) {
      return cacheData;
    }
    let cursor = null;
    const purchaseMarks = [];
    while (true) {
      const ownerRes = await this._sdk.ClmmSDK.fullClient.getOwnedObjects({
        owner: accountAddress,
        options: { showType: true, showContent: true, showDisplay: true },
        cursor
        // filter: { StructType: `${launchpad.package_id}::pool::PurchaseMark` },
      });
      for (const item of ownerRes.data) {
        const { fields } = item.data.content;
        const type = extractStructTagFromType4(getMoveObjectType3(item)).source_address;
        if (type === `${launchpad.package_id}::pool::PurchaseMark`) {
          console.log("fields: ", fields);
          const purchaseMark = {
            id: fields.id.id,
            pool_id: extractStructTagFromType4(fields.pool_id).address,
            purchased_raise_amount: fields.purchase_total,
            obtain_sale_amount: fields.obtain_sale_amount,
            used_raise_amount: fields.used_raise_amount
          };
          if (poolAddressArray.length > 0) {
            if (poolAddressArray.includes(purchaseMark.pool_id)) {
              purchaseMarks.push(purchaseMark);
            }
          } else {
            purchaseMarks.push(purchaseMark);
          }
        }
      }
      if (ownerRes.hasNextPage) {
        cursor = ownerRes.nextCursor;
      } else {
        break;
      }
    }
    this.updateCache(cacheKey, purchaseMarks, cacheTime24h2);
    return purchaseMarks;
  }
  /**
   * Gets the settle event for the given pool address.
   *
   * @param {SuiObjectIdType} poolAddress The pool address to get the settle event for.
   * @returns {Promise<SettleEvent | undefined>} A promise that resolves to the settle event or undefined if the event does not exist.
   */
  async getSettleEvent(poolAddress) {
    const { launchpad } = this._sdk.sdkOptions;
    const cacheKey = `${poolAddress}_getPurchaseMark`;
    const cacheData = this.getCache(cacheKey);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const ownerRes = await this._sdk.ClmmSDK.fullClient.queryEventsByPage({
      MoveEventType: `${launchpad.package_id}::pool::SettleEvent`
    });
    for (const item of ownerRes.data) {
      const parsedJson = item.parsedJson;
      if (poolAddress === normalizeSuiObjectId2(parsedJson.pool_id)) {
        const settleEvent = {
          pool_id: parsedJson.pool_id,
          settle_price: parsedJson.settle_price,
          unused_sale: parsedJson.unused_sale,
          unused_raise: parsedJson.unused_raise,
          white_purchase_total: parsedJson.white_purchase_total
        };
        this.updateCache(cacheKey, settleEvent, cacheTime24h2);
        return settleEvent;
      }
    }
    return void 0;
  }
  /**
   * Builds a launchpad coin type from the given sale coin type and raise coin type.
   *
   * @param {SuiAddressType} coinTypeSale The sale coin type.
   * @param {SuiAddressType} coinTypeRaise The raise coin type.
   * @returns {string} The launchpad coin type.
   */
  buildLaunchpadCoinType(coin_type_sale, coin_type_raise) {
    return composeType(this._sdk.sdkOptions.launchpad.package_id, "pool", "PoolLiquidityCoin", [coin_type_sale, coin_type_raise]);
  }
  assertLuanchpadConfig() {
    const { launchpad } = this.sdk.sdkOptions;
    if (getPackagerConfigs2(launchpad) === void 0) {
      throw Error("sdk getPackagerConfigs(launchpad) is null");
    }
  }
  updateCache(key, data, time = cacheTime5min2) {
    let cacheData = this._cache[key];
    if (cacheData) {
      cacheData.overdueTime = getFutureTime2(time);
      cacheData.value = data;
    } else {
      cacheData = new CachedContent2(data, getFutureTime2(time));
    }
    this._cache[key] = cacheData;
  }
  getCache(key, forceRefresh = false) {
    const cacheData = this._cache[key];
    if (!forceRefresh && cacheData?.isValid()) {
      return cacheData.value;
    }
    delete this._cache[key];
    return void 0;
  }
};

// src/modules/makerModule.ts
import Decimal from "decimal.js";
import {
  CachedContent as CachedContent3,
  cacheTime24h as cacheTime24h3,
  cacheTime5min as cacheTime5min3,
  ClmmPositionStatus,
  d as d6,
  getFutureTime as getFutureTime3,
  getPackagerConfigs as getPackagerConfigs3,
  getObjectFields as getObjectFields6
} from "@cetusprotocol/cetus-sui-clmm-sdk";
import { TransactionBlock as TransactionBlock3 } from "@mysten/sui.js/transactions";

// src/utils/maker.ts
import { d as d5, extractStructTagFromType as extractStructTagFromType5, getObjectFields as getObjectFields5 } from "@cetusprotocol/cetus-sui-clmm-sdk";
var MakerUtil = class {
  static buildPoolImmutables(data) {
    const { fields } = data.value.fields.value;
    const pool = {
      clmm_pool_id: extractStructTagFromType5(fields.clmm_pool_id).address,
      bonus_type: extractStructTagFromType5(fields.bonus_type.fields.name).source_address,
      pool_id: extractStructTagFromType5(fields.pool_id).address,
      coinTypeA: extractStructTagFromType5(fields.coin_type_a.fields.name).source_address,
      coinTypeB: extractStructTagFromType5(fields.coin_type_b.fields.name).source_address
    };
    return pool;
  }
  static buildPoolState(data) {
    const fields = getObjectFields5(data);
    const rewarderMultipliers = [];
    fields.config.fields.contents.forEach((item) => {
      rewarderMultipliers.push({
        rate: Number(d5(item.fields.key).div(CONFIG_PERCENT_MULTIPER)),
        multiplier: Number(d5(item.fields.value).div(CONFIG_PERCENT_MULTIPER))
      });
    });
    const pool = {
      balance: fields.balance,
      config: rewarderMultipliers,
      is_open: fields.is_open,
      index: Number(fields.index),
      start_time: Number(fields.start_time),
      interval_day: Number(fields.interval_day),
      minimum_percent_to_reward: Number(d5(fields.minimum_percent_to_reward).div(CONFIG_PERCENT_MULTIPER)),
      rewarders: {
        rewarder_handle: fields.rewarders.fields.id.id,
        size: Number(fields.rewarders.fields.size)
      },
      whale_nfts: {
        whale_nfts_handle: fields.whale_nfts.fields.id.id,
        size: Number(fields.whale_nfts.fields.size)
      },
      points: {
        point_handle: fields.points.fields.id.id,
        size: Number(fields.points.fields.size)
      }
    };
    return pool;
  }
  static buildMarkerPositions(data) {
    const { contents } = data.value.fields.value.fields;
    const mList = [];
    const period_id = data.id.id;
    contents.forEach((item) => {
      const { key, value } = item.fields;
      const info = {
        id: key,
        period_id,
        bonus_num: value.fields.bonus_num,
        point: value.fields.point,
        is_burn: value.fields.is_burn,
        point_after_multiplier: value.fields.point_after_multiplier,
        percent: Number(d5(value.fields.percent).div(CONFIG_PERCENT_MULTIPER)),
        fee_share_rate: 0,
        is_redeemed: value.fields.is_redeemed
      };
      mList.push(info);
    });
    return mList;
  }
  static buildPoolBonusInfo(data) {
    const { fields, type } = data.value.fields.value;
    const bonusInfo = {
      type,
      time: Number(fields.time),
      settle_time: Number(fields.settle_time),
      settled_num: fields.settled_num,
      is_settled: fields.is_settled,
      basis_bonus: fields.basis_bonus,
      total_bonus: fields.total_bonus,
      is_vacant: fields.is_vacant,
      redeemed_num: fields.redeemed_num
    };
    return bonusInfo;
  }
  static getBonusPercent(configs, percent) {
    let level_now = 0;
    for (const config of configs) {
      if (percent >= config.rate && config.rate > level_now) {
        level_now = config.multiplier;
      }
    }
    return level_now;
  }
};

// src/types/maker_type.ts
var MakerRouterModule = "router";

// src/modules/makerModule.ts
var MakerModule = class {
  _sdk;
  _cache = {};
  constructor(sdk) {
    this._sdk = sdk;
  }
  get sdk() {
    return this._sdk;
  }
  /**
   * Gets the pool immutables
   *
   * @param {boolean} forceRefresh Whether to force a refresh of the cache.
   * @returns {Promise<MakerPoolImmutables[]>} A promise that resolves to an array of maker pool immutables.
   */
  async getPoolImmutables(forceRefresh = false) {
    const { maker_bonus } = this._sdk.sdkOptions;
    const cacheKey = `${maker_bonus.package_id}_getPoolImmutables`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    const allPool = [];
    if (cacheData !== void 0) {
      allPool.push(...cacheData);
    } else {
      const simplePoolIds = [];
      const result = await this._sdk.ClmmSDK.fullClient.getDynamicFieldsByPage(getPackagerConfigs3(maker_bonus).maker_pool_handle);
      result.data?.forEach((item) => {
        simplePoolIds.push(item.objectId);
      });
      const simpleDatas = await this._sdk.ClmmSDK.fullClient.batchGetObjects(simplePoolIds, { showContent: true });
      for (const item of simpleDatas) {
        const fields = getObjectFields6(item);
        if (fields) {
          allPool.push(MakerUtil.buildPoolImmutables(fields));
        }
      }
    }
    return allPool;
  }
  /**
   * Gets the pool immutable for the given pool object id.
   *
   * @param {SuiObjectIdType} poolObjectId The pool object id.
   * @returns {Promise<MakerPoolImmutables>} A promise that resolves to the pool immutable.
   */
  async getPoolImmutable(poolObjectId) {
    const { maker_bonus } = this._sdk.sdkOptions;
    const cacheKey = `${maker_bonus.package_id}_getPoolImmutables`;
    const cacheData = this.getCache(cacheKey);
    if (cacheData !== void 0) {
      const poolImmutableool = cacheData.filter((item) => {
        return poolObjectId === item.pool_id;
      });
      if (poolImmutableool.length > 0) {
        return poolImmutableool[0];
      }
    }
    const result = await this._sdk.ClmmSDK.fullClient.getDynamicFieldObject({
      parentId: getPackagerConfigs3(maker_bonus).maker_pool_handle,
      name: {
        type: "0x2::object::ID",
        value: poolObjectId
      }
    });
    const fields = getObjectFields6(result);
    return MakerUtil.buildPoolImmutables(fields);
  }
  /**
   * Gets all pools.
   *
   * @returns {Promise<MakerPool[]>} A promise that resolves to an array of MakerPool objects.
   */
  async getPools() {
    const allPool = [];
    const poolImmutables = await this.getPoolImmutables();
    const poolObjectIds = poolImmutables.map((item) => {
      return item.pool_id;
    });
    const objectDataResponses = await this._sdk.ClmmSDK.fullClient.batchGetObjects(poolObjectIds, { showType: true, showContent: true });
    let index = 0;
    for (const suiObj of objectDataResponses) {
      const poolState = MakerUtil.buildPoolState(suiObj);
      if (poolState) {
        const pool = {
          ...poolImmutables[index],
          ...poolState
        };
        allPool.push(pool);
        const cacheKey = `${pool.pool_id}_getPoolObject`;
        this.updateCache(cacheKey, pool, cacheTime24h3);
      }
      index += 1;
    }
    return allPool;
  }
  /**
   * Gets a pool by its object id.
   *
   * @param {string} poolObjectId The object id of the pool.
   * @param {boolean} forceRefresh Whether to force a refresh of the cache.
   * @returns {Promise<MakerPool>} A promise that resolves to the pool.
   */
  async getPool(poolObjectId, forceRefresh = true) {
    const cacheKey = `${poolObjectId}_getPoolObject`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    const poolImmutables = await this.getPoolImmutable(poolObjectId);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const objects = await this._sdk.ClmmSDK.fullClient.getObject({
      id: poolObjectId,
      options: { showContent: true, showType: true }
    });
    const poolState = MakerUtil.buildPoolState(objects);
    const pool = {
      ...poolImmutables,
      ...poolState
    };
    this.updateCache(cacheKey, pool, cacheTime24h3);
    return pool;
  }
  async getPoolHandleId(booster_config_id) {
    const reault = await this._sdk.ClmmSDK.fullClient.getObject({ id: booster_config_id, options: { showContent: true } });
    const fields = getObjectFields6(reault);
    if (fields) {
      return fields.list.fields.id.id;
    }
    return "";
  }
  /**
   * Gets the periods for a given MakerPool.
   *
   * @param {MakerPool} pool The MakerPool to get periods for.
   * @param {boolean} forceRefresh Whether to force a refresh of the cache.
   * @returns {Promise<MakerPoolPeriod[]>} A promise that resolves to an array of MakerPoolPeriod objects.
   */
  async getMakerPoolPeriods(pool, forceRefresh = false) {
    const periods = [];
    const cacheKey = `${pool.pool_id}_getMakerPoolPeriods`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const results = await this._sdk.ClmmSDK.fullClient.getDynamicFieldsByPage(pool.whale_nfts.whale_nfts_handle);
    results.data.forEach((item) => {
      const info = {
        id: item.objectId,
        start_time: 0,
        end_time: 0,
        period: Number(item.name.value)
      };
      info.start_time = Number(
        d6(pool.start_time).add(
          d6(pool.interval_day).mul(24 * 3600).mul(info.period)
        ).toFixed(0, Decimal.ROUND_DOWN)
      );
      info.end_time = Number(
        d6(info.start_time).add(d6(pool.interval_day).mul(24 * 3600)).toFixed(0, Decimal.ROUND_DOWN)
      );
      periods.push(info);
    });
    this.updateCache(cacheKey, periods, cacheTime24h3);
    return periods;
  }
  /**
   * Gets the init factory event.
   *
   * @returns {Promise<MakerConfig>} A promise that resolves to a MakerInitEvent object.
   */
  async getMakerBonusInitConfigs() {
    const { package_id } = this.sdk.sdkOptions.maker_bonus;
    const initEventObjects = (await this._sdk.ClmmSDK.fullClient.queryEventsByPage({ MoveEventType: `${package_id}::config::InitEvent` }))?.data;
    const initEvent = {
      maker_config_id: "",
      maker_pool_handle: ""
    };
    if (initEventObjects.length > 0) {
      initEventObjects.forEach((item) => {
        const fields = item.parsedJson;
        if (fields) {
          initEvent.maker_config_id = fields.config_id;
        }
      });
    }
    initEvent.maker_pool_handle = await this.getPoolHandleId(initEvent.maker_config_id);
    return initEvent;
  }
  /**
   * Gets the list of marker positions for a given whale NFTs handle and maker pool periods.
   *
   * @param {SuiObjectIdType} whale_nfts_handle The whale NFTs handle.
   * @param {MakerPoolPeriod[]} makerPoolPeriods The maker pool periods.
   * @param {boolean} forceRefresh Whether to force a refresh of the cache.
   * @returns {Promise<Record<number, MarkerPosition[]>>} A promise that resolves to a record of marker positions keyed by period.
   */
  async getPoolMarkerPositionList(whale_nfts_handle, makerPoolPeriods, forceRefresh = false) {
    const recordMarkerPosition = {};
    const notFindMakerPoolPeriods = [];
    if (!forceRefresh) {
      makerPoolPeriods.forEach((item) => {
        const cacheKey = `${whale_nfts_handle}_${item.period}_getPoolMarkerPositionList`;
        const cacheData = this.getCache(cacheKey);
        if (cacheData !== void 0) {
          recordMarkerPosition[item.period] = cacheData;
        } else {
          recordMarkerPosition[item.period] = [];
          notFindMakerPoolPeriods.push(item);
        }
      });
    }
    try {
      if (notFindMakerPoolPeriods.length > 0) {
        const results = await this._sdk.ClmmSDK.fullClient.batchGetObjects(
          notFindMakerPoolPeriods.map((item) => item.id),
          { showContent: true }
        );
        results.forEach((item) => {
          const fields = getObjectFields6(item);
          const bonusInfoList = MakerUtil.buildMarkerPositions(fields);
          if (bonusInfoList.length > 0) {
            const { period_id } = bonusInfoList[0];
            const findPeriod = makerPoolPeriods.filter((item2) => item2.id === period_id)[0];
            recordMarkerPosition[findPeriod.period] = bonusInfoList;
          }
        });
      }
      const allList = [];
      for (const key in recordMarkerPosition) {
        const markerPosition = recordMarkerPosition[key];
        markerPosition.forEach((position) => {
          allList.push(position);
        });
      }
      if (allList.length > 0) {
        const positionList = await this._sdk.ClmmSDK.Position.getSipmlePositionList(
          allList.map((item) => {
            return item.id;
          })
        );
        for (const bonusInfo of allList) {
          for (const position of positionList) {
            if (bonusInfo.id === position.pos_object_id) {
              bonusInfo.clmm_position = position;
              break;
            }
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
    for (const key in recordMarkerPosition) {
      const cacheKey = `${whale_nfts_handle}_${key}_getPoolMarkerPositionList`;
      this.updateCache(cacheKey, recordMarkerPosition[key], cacheTime24h3);
    }
    return recordMarkerPosition;
  }
  /**
   * Updates the XCetus rewarder and fee for a given maker pool, position list, and maker pool period.
   *
   * @param {MakerPool} pool The maker pool.
   * @param {MarkerPosition[]} positionList The list of marker positions.
   * @param {MakerPoolPeriod} makerPoolPeriod The maker pool period.
   * @returns {MarkerPosition[]} The updated list of marker positions.
   */
  async updateXCetusRewarderAndFee(pool, positionList, makerPoolPeriod) {
    const total_points_after_multiper = await this.calculateTotalPointsAfterMultiper(pool, makerPoolPeriod);
    for (const position of positionList) {
      await this.calculateXCetusRewarder(pool, position, makerPoolPeriod.period, total_points_after_multiper);
    }
    return positionList;
  }
  async calculateXCetusRewarder(pool, position, period, total_points_after_multiper) {
    const rewarder_info = await this.getPoolBonusInfo(pool.rewarders.rewarder_handle, period);
    const { fee_share_rate } = this.calculateFeeShareRate(pool, position, total_points_after_multiper);
    const bonus_num = d6(fee_share_rate).mul(rewarder_info.total_bonus);
    if (position.is_redeemed) {
      position.bonus_num = "0";
    } else {
      position.bonus_num = bonus_num.toString();
    }
    return position.bonus_num;
  }
  /**
   * Calculates the fee share rate for a given maker pool, position, and total points after multiplier.
   *
   * @param {MakerPool} pool The maker pool.
   * @param {MarkerPosition} position The position.
   * @param {string} total_points_after_multiper The total points after multiplier.
   * @returns {Object} An object with the fee share rate and points after multiplier.
   */
  calculateFeeShareRate(pool, position, total_points_after_multiper) {
    const bonus_percent = MakerUtil.getBonusPercent(pool.config, position.percent);
    const points_after_multiper = d6(position.point).mul(bonus_percent);
    const fee_share_rate = d6(points_after_multiper).div(total_points_after_multiper);
    position.point_after_multiplier = points_after_multiper.toString();
    position.fee_share_rate = Number(fee_share_rate);
    return { fee_share_rate: Number(fee_share_rate), points_after_multiper: points_after_multiper.toString() };
  }
  /**
   * Calculates the total points after multiplier for a given maker pool and maker pool period.
   *
   * @param {MakerPool} pool The maker pool.
   * @param {MakerPoolPeriod} makerPoolPeriod The maker pool period.
   * @returns {Promise<string>} A promise that resolves to the total points after multiplier.
   */
  async calculateTotalPointsAfterMultiper(pool, makerPoolPeriod) {
    const positionListMap = await this.getPoolMarkerPositionList(pool.whale_nfts.whale_nfts_handle, [makerPoolPeriod]);
    let total_points_after_multiper = d6(0);
    const positionList = positionListMap[makerPoolPeriod.period];
    for (const position of positionList) {
      const bonus_percent = MakerUtil.getBonusPercent(pool.config, position.percent);
      const points_after_multiper = d6(position.point).mul(bonus_percent);
      total_points_after_multiper = total_points_after_multiper.add(points_after_multiper);
    }
    return total_points_after_multiper.toString();
  }
  /**
   * Calculates the XCetus rewarder for all given maker pools.
   *
   * @param {MakerPool[]} pools The list of maker pools.
   * @returns {Object} An object with the total claimable amount and the list of NFT IDs that can be claimed.
   */
  async calculateAllXCetusRewarder(pools) {
    const ownerAddress = this._sdk.ClmmSDK.senderAddress;
    let claimtotal = d6(0);
    const claimRecord = [];
    for (const pool of pools) {
      const makerPoolPeriods = await this._sdk.MakerModule.getMakerPoolPeriods(pool);
      const positionList = await this._sdk.MakerModule.getPoolMarkerPositionList(pool.whale_nfts.whale_nfts_handle, makerPoolPeriods);
      const owner_position_ids = [];
      for (const makerPoolPeriod of makerPoolPeriods) {
        const ownerList = positionList[makerPoolPeriod.period].filter((item) => {
          if (ownerAddress.length === 0) {
            return false;
          }
          return item.clmm_position?.owner === ownerAddress;
        });
        if (ownerList.length > 0) {
          await this._sdk.MakerModule.updateXCetusRewarderAndFee(pool, ownerList, makerPoolPeriod);
          ownerList.forEach((item) => {
            if (item.clmm_position?.position_status === ClmmPositionStatus.Exists && d6(item.bonus_num).greaterThan(0)) {
              claimtotal = claimtotal.add(item.bonus_num);
              if (!owner_position_ids.includes(item.clmm_position.pos_object_id)) {
                owner_position_ids.push(item.clmm_position.pos_object_id);
              }
            }
          });
        }
      }
      claimRecord.push({
        bonus_type: pool.bonus_type,
        pool_id: pool.pool_id,
        nft_ids: owner_position_ids
      });
    }
    return {
      claimtotal,
      claimRecord
    };
  }
  /**
   * Gets the pool bonus information for the given rewarder handle and period.
   *
   * @param {SuiObjectIdType} rewarder_handle The rewarder handle.
   * @param {number} period The period.
   * @param {boolean} forceRefresh Whether to force a refresh of the cache.
   * @returns {Promise<PoolBonusInfo>} A promise that resolves to the pool bonus information.
   */
  async getPoolBonusInfo(rewarder_handle, period, forceRefresh = false) {
    const cacheKey = `${rewarder_handle}_${period}_getPoolBonusInfo`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const results = await this.sdk.ClmmSDK.fullClient.getDynamicFieldObject({
      parentId: rewarder_handle,
      name: {
        type: "u64",
        value: period.toString()
      }
    });
    const fields = getObjectFields6(results);
    const bonusInfo = MakerUtil.buildPoolBonusInfo(fields);
    this.updateCache(cacheKey, bonusInfo, cacheTime5min3);
    return bonusInfo;
  }
  /**
   * Creates a transaction payload for claiming a maker bonus.
   *
   * @param {ClaimMakerParams} params The parameters for the claim.
   * @returns {TransactionBlock} The transaction payload.
   */
  claimPayload(params) {
    const { maker_bonus, xcetus } = this.sdk.sdkOptions;
    const tx = new TransactionBlock3();
    tx.moveCall({
      target: `${maker_bonus.published_at}::${MakerRouterModule}::claim`,
      typeArguments: [params.bonus_type],
      arguments: [
        tx.pure(getPackagerConfigs3(maker_bonus).maker_config_id),
        tx.pure(params.market_pool_id),
        tx.pure(params.position_nft_id),
        tx.pure(params.phase),
        tx.pure(getPackagerConfigs3(xcetus).lock_manager_id),
        tx.pure(getPackagerConfigs3(xcetus).xcetus_manager_id),
        tx.pure(params.ve_nft_id)
      ]
    });
    return tx;
  }
  /**
   * Creates a transaction payload for claiming all bonuses for a given set of whale NFTs.
   *
   * @param {ClaimAllParams} params The parameters for the claim.
   * @returns {TransactionBlock} The transaction payload.
   */
  claimAllPayload(params) {
    const { maker_bonus, xcetus } = this.sdk.sdkOptions;
    const tx = new TransactionBlock3();
    params.whale_nfts.forEach((item) => {
      item.nft_ids.forEach((nft_id) => {
        tx.moveCall({
          target: `${maker_bonus.published_at}::${MakerRouterModule}::claim_all`,
          typeArguments: [item.bonus_type],
          arguments: [
            tx.object(getPackagerConfigs3(maker_bonus).maker_config_id),
            tx.object(item.pool_id),
            tx.object(nft_id),
            tx.object(getPackagerConfigs3(xcetus).lock_manager_id),
            tx.object(getPackagerConfigs3(xcetus).xcetus_manager_id),
            tx.object(params.ve_nft_id)
          ]
        });
      });
    });
    return tx;
  }
  updateCache(key, data, time = cacheTime5min3) {
    let cacheData = this._cache[key];
    if (cacheData) {
      cacheData.overdueTime = getFutureTime3(time);
      cacheData.value = data;
    } else {
      cacheData = new CachedContent3(data, getFutureTime3(time));
    }
    this._cache[key] = cacheData;
  }
  getCache(key, forceRefresh = false) {
    const cacheData = this._cache[key];
    if (!forceRefresh && cacheData?.isValid()) {
      return cacheData.value;
    }
    delete this._cache[key];
    return void 0;
  }
};

// src/modules/xcetusModule.ts
import Decimal2 from "decimal.js";
import {
  buildNFT,
  CachedContent as CachedContent4,
  cacheTime24h as cacheTime24h4,
  cacheTime5min as cacheTime5min4,
  CLOCK_ADDRESS as CLOCK_ADDRESS3,
  d as d8,
  extractStructTagFromType as extractStructTagFromType7,
  getFutureTime as getFutureTime4,
  getMoveObjectType as getMoveObjectType4,
  getObjectFields as getObjectFields7,
  getObjectId as getObjectId3,
  getPackagerConfigs as getPackagerConfigs4,
  TransactionUtil as TransactionUtil2
} from "@cetusprotocol/cetus-sui-clmm-sdk";
import { TransactionBlock as TransactionBlock4 } from "@mysten/sui.js/transactions";

// src/utils/xcetus.ts
import { d as d7, extractStructTagFromType as extractStructTagFromType6, normalizeCoinType } from "@cetusprotocol/cetus-sui-clmm-sdk";

// src/types/xcetus_type.ts
var XcetusRouterModule = "router";
var DividendsRouterModule = "router";
var ONE_DAY_SECONDS = 24 * 3600;
var EXCHANGE_RATE_MULTIPER = 1e3;
var REDEEM_NUM_MULTIPER = 1e11;

// src/utils/xcetus.ts
var XCetusUtil = class {
  static buildVeNFTDividendInfo(fields) {
    const veNFTDividendInfo = {
      id: fields.id.id,
      ve_nft_id: fields.name,
      rewards: []
    };
    fields.value.fields.value.fields.dividends.fields.contents.forEach((item) => {
      const periodRewards = [];
      item.fields.value.fields.contents.forEach((reward) => {
        periodRewards.push({
          coin_type: extractStructTagFromType6(reward.fields.key.fields.name).source_address,
          amount: reward.fields.value
        });
      });
      veNFTDividendInfo.rewards.push({
        period: Number(item.fields.key),
        rewards: periodRewards
      });
    });
    return veNFTDividendInfo;
  }
  static buildDividendManager(fields) {
    const dividendManager = {
      id: fields.id.id,
      dividends: {
        id: fields.dividends.fields.id.id,
        size: fields.dividends.fields.size
      },
      venft_dividends: {
        id: fields.venft_dividends.fields.id.id,
        size: fields.venft_dividends.fields.size
      },
      bonus_types: [],
      start_time: Number(fields.start_time),
      interval_day: Number(fields.interval_day),
      balances: {
        id: fields.balances.fields.id.id,
        size: fields.balances.fields.size
      },
      is_open: fields.is_open
    };
    fields.bonus_types.forEach((item) => {
      dividendManager.bonus_types.push(extractStructTagFromType6(item.fields.name).source_address);
    });
    return dividendManager;
  }
  static buildLockUpManager(fields) {
    const lockUpManager = {
      id: fields.id.id,
      balance: fields.balance,
      treasury_manager: fields.treasury_manager,
      extra_treasury: fields.extra_treasury,
      lock_infos: {
        lock_handle_id: fields.lock_infos.fields.id.id,
        size: Number(fields.lock_infos.fields.size)
      },
      type_name: normalizeCoinType(fields.type_name.fields.name),
      min_lock_day: Number(fields.min_lock_day),
      max_lock_day: Number(fields.max_lock_day),
      package_version: Number(fields.package_version),
      max_percent_numerator: Number(fields.max_percent_numerator),
      min_percent_numerator: Number(fields.min_percent_numerator)
    };
    return lockUpManager;
  }
  static buildLockCetus(data) {
    const fields = data.fields;
    const lockCetus = {
      id: fields.id.id,
      type: extractStructTagFromType6(data.type).source_address,
      locked_start_time: Number(fields.locked_start_time),
      locked_until_time: Number(fields.locked_until_time),
      cetus_amount: fields.balance,
      xcetus_amount: "0",
      lock_day: 0
    };
    lockCetus.lock_day = (lockCetus.locked_until_time - lockCetus.locked_start_time) / ONE_DAY_SECONDS;
    return lockCetus;
  }
  static getAvailableXCetus(veNTF, locks) {
    let lockAmount = d7(0);
    locks.forEach((lock) => {
      lockAmount = lockAmount.add(lock.xcetus_amount);
    });
    return d7(veNTF.xcetus_balance).sub(lockAmount).toString();
  }
  static getWaitUnLockCetuss(locks) {
    return locks.filter((lock) => {
      return !XCetusUtil.isLocked(lock);
    });
  }
  static getLockingCetuss(locks) {
    return locks.filter((lock) => {
      return XCetusUtil.isLocked(lock);
    });
  }
  static isLocked(lock) {
    return lock.locked_until_time > Date.parse((/* @__PURE__ */ new Date()).toString()) / 1e3;
  }
  static buildDividendRewardTypeList(rewardList) {
    const uniqueItems = /* @__PURE__ */ new Set();
    rewardList.forEach((obj) => {
      obj.rewards.forEach((item) => {
        uniqueItems.add(item.coin_type);
      });
    });
    return Array.from(uniqueItems);
  }
  static getNextStartTime(dividendManager) {
    const currentTime = Date.now() / 1e3;
    const { start_time, interval_day } = dividendManager;
    const currentPeriod = Math.ceil((currentTime - start_time) / (interval_day * ONE_DAY_SECONDS));
    const nextStartTime = start_time + currentPeriod * interval_day * ONE_DAY_SECONDS;
    return nextStartTime;
  }
};

// src/modules/xcetusModule.ts
var XCetusModule = class {
  _sdk;
  _cache = {};
  constructor(sdk) {
    this._sdk = sdk;
  }
  get sdk() {
    return this._sdk;
  }
  /**
   * Gets the VeNFT object for the specified account address.
   *
   * @param accountAddress The address of the account that owns the VeNFT object.
   * @param forceRefresh Indicates whether to refresh the cache of the VeNFT object.
   * @returns A Promise that resolves to the VeNFT object or `undefined` if the object is not found.
   */
  async getOwnerVeNFT(accountAddress, forceRefresh = true) {
    const { xcetus } = this.sdk.sdkOptions;
    const cacheKey = `${accountAddress}_getLockUpManagerEvent`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData !== void 0) {
      return cacheData;
    }
    let veNFT;
    const filterType = `${xcetus.package_id}::xcetus::VeNFT`;
    const ownerRes = await this._sdk.ClmmSDK.fullClient.getOwnedObjectsByPage(accountAddress, {
      options: { showType: true, showContent: true, showDisplay: true },
      filter: { StructType: filterType }
    });
    ownerRes.data.forEach((item) => {
      const type = extractStructTagFromType7(getMoveObjectType4(item)).source_address;
      if (type === filterType) {
        if (item.data && item.data.content) {
          const { fields } = item.data.content;
          veNFT = {
            ...buildNFT(item),
            id: fields.id.id,
            index: fields.index,
            type,
            xcetus_balance: fields.xcetus_balance
          };
          this.updateCache(cacheKey, veNFT, cacheTime24h4);
        }
      }
    });
    return veNFT;
  }
  /**
   * Gets the list of LockCetus objects owned by the specified account address.
   *
   * @param accountAddress The address of the account that owns the LockCetus objects.
   * @returns A Promise that resolves to a list of LockCetus objects.
   */
  async getOwnerLockCetuss(accountAddress) {
    const { xcetus } = this.sdk.sdkOptions;
    const lockCetuss = [];
    const filterType = `${xcetus.package_id}::lock_coin::LockedCoin<${this.buileCetusCoinType()}>`;
    const ownerRes = await this._sdk.ClmmSDK.fullClient.getOwnedObjectsByPage(accountAddress, {
      options: { showType: true, showContent: true },
      filter: { StructType: filterType }
    });
    for (const item of ownerRes.data) {
      const type = extractStructTagFromType7(getMoveObjectType4(item)).source_address;
      if (type === filterType) {
        if (item.data) {
          const lockCetus = XCetusUtil.buildLockCetus(item.data.content);
          lockCetus.xcetus_amount = await this.getXCetusAmount(lockCetus.id);
          lockCetuss.push(lockCetus);
        }
      }
    }
    return lockCetuss;
  }
  /**
   * Gets the LockCetus object with the specified ID.
   *
   * @param lock_id The ID of the LockCetus object.
   * @returns A Promise that resolves to the LockCetus object or `undefined` if the object is not found.
   */
  async getLockCetus(lock_id) {
    const result = await this._sdk.ClmmSDK.fullClient.getObject({ id: lock_id, options: { showType: true, showContent: true } });
    if (result.data?.content) {
      const lockCetus = XCetusUtil.buildLockCetus(result.data.content);
      lockCetus.xcetus_amount = await this.getXCetusAmount(lockCetus.id);
      return lockCetus;
    }
    return void 0;
  }
  /**
   * Gets the list of Cetus coins owned by the specified account address.
   *
   * @param accountAddress The address of the account that owns the Cetus coins.
   * @returns A Promise that resolves to a list of CoinAsset objects.
   */
  async getOwnerCetusCoins(accountAddress) {
    const coins = await this._sdk.ClmmSDK.getOwnerCoinAssets(accountAddress, this.buileCetusCoinType());
    return coins;
  }
  /**
   * mint venft
   * @returns
   */
  mintVeNFTPayload() {
    const { xcetus } = this.sdk.sdkOptions;
    const tx = new TransactionBlock4();
    tx.moveCall({
      target: `${xcetus.published_at}::${XcetusRouterModule}::mint_venft`,
      typeArguments: [],
      arguments: [tx.pure(getPackagerConfigs4(xcetus).xcetus_manager_id)]
    });
    return tx;
  }
  /**
   * Convert Cetus to Xcetus.
   * @param params
   * @returns
   */
  async convertPayload(params) {
    const { xcetus } = this.sdk.sdkOptions;
    const tx = new TransactionBlock4();
    const coin_type = this.buileCetusCoinType();
    const primaryCoinInputs = await TransactionUtil2.syncBuildCoinInputForAmount(
      this._sdk.ClmmSDK,
      tx,
      BigInt(params.amount),
      coin_type
    );
    if (params.venft_id === void 0) {
      tx.moveCall({
        target: `${xcetus.published_at}::${XcetusRouterModule}::mint_and_convert`,
        typeArguments: [],
        arguments: [
          tx.object(getPackagerConfigs4(xcetus).lock_manager_id),
          tx.object(getPackagerConfigs4(xcetus).xcetus_manager_id),
          primaryCoinInputs,
          tx.pure(params.amount)
        ]
      });
    } else {
      tx.moveCall({
        target: `${xcetus.published_at}::${XcetusRouterModule}::convert`,
        typeArguments: [],
        arguments: [
          tx.object(getPackagerConfigs4(xcetus).lock_manager_id),
          tx.object(getPackagerConfigs4(xcetus).xcetus_manager_id),
          primaryCoinInputs,
          tx.pure(params.amount),
          tx.pure(params.venft_id)
        ]
      });
    }
    return tx;
  }
  /**
   * Convert Xcetus to Cetus, first step is to lock the Cetus for a period.
   * When the time is reach, cetus can be redeem and xcetus will be burned.
   * @param params
   * @returns
   */
  redeemLockPayload(params) {
    const { xcetus } = this.sdk.sdkOptions;
    const tx = new TransactionBlock4();
    tx.moveCall({
      target: `${xcetus.published_at}::${XcetusRouterModule}::redeem_lock`,
      typeArguments: [],
      arguments: [
        tx.pure(getPackagerConfigs4(xcetus).lock_manager_id),
        tx.pure(getPackagerConfigs4(xcetus).xcetus_manager_id),
        tx.pure(params.venft_id),
        tx.pure(params.amount),
        tx.pure(params.lock_day),
        tx.pure(CLOCK_ADDRESS3)
      ]
    });
    return tx;
  }
  /**
   * lock time is reach and the cetus can be redeemed, the xcetus will be burned.
   * @param params
   * @returns
   */
  redeemPayload(params) {
    const { xcetus } = this.sdk.sdkOptions;
    const tx = new TransactionBlock4();
    tx.moveCall({
      target: `${xcetus.published_at}::${XcetusRouterModule}::redeem`,
      typeArguments: [],
      arguments: [
        tx.pure(getPackagerConfigs4(xcetus).lock_manager_id),
        tx.pure(getPackagerConfigs4(xcetus).xcetus_manager_id),
        tx.pure(params.venft_id),
        tx.pure(params.lock_id),
        tx.pure(CLOCK_ADDRESS3)
      ]
    });
    return tx;
  }
  redeemDividendPayload(venft_id, bonus_types) {
    const { xcetus, xcetus_dividends } = this.sdk.sdkOptions;
    const tx = new TransactionBlock4();
    bonus_types.forEach((coin) => {
      tx.moveCall({
        target: `${xcetus.published_at}::${DividendsRouterModule}::redeem`,
        typeArguments: [coin],
        arguments: [tx.object(getPackagerConfigs4(xcetus_dividends).dividend_manager_id), tx.object(venft_id)]
      });
    });
    return tx;
  }
  redeemDividendV2Payload(venft_id, bonus_types, xTokenType) {
    const { xcetus_dividends } = this.sdk.sdkOptions;
    let tx = new TransactionBlock4();
    const xTokenTypeList = bonus_types.filter((coin) => {
      return xTokenType.includes(coin);
    });
    if (xTokenTypeList.length > 0) {
      tx = this.redeemDividendXTokenPayload(venft_id, tx);
    }
    bonus_types.forEach((coin) => {
      if (!xTokenType.includes(coin)) {
        tx.moveCall({
          target: `${xcetus_dividends.published_at}::${DividendsRouterModule}::redeem_v2`,
          typeArguments: [coin],
          arguments: [tx.object(getPackagerConfigs4(xcetus_dividends).dividend_manager_id), tx.object(venft_id), tx.object(CLOCK_ADDRESS3)]
        });
      }
    });
    return tx;
  }
  redeemDividendXTokenPayload(venft_id, tx) {
    const { xcetus_dividends, xcetus } = this.sdk.sdkOptions;
    const { xcetus_manager_id, lock_manager_id } = getPackagerConfigs4(xcetus);
    const { dividend_manager_id } = getPackagerConfigs4(xcetus_dividends);
    tx = tx === void 0 ? new TransactionBlock4() : tx;
    tx.moveCall({
      target: `${xcetus_dividends.published_at}::${DividendsRouterModule}::redeem_xtoken`,
      typeArguments: [],
      arguments: [
        tx.object(lock_manager_id),
        tx.object(xcetus_manager_id),
        tx.object(dividend_manager_id),
        tx.object(venft_id),
        tx.object(CLOCK_ADDRESS3)
      ]
    });
    return tx;
  }
  buileCetusCoinType() {
    return `${this.sdk.sdkOptions.cetus_faucet.package_id}::cetus::CETUS`;
  }
  buileXTokenCoinType(packageId = this._sdk.sdkOptions.xcetus.package_id, module = "xcetus", name = "XCETUS") {
    return `${packageId}::${module}::${name}`;
  }
  /**
   * Cancel the redeem lock, the cetus locked will be return back to the manager and the xcetus will be available again.
   * @param params
   * @returns
   */
  cancelRedeemPayload(params) {
    const { xcetus } = this.sdk.sdkOptions;
    const tx = new TransactionBlock4();
    tx.moveCall({
      target: `${xcetus.published_at}::${XcetusRouterModule}::cancel_redeem_lock`,
      typeArguments: [],
      arguments: [
        tx.pure(getPackagerConfigs4(xcetus).lock_manager_id),
        tx.pure(getPackagerConfigs4(xcetus).xcetus_manager_id),
        tx.pure(params.venft_id),
        tx.pure(params.lock_id),
        tx.pure(CLOCK_ADDRESS3)
      ]
    });
    return tx;
  }
  /**
   * Gets the init factory event.
   *
   * @returns A Promise that resolves to the init factory event.
   */
  async getInitConfigs() {
    const { package_id } = this.sdk.sdkOptions.xcetus;
    const cacheKey = `${package_id}_getInitFactoryEvent`;
    const cacheData = this.getCache(cacheKey);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const initEventObjects = (await this._sdk.ClmmSDK.fullClient.queryEventsByPage({ MoveEventType: `${package_id}::xcetus::InitEvent` }))?.data;
    const initEvent = {
      xcetus_manager_id: "",
      lock_manager_id: "",
      lock_handle_id: ""
    };
    if (initEventObjects.length > 0) {
      initEventObjects.forEach((item) => {
        const fields = item.parsedJson;
        if (fields) {
          initEvent.xcetus_manager_id = fields.xcetus_manager;
        }
      });
    }
    const lockEventObjects = (await this._sdk.ClmmSDK.fullClient.queryEventsByPage({ MoveEventType: `${package_id}::locking::InitializeEvent` }))?.data;
    if (lockEventObjects.length > 0) {
      lockEventObjects.forEach((item) => {
        const fields = item.parsedJson;
        if (fields) {
          initEvent.lock_manager_id = fields.lock_manager;
        }
      });
    }
    if (initEvent.lock_manager_id.length > 0) {
      initEvent.lock_handle_id = (await this.getLockUpManager(initEvent.lock_manager_id)).lock_infos.lock_handle_id;
    }
    this.updateCache(cacheKey, initEvent, cacheTime24h4);
    return initEvent;
  }
  /**
   * Gets the lock up manager event.
   *
   * @returns A Promise that resolves to the lock up manager event.
   */
  async getLockUpManager(lock_manager_id = getPackagerConfigs4(this.sdk.sdkOptions.xcetus).lock_manager_id, forceRefresh = false) {
    const cacheKey = `${lock_manager_id}_getLockUpManager`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const lockObject = await this.sdk.ClmmSDK.fullClient.getObject({ id: lock_manager_id, options: { showContent: true } });
    const info = XCetusUtil.buildLockUpManager(getObjectFields7(lockObject));
    this.updateCache(cacheKey, info, cacheTime24h4);
    return info;
  }
  /**
   * Gets the dividend manager event.
   *
   * @returns A Promise that resolves to the dividend manager event.
   */
  async getDividendConfigs() {
    const { package_id } = this.sdk.sdkOptions.xcetus_dividends;
    const cacheKey = `${package_id}_getDividendManagerEvent`;
    const cacheData = this.getCache(cacheKey);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const lockEventObjects = (await this._sdk.ClmmSDK.fullClient.queryEventsByPage({ MoveEventType: `${package_id}::dividend::InitEvent` }))?.data;
    const initEvent = {
      dividend_manager_id: "",
      dividend_admin_id: "",
      dividend_settle_id: ""
    };
    if (lockEventObjects.length > 0) {
      lockEventObjects.forEach((item) => {
        const fields = item.parsedJson;
        if (fields) {
          initEvent.dividend_manager_id = fields.manager_id;
          initEvent.dividend_admin_id = fields.admin_id;
          initEvent.dividend_settle_id = fields.settle_id;
          this.updateCache(cacheKey, initEvent, cacheTime24h4);
        }
      });
    }
    return initEvent;
  }
  /**
   * Gets the dividend manager object.
   *
   * @param forceRefresh Whether to force a refresh of the cache.
   * @returns A Promise that resolves to the dividend manager object.
   */
  async getDividendManager(forceRefresh = false) {
    const { dividend_manager_id } = getPackagerConfigs4(this._sdk.sdkOptions.xcetus_dividends);
    const cacheKey = `${dividend_manager_id}_getDividendManager`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const objects = await this._sdk.ClmmSDK.fullClient.getObject({ id: dividend_manager_id, options: { showContent: true } });
    const fields = getObjectFields7(objects);
    const dividendManager = XCetusUtil.buildDividendManager(fields);
    this.updateCache(cacheKey, dividendManager, cacheTime24h4);
    return dividendManager;
  }
  /**
   * Gets the Xcetus manager object.
   *
   * @returns A Promise that resolves to the Xcetus manager object.
   */
  async getXcetusManager(forceRefresh = true) {
    const { xcetus_manager_id } = getPackagerConfigs4(this._sdk.sdkOptions.xcetus);
    const cacheKey = `${xcetus_manager_id}_getXcetusManager`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData) {
      return cacheData;
    }
    const result = await this._sdk.ClmmSDK.fullClient.getObject({ id: xcetus_manager_id, options: { showContent: true } });
    const fields = getObjectFields7(result);
    const xcetusManager = {
      id: fields.id.id,
      index: Number(fields.index),
      has_venft: {
        handle: fields.has_venft.fields.id.id,
        size: fields.has_venft.fields.size
      },
      nfts: {
        handle: fields.nfts.fields.id.id,
        size: fields.nfts.fields.size
      },
      total_locked: fields.total_locked,
      treasury: fields.treasury.fields.total_supply.fields.value
    };
    this.updateCache(cacheKey, xcetusManager);
    return xcetusManager;
  }
  /**
   * Gets the VeNFT dividend information for the specified VeNFT dividend handle and VeNFT ID.
   *
   * @param venft_dividends_handle The VeNFT dividend handle.
   * @param venft_id The VeNFT ID.
   * @returns A Promise that resolves to the VeNFT dividend information or undefined if an error occurs.
   */
  async getVeNFTDividendInfo(venft_dividends_handle, venft_id) {
    try {
      const venft_dividends = await this._sdk.ClmmSDK.fullClient.getDynamicFieldObject({
        parentId: venft_dividends_handle,
        name: {
          type: "0x2::object::ID",
          value: venft_id
        }
      });
      const fields = getObjectFields7(venft_dividends);
      const veNFTDividendInfo = XCetusUtil.buildVeNFTDividendInfo(fields);
      return veNFTDividendInfo;
    } catch (error) {
      return void 0;
    }
  }
  /**
   * Calculates the redeem number for the specified amount and lock day.
   *
   * @param redeemAmount The amount to redeem.
   * @param lockDay The number of days to lock the amount for.
   * @returns A Promise that resolves to an object with the amount out and percent.
   */
  async redeemNum(redeemAmount, lock_day) {
    if (BigInt(redeemAmount) === BigInt(0)) {
      return { amountOut: "0", percent: "0" };
    }
    const lockUpManager = await this.getLockUpManager(getPackagerConfigs4(this._sdk.sdkOptions.xcetus).lock_manager_id);
    const mid = d8(REDEEM_NUM_MULTIPER).mul(d8(lockUpManager.max_lock_day).sub(d8(lock_day))).mul(d8(lockUpManager.max_percent_numerator).sub(d8(lockUpManager.min_percent_numerator))).div(d8(lockUpManager.max_lock_day).sub(d8(lockUpManager.min_lock_day)));
    const percent = d8(REDEEM_NUM_MULTIPER).mul(d8(lockUpManager.max_percent_numerator)).sub(mid).div(d8(EXCHANGE_RATE_MULTIPER)).div(REDEEM_NUM_MULTIPER);
    return { amountOut: d8(percent).mul(d8(redeemAmount)).round().toString(), percent: percent.toString() };
  }
  /**
   * Reverses the redeem number for the specified amount and lock day.
   *
   * @param amount The amount to redeem.
   * @param lockDay The number of days to lock the amount for.
   * @returns A Promise that resolves to an object with the reversed amount and percent.
   */
  async reverseRedeemNum(amount, lock_day) {
    if (BigInt(amount) === BigInt(0)) {
      return { amountOut: "0", percent: "0" };
    }
    const lockUpManager = await this.getLockUpManager(getPackagerConfigs4(this._sdk.sdkOptions.xcetus).lock_manager_id);
    const mid = d8(REDEEM_NUM_MULTIPER).mul(d8(lockUpManager.max_lock_day).sub(d8(lock_day))).mul(d8(lockUpManager.max_percent_numerator).sub(d8(lockUpManager.min_percent_numerator))).div(d8(lockUpManager.max_lock_day).sub(d8(lockUpManager.min_lock_day)));
    const percent = d8(REDEEM_NUM_MULTIPER).mul(d8(lockUpManager.max_percent_numerator)).sub(mid).div(d8(EXCHANGE_RATE_MULTIPER)).div(REDEEM_NUM_MULTIPER);
    return { amountOut: d8(amount).div(percent).toFixed(0, Decimal2.ROUND_UP), percent: percent.toString() };
  }
  /**
   * Gets the XCetus amount for the specified lock ID.
   *
   * @param lockId The ID of the lock.
   * @returns A Promise that resolves to the XCetus amount.
   */
  async getXCetusAmount(lock_id) {
    const { lock_handle_id } = getPackagerConfigs4(this._sdk.sdkOptions.xcetus);
    const cacheKey = `${lock_id}_getXCetusAmount`;
    const cacheData = this.getCache(cacheKey);
    if (cacheData !== void 0) {
      return cacheData;
    }
    try {
      const response = await this.sdk.ClmmSDK.fullClient.getDynamicFieldObject({
        parentId: lock_handle_id,
        name: {
          type: "0x2::object::ID",
          value: lock_id
        }
      });
      const fields = getObjectFields7(response);
      if (fields) {
        const { xcetus_amount } = fields.value.fields.value.fields;
        this.updateCache(cacheKey, xcetus_amount, cacheTime24h4);
        return xcetus_amount;
      }
    } catch (error) {
    }
    return "0";
  }
  /**
   * Gets the amount of XCetus and lock for the specified VENFT.
   *
   * @param nftHandleId The ID of the NFT handle.
   * @param veNftId The ID of the VENFT.
   * @returns A Promise that resolves to an object with the XCetus amount and lock amount.
   */
  async getVeNftAmount(nft_handle_id, ve_nft_id) {
    try {
      const response = await this.sdk.ClmmSDK.fullClient.getDynamicFieldObject({
        parentId: nft_handle_id,
        name: {
          type: "0x2::object::ID",
          value: ve_nft_id
        }
      });
      const fields = getObjectFields7(response);
      if (fields) {
        const { lock_amount, xcetus_amount } = fields.value.fields.value.fields;
        return { lock_amount, xcetus_amount };
      }
    } catch (error) {
    }
    return { lock_amount: "0", xcetus_amount: "0" };
  }
  /**
   * @param phase_handle
   * @param phase
   * @param forceRefresh
   * @returns
   */
  async getPhaseDividendInfo(phase, forceRefresh = false) {
    const dividendManager = await this.getDividendManager();
    const phase_handle = dividendManager.dividends.id;
    const cacheKey = `${phase_handle}_${phase}_getPhaseDividendInfo`;
    const cacheData = this._sdk.getCache(cacheKey, forceRefresh);
    if (cacheData) {
      return cacheData;
    }
    try {
      const res = await this._sdk.ClmmSDK.fullClient.getDynamicFieldObject({
        parentId: phase_handle,
        name: {
          type: "u64",
          value: phase
        }
      });
      const fields = getObjectFields7(res);
      const valueFields = fields.value.fields.value.fields;
      const redeemed_num = valueFields.redeemed_num.fields.contents.map((item) => {
        return {
          name: item.fields.key.fields.name,
          value: item.fields.value
        };
      });
      const bonus_types = valueFields.bonus_types.map((item) => {
        return item.fields.name;
      });
      const bonus = valueFields.bonus.fields.contents.map((item) => {
        return {
          name: item.fields.key.fields.name,
          value: item.fields.value
        };
      });
      const info = {
        id: getObjectId3(res),
        phase: fields.name,
        settled_num: valueFields.settled_num,
        register_time: valueFields.register_time,
        redeemed_num,
        is_settled: valueFields.is_settled,
        bonus_types,
        bonus,
        phase_end_time: ""
      };
      this.updateCache(cacheKey, info);
      return info;
    } catch (error) {
    }
    return void 0;
  }
  updateCache(key, data, time = cacheTime5min4) {
    let cacheData = this._cache[key];
    if (cacheData) {
      cacheData.overdueTime = getFutureTime4(time);
      cacheData.value = data;
    } else {
      cacheData = new CachedContent4(data, getFutureTime4(time));
    }
    this._cache[key] = cacheData;
  }
  getCache(key, forceRefresh = false) {
    const cacheData = this._cache[key];
    if (!forceRefresh && cacheData?.isValid()) {
      return cacheData.value;
    }
    delete this._cache[key];
    return void 0;
  }
};

// src/modules/idoModule.ts
import {
  CachedContent as CachedContent5,
  cacheTime24h as cacheTime24h5,
  extractStructTagFromType as extractStructTagFromType15,
  buildPosition as buildPosition3,
  TransactionUtil as TransactionUtil4,
  CLOCK_ADDRESS as CLOCK_ADDRESS4,
  TickMath as TickMath5,
  d as d18,
  getFutureTime as getFutureTime5,
  asIntN as asIntN4,
  cacheTime5min as cacheTime5min5,
  isSortedSymbols,
  ClmmIntegratePoolModule,
  getPackagerConfigs as getPackagerConfigs8,
  getObjectPreviousTransactionDigest as getObjectPreviousTransactionDigest2,
  getObjectFields as getObjectFields13,
  getObjectType as getObjectType5,
  getObjectOwner as getObjectOwner2
} from "@cetusprotocol/cetus-sui-clmm-sdk";
import { normalizeSuiAddress as normalizeSuiAddress3, normalizeSuiObjectId as normalizeSuiObjectId7 } from "@mysten/sui.js/utils";
import { TransactionBlock as TransactionBlock9 } from "@mysten/sui.js/transactions";

// src/utils/ido.ts
import { d as d9, extractStructTagFromType as extractStructTagFromType8, getMoveObjectType as getMoveObjectType5, getObjectFields as getObjectFields8 } from "@cetusprotocol/cetus-sui-clmm-sdk";
import Decimal3 from "decimal.js";
import { normalizeSuiObjectId as normalizeSuiObjectId3 } from "@mysten/sui.js/utils";

// src/types/ido_type.ts
var IdoFactoryModule = "factory";
var IdoPoolModule = "oversubscribe_pool";
var LIQUIDITY_INJECTION_RATIO_DENOM = 1e6;
var PRICE_MULTIPLICATION = 1e18;
var IdoPoolActivityState = /* @__PURE__ */ ((IdoPoolActivityState2) => {
  IdoPoolActivityState2["Upcoming"] = "Upcoming";
  IdoPoolActivityState2["Live"] = "Live";
  IdoPoolActivityState2["Ended"] = "Ended";
  IdoPoolActivityState2["Failed"] = "Failed";
  IdoPoolActivityState2["Canceled"] = "Canceled";
  return IdoPoolActivityState2;
})(IdoPoolActivityState || {});

// src/types/xtoken_type.ts
var XTokenRouterModule = "router";
var XTokenDividendsRouterModule = "router";

// src/types/liquidity_strategy.ts
var LiquidityStrategyRouterModule = "router";
var LiquiditySide = /* @__PURE__ */ ((LiquiditySide2) => {
  LiquiditySide2["BOTH"] = "both";
  LiquiditySide2["LEFT"] = "left";
  LiquiditySide2["Right"] = "right";
  return LiquiditySide2;
})(LiquiditySide || {});
var Strategy = /* @__PURE__ */ ((Strategy2) => {
  Strategy2[Strategy2["CURVE"] = 3] = "CURVE";
  Strategy2[Strategy2["BID_ASK"] = 4] = "BID_ASK";
  return Strategy2;
})(Strategy || {});
var defaultStrategyConfig = {
  curve: {
    strategy: 3 /* CURVE */,
    both_side: {
      div_width: 10,
      ratios: [0.333333, 0.266666, 0.2, 0.133333, 0.066666]
    },
    one_side: {
      div_width: 5,
      ratios: [0.333333, 0.266666, 0.2, 0.133333, 0.066666]
    }
  },
  bidAsk: {
    strategy: 4 /* BID_ASK */,
    both_side: {
      div_width: 10,
      ratios: [0.172413793, 0.137931034, 0.103448276, 0.068965517, 0.034482759, 0.068965517, 0.103448276, 0.137931034, 0.172413793]
    },
    one_side: {
      div_width: 5,
      ratios: [0.066666667, 0.13333333, 0.2, 0.266666667, 0.333333333]
    }
  }
};
function isAddMasterLiquidityParams(params) {
  return "master_address" in params;
}

// src/types/vaults.ts
var VaultsRouterModule = "router";
var VaultsVaultModule = "vaults";
var PROTOCOL_FEE_DENOMINATOR = 1e4;
var VaultStatus = /* @__PURE__ */ ((VaultStatus2) => {
  VaultStatus2["STATUS_RUNNING"] = "STATUS_RUNNING";
  VaultStatus2["STATUS_REBALANCING"] = "STATUS_REBALANCING";
  return VaultStatus2;
})(VaultStatus || {});
var StakeProtocol = /* @__PURE__ */ ((StakeProtocol2) => {
  StakeProtocol2["Haedal"] = "Haedal";
  StakeProtocol2["Volo"] = "Volo";
  StakeProtocol2["Aftermath"] = "aftermath";
  return StakeProtocol2;
})(StakeProtocol || {});

// src/types/limit_order.ts
var LimitOrderStatus = /* @__PURE__ */ ((LimitOrderStatus2) => {
  LimitOrderStatus2["Running"] = "Running";
  LimitOrderStatus2["PartialCompleted"] = "PartialCompleted";
  LimitOrderStatus2["Completed"] = "Completed";
  LimitOrderStatus2["Cancelled"] = "Cancelled";
  return LimitOrderStatus2;
})(LimitOrderStatus || {});

// src/utils/ido.ts
var IdoUtil = class {
  static buildIdoPoolImmutables(fields) {
    const info = {
      pool_address: normalizeSuiObjectId3(fields.pool_id),
      coin_type_sale: extractStructTagFromType8(fields.sale_coin_type.name).full_address,
      coin_type_raise: extractStructTagFromType8(fields.raise_coin_type.name).full_address,
      tick_spacing: Number(fields.tick_spacing),
      total_supply: fields.total_supply,
      min_purchase: fields.min_purchase_amount,
      max_purchase: fields.max_purchase_amount
    };
    return info;
  }
  static buildIdoPool(objects) {
    const type = getMoveObjectType5(objects);
    const formatType = extractStructTagFromType8(type);
    const fields = getObjectFields8(objects);
    console.log("fields:", fields);
    const poolImmutables = {
      pool_address: normalizeSuiObjectId3(fields.id.id),
      coin_type_sale: formatType.type_arguments[0],
      coin_type_raise: formatType.type_arguments[1],
      tick_spacing: Number(fields.tick_spacing),
      total_supply: fields.total_supply,
      min_purchase: fields.min_purchase_amount,
      max_purchase: fields.max_purchase_amount
    };
    const durationManager = {
      start_time: Number(fields.duration_manager.fields.start_time),
      lock_up_duration: Number(fields.duration_manager.fields.lock_up_duration),
      purchase_duration: Number(fields.duration_manager.fields.purchase_duration),
      end_time: 0
    };
    durationManager.end_time = durationManager.start_time + durationManager.purchase_duration;
    const poolState = {
      complete_raise_limit: fields.complete_raise_limit,
      liquidity_injection_ratio: d9(fields.liquidity_injection_ratio).div(LIQUIDITY_INJECTION_RATIO_DENOM).toNumber(),
      current_sqrt_price_min: fields.current_sqrt_price_min,
      current_sqrt_price_max: fields.current_sqrt_price_max,
      owner: fields.owner,
      sale_coin_amount: fields.sale_coin,
      raise_coin_amount: fields.raise_coin,
      price: fields.price,
      current_price: fields.price,
      duration_summary: durationManager,
      is_cancelled: fields.is_cancelled,
      is_liquidity_injected: fields.is_liquidity_injected,
      liquidity_used_raise_amount: fields.liquidity_used_raise_amount,
      liquidity_used_sale_amount: fields.liquidity_used_sale_amount,
      purchase_summary: {
        size: Number(fields.purchase_manager.fields.purchases.fields.size),
        purchase_handle: fields.purchase_manager.fields.purchases.fields.id.id
      },
      total_purchase_amount: fields.total_purchase_amount,
      white_summary: {
        white_list_id: fields.white_list.fields.id.id,
        protection_limit_used: fields.white_list.fields.protection_limit_used,
        protection_hard_cap: fields.white_list.fields.protection_hard_cap,
        white_handle: fields.white_list.fields.addresses.fields.id.id,
        size: Number(fields.white_list.fields.addresses.fields.size)
      },
      pool_status: "Upcoming" /* Upcoming */,
      max_raise_amount: "",
      sale_decimals: 0,
      raise_decimals: 0,
      is_withdrawn: fields.is_withdrawn
    };
    IdoUtil.updatePoolStatus(poolState);
    return {
      ...poolImmutables,
      ...poolState
    };
  }
  /**
   * Check if Pool is in the initialization state.
   * @param manager
   * @returns
   */
  static is_init_period(manager) {
    const now = Number(d9(Date.now() / 1e3).toFixed(0));
    return now < manager.start_time;
  }
  /**
   * Check if Pool is in the purchase state
   * @param manager
   * @returns
   */
  static is_purchase_period(manager) {
    const now = Number(d9(Date.now() / 1e3).toFixed(0));
    return now >= manager.start_time && now <= manager.start_time + manager.purchase_duration;
  }
  /**
   * Check if Pool is in the claim state
   * @param manager
   * @returns
   */
  static is_claim_period(manager) {
    const now = Number(d9(Date.now() / 1e3).toFixed(0));
    const start_claim_time = manager.start_time + manager.purchase_duration;
    return now > start_claim_time;
  }
  /**
   * is can injectLiquidity
   * @param manager
   * @returns
   */
  static is_can_inject_liquidity(pool) {
    return !pool.is_cancelled && pool.liquidity_injection_ratio !== 0 && IdoUtil.is_claim_period(pool.duration_summary) && !pool.is_liquidity_injected;
  }
  /**
   * is can withdraw
   * @param pool
   * @returns
   */
  static is_can_withdraw(pool) {
    if (pool.is_withdrawn) {
      return false;
    }
    if (IdoUtil.is_sale_failed(pool)) {
      return true;
    }
    return (pool.liquidity_injection_ratio === 0 || pool.is_liquidity_injected) && IdoUtil.is_claim_period(pool.duration_summary);
  }
  /**
   * Check if the pool sale failed
   * @param manager
   * @returns
   */
  static is_sale_failed(pool) {
    if (pool.is_cancelled) {
      return true;
    }
    if (IdoUtil.is_claim_period(pool.duration_summary) && d9(pool.total_purchase_amount).lessThan(pool.complete_raise_limit)) {
      return true;
    }
    return false;
  }
  static buildPurchaseInfo(filds) {
    const jsonItem = filds.value.fields.value.fields;
    const info = {
      ...jsonItem,
      owner: filds.name
    };
    return info;
  }
  /**
   * calculate `obtained_sale_ccoin` and `refunded_raise_coin`
   * @param pool
   * @param purchaseInfo
   */
  static calculateRedeemAmount(pool, purchaseInfo) {
    let obtained_sale_amount;
    let used_raise_amount;
    if (IdoUtil.is_sale_failed(pool)) {
      obtained_sale_amount = "0";
      used_raise_amount = "0";
    } else if (purchaseInfo.is_redeemed) {
      obtained_sale_amount = purchaseInfo.obtained_sale_amount;
      used_raise_amount = purchaseInfo.used_raise_amount;
    } else if (d9(pool.total_purchase_amount).lessThanOrEqualTo(pool.max_raise_amount)) {
      obtained_sale_amount = d9(purchaseInfo.purchased_raise_amount).mul(PRICE_MULTIPLICATION).div(pool.price).toString();
      used_raise_amount = purchaseInfo.purchased_raise_amount;
    } else {
      const res = IdoUtil.redeemForSaleOversubcribeV2(pool, purchaseInfo);
      obtained_sale_amount = res.user_obtained_sale_amount;
      used_raise_amount = res.user_used_raise_amount_ceil;
    }
    obtained_sale_amount = Decimal3.floor(obtained_sale_amount);
    const refunded_raise_amount = Decimal3.floor(d9(purchaseInfo.purchased_raise_amount).sub(used_raise_amount).toString());
    return { obtained_sale_amount, refunded_raise_amount };
  }
  /**
   * Withdraw the `otained_raise_coin` and `refunded_sale_coin` for pool's owner.
   * @param pool
   * @returns
   */
  static calculateWithdrawAmount(pool) {
    let obtained_raise_amount;
    let refunded_sale_amount;
    const resever_sale_amount_for_liquidity = d9(pool.total_supply).mul(pool.liquidity_injection_ratio);
    if (IdoUtil.is_sale_failed(pool)) {
      obtained_raise_amount = "0";
      refunded_sale_amount = d9(resever_sale_amount_for_liquidity).add(pool.total_supply);
    } else {
      const { max_raise_amount } = pool;
      if (d9(pool.total_purchase_amount).greaterThanOrEqualTo(max_raise_amount)) {
        obtained_raise_amount = d9(max_raise_amount).sub(pool.liquidity_used_raise_amount).toString();
        refunded_sale_amount = "0";
      } else {
        obtained_raise_amount = d9(pool.total_purchase_amount).sub(pool.liquidity_used_raise_amount);
        refunded_sale_amount = d9(pool.total_supply).add(resever_sale_amount_for_liquidity).sub(pool.liquidity_used_sale_amount).sub(IdoUtil.get_sold_amount(pool));
      }
    }
    return {
      obtained_raise_amount: Decimal3.floor(obtained_raise_amount).toString(),
      refunded_sale_amount: Decimal3.ceil(refunded_sale_amount).toString()
    };
  }
  /**
   * Calculate the actual sold amount, but note that if the fundraising fails, the sold amount will be 0 and this
   * function should not be used for calculation.
   * @param pool
   */
  static get_sold_amount(pool) {
    if (d9(pool.total_purchase_amount).greaterThanOrEqualTo(pool.max_raise_amount)) {
      return pool.total_supply;
    }
    return d9(pool.total_purchase_amount).mul(PRICE_MULTIPLICATION).div(pool.price);
  }
  static redeemForSaleOversubcribe(pool, purchaseInfo) {
    const user_protection_raise_amount = purchaseInfo.protection_raise_amount;
    const user_unprotection_raise_amount = d9(purchaseInfo.purchased_raise_amount).sub(user_protection_raise_amount);
    const pool_max_raise_amount = pool.max_raise_amount;
    const { protection_limit_used } = pool.white_summary;
    const pool_expect_unprotection_raise_total = d9(pool_max_raise_amount).sub(protection_limit_used);
    const pool_sold_unprotection_raise_total = d9(pool.total_purchase_amount).sub(protection_limit_used);
    const user_used_raise_amount_ceil = Decimal3.ceil(
      user_unprotection_raise_amount.mul(pool_expect_unprotection_raise_total).div(pool_sold_unprotection_raise_total).add(user_protection_raise_amount)
    );
    const user_used_raise_amount_floor = Decimal3.floor(
      user_unprotection_raise_amount.mul(pool_expect_unprotection_raise_total).div(pool_sold_unprotection_raise_total).add(user_protection_raise_amount)
    );
    const user_obtained_sale_amount = Decimal3.floor(d9(pool.total_supply).mul(user_used_raise_amount_floor).div(pool_max_raise_amount));
    return { user_obtained_sale_amount, user_used_raise_amount_ceil };
  }
  static redeemForSaleOversubcribeV2(pool, purchaseInfo) {
    const user_protection_raise_amount = purchaseInfo.protection_raise_amount;
    const user_unprotection_raise_amount = d9(purchaseInfo.purchased_raise_amount).sub(user_protection_raise_amount);
    const pool_max_raise_amount = pool.max_raise_amount;
    const { protection_limit_used } = pool.white_summary;
    const pool_unprotection_raise_amount = d9(pool_max_raise_amount).sub(protection_limit_used);
    const pool_sold_unprotection_raise_total = d9(pool.total_purchase_amount).sub(protection_limit_used);
    const user_obtained_sale_amount = d9(user_protection_raise_amount).mul(pool_sold_unprotection_raise_total).add(d9(user_unprotection_raise_amount).mul(pool_unprotection_raise_amount)).mul(PRICE_MULTIPLICATION).div(d9(pool_sold_unprotection_raise_total).mul(pool.price));
    const user_used_raise_amount_ceil = Decimal3.ceil(user_obtained_sale_amount.mul(pool.price).div(PRICE_MULTIPLICATION));
    return { user_obtained_sale_amount, user_used_raise_amount_ceil };
  }
  static raiseTotalAmount(pool) {
    const subDecimals = d9(pool.sale_decimals - pool.raise_decimals).abs().toNumber();
    if (pool.sale_decimals > pool.raise_decimals) {
      return d9(pool.total_supply).mul(pool.current_price).div(d9(10).pow(d9(subDecimals))).toString();
    }
    return d9(pool.total_supply).mul(pool.current_price).mul(d9(10).pow(d9(subDecimals))).toString();
  }
  static updatePoolStatus(pool) {
    const now = Number(d9(Date.now() / 1e3).toFixed(0));
    const manager = pool.duration_summary;
    const end_activity_time = manager.start_time + manager.purchase_duration;
    if (pool.is_cancelled) {
      pool.pool_status = "Canceled" /* Canceled */;
    } else if (now < manager.start_time) {
      pool.pool_status = "Upcoming" /* Upcoming */;
    } else if (now > manager.start_time && now < end_activity_time) {
      pool.pool_status = "Live" /* Live */;
    } else if (d9(pool.total_purchase_amount).lessThan(pool.complete_raise_limit)) {
      pool.pool_status = "Failed" /* Failed */;
    } else {
      pool.pool_status = "Ended" /* Ended */;
    }
  }
  /**
   * calculate Pool Price
   * @param sdk
   * @param pool
   */
  static async calculatePoolUiPrice(sdk, pool) {
    const coinInfos = await sdk.ClmmSDK.CetusConfig.getTokenListByCoinTypes([pool.coin_type_sale, pool.coin_type_raise]);
    const saleDecimals = coinInfos[pool.coin_type_sale].decimals;
    const raiseDecimals = coinInfos[pool.coin_type_raise].decimals;
    pool.current_price = LauncpadUtil.priceFixToReal(
      Number(d9(pool.price).div(d9(PRICE_MULTIPLICATION)).toString()),
      saleDecimals,
      raiseDecimals
    ).toString();
    pool.sale_decimals = saleDecimals;
    pool.raise_decimals = raiseDecimals;
    pool.max_raise_amount = this.raiseTotalAmount(pool);
  }
};

// src/utils/ido_router.ts
import { extractStructTagFromType as extractStructTagFromType9, getObjectType as getObjectType2 } from "@cetusprotocol/cetus-sui-clmm-sdk";
import { normalizeSuiAddress as normalizeSuiAddress2 } from "@mysten/sui.js/utils";
var IdoVersion = /* @__PURE__ */ ((IdoVersion2) => {
  IdoVersion2["V1"] = "1";
  IdoVersion2["V2"] = "2";
  return IdoVersion2;
})(IdoVersion || {});
var IdoRouter = class {
  _sdk;
  constructor(sdk) {
    this._sdk = sdk;
  }
  async getPools(assignPools = [], forceRefresh = false) {
    const pools = [];
    if (assignPools.length > 0) {
      const filerIds = assignPools.filter((id) => {
        const cachekey = `${id}_getPool`;
        const pool = this._sdk.getCache(cachekey, forceRefresh);
        if (pool) {
          pools.push(pool);
          return false;
        }
        return true;
      });
      if (filerIds.length > 0) {
        const objectDataResponses = await this._sdk.ClmmSDK.fullClient.batchGetObjects(assignPools, { showType: true, showContent: true });
        for (const suiObj of objectDataResponses) {
          const pool = await this.buildIdoWarpPool(suiObj);
          if (pool) {
            pools.push(pool);
          }
        }
      }
    } else {
      const idoPools = await this._sdk.Ido.getPools();
      idoPools.forEach((pool) => {
        pools.push({
          ...pool,
          version: "2" /* V2 */
        });
      });
    }
    pools.forEach((pool) => {
      const cacheKey = `${pool.pool_address}_getPool`;
      this._sdk.updateCache(cacheKey, pool);
    });
    return pools;
  }
  async getPool(id, forceRefresh = true) {
    const cachekey = `${id}_getPool`;
    const cacheData = this._sdk.getCache(cachekey, forceRefresh);
    if (cacheData) {
      return cacheData;
    }
    const objects = await this._sdk.ClmmSDK.fullClient.getObject({
      id,
      options: { showContent: true, showType: true }
    });
    const pool = await this.buildIdoWarpPool(objects);
    this._sdk.updateCache(cachekey, pool);
    return pool;
  }
  async getJoinPools(address, poolList) {
    const joinPools = [];
    const poolListV1 = poolList.filter((pool) => pool.version === "1" /* V1 */);
    const poolListV2 = poolList.filter((pool) => pool.version === "2" /* V2 */);
    if (poolListV1.length > 0) {
      const purchaseMarks = await this._sdk.Launchpad.getPurchaseMarks(
        address,
        poolListV1.map((item) => item.pool_address),
        false
      );
      poolListV1.forEach((pool) => {
        for (const purchaseMark of purchaseMarks) {
          if (purchaseMark.pool_id === pool.pool_address) {
            joinPools.push(pool);
            break;
          }
        }
      });
    }
    for (const pool of poolListV2) {
      const purchaseInfo = await this._sdk.Ido.getPurchaseInfo(pool.purchase_summary.purchase_handle, address, false);
      if (purchaseInfo) {
        joinPools.push(pool);
      }
    }
    return joinPools;
  }
  async getOwnerPools(ownerAddress, poolList) {
    return poolList.filter((pool) => pool.owner === normalizeSuiAddress2(ownerAddress));
  }
  async creatPurchasePayload(pool, raiseAmount) {
    if (pool.version === "1" /* V1 */) {
      const payload2 = await this._sdk.Launchpad.creatPurchasePayload({
        pool_address: pool.pool_address,
        purchase_amount: raiseAmount,
        coin_type_sale: pool.coin_type_sale,
        coin_type_raise: pool.coin_type_raise
      });
      return payload2;
    }
    const payload = await this._sdk.Ido.creatPurchasePayload({
      pool_address: pool.pool_address,
      coin_type_sale: pool.coin_type_sale,
      coin_type_raise: pool.coin_type_raise,
      purchase_handle: pool.purchase_summary.purchase_handle,
      raise_amount: raiseAmount.toString()
    });
    return payload;
  }
  async creatRedeemPayload(pool) {
    if (pool.version === "1" /* V1 */) {
      const payload2 = await this._sdk.Launchpad.creatClaimPayload({
        pool_address: pool.pool_address,
        coin_type_sale: pool.coin_type_sale,
        coin_type_raise: pool.coin_type_raise
      });
      return payload2;
    }
    const payload = await this._sdk.Ido.redeemPayload({
      pool_address: pool.pool_address,
      coin_type_sale: pool.coin_type_sale,
      coin_type_raise: pool.coin_type_raise,
      purchase_handle: pool.purchase_summary.purchase_handle
    });
    return payload;
  }
  async creatWithdrawPayload(pool) {
    if (pool.version === "1" /* V1 */) {
      const saleAmount = await LauncpadUtil.getWithdrawSale(pool);
      const raiseAmount = await LauncpadUtil.getWithdrawRaise(pool);
      const payload2 = this._sdk.Launchpad.creatWithdrawPayload({
        pool_address: pool.pool_address,
        coin_type_sale: pool.coin_type_sale,
        coin_type_raise: pool.coin_type_raise,
        sale_amount: BigInt(saleAmount),
        raise_amount: BigInt(raiseAmount)
      });
      return payload2;
    }
    const payload = this._sdk.Ido.withdrawPayload({
      pool_address: pool.pool_address,
      coin_type_sale: pool.coin_type_sale,
      coin_type_raise: pool.coin_type_raise
    });
    return payload;
  }
  unlockNftPayload(pool, params) {
    if (pool.version === "1" /* V1 */) {
      const payload2 = this._sdk.Launchpad.creatUnlockNftPayload(params);
      return payload2;
    }
    const payload = this._sdk.Ido.unlockNftPayload({
      lock_nft_id: params.lock_nft,
      nft_type: params.nft_type
    });
    return payload;
  }
  async getLockNFTList(pool) {
    let lockNFTList = [];
    if (pool.version === "1" /* V1 */) {
      lockNFTList = await this._sdk.Launchpad.getLockNFTList(pool.owner);
    }
    lockNFTList = await this._sdk.Ido.getLockNFTList(pool.owner);
    const list = lockNFTList.filter((item) => {
      const coin_type_a = extractStructTagFromType9(item.coin_type_a).full_address;
      const coin_type_b = extractStructTagFromType9(item.coin_type_b).full_address;
      const coin_type_sale = extractStructTagFromType9(pool.coin_type_sale).full_address;
      const coin_type_raise = extractStructTagFromType9(pool.coin_type_raise).full_address;
      return coin_type_a === coin_type_sale && coin_type_b === coin_type_raise || coin_type_a === coin_type_raise && coin_type_b === coin_type_sale;
    });
    return list;
  }
  async isWhiteListUser(walletAddress, pool) {
    if (pool.version === "1" /* V1 */) {
      const isWhiteListUser = await this._sdk.Launchpad.isWhiteListUser(pool.white_summary.white_handle, walletAddress);
      return isWhiteListUser;
    }
    const protectionLimit = await this._sdk.Ido.getWhiteProtectionLimit(pool.white_summary.white_handle, walletAddress);
    if (protectionLimit) {
      return true;
    }
    return false;
  }
  async getPurchaseInfo(walletAddress, pool) {
    if (pool.version === "1" /* V1 */) {
      const purchaseMark = await this._sdk.Launchpad.getPurchaseMarks(walletAddress, [pool.pool_address]);
      if (purchaseMark.length > 0) {
        return purchaseMark[0];
      }
      return void 0;
    }
    const purchaseInfo = await this._sdk.Ido.getPurchaseInfo(pool.purchase_summary.purchase_handle, walletAddress);
    return purchaseInfo;
  }
  async getwhiteProtectionInfo(walletAddress, pool, forceRefresh = false) {
    if (pool.version === "1" /* V1 */) {
      const purchaseAmount = await this._sdk.Launchpad.getPurchaseAmount(pool.white_summary.white_handle, walletAddress);
      return purchaseAmount;
    }
    const purchaseInfo = await this._sdk.Ido.getPurchaseInfo(pool.purchase_summary.purchase_handle, walletAddress, forceRefresh);
    if (purchaseInfo) {
      return { safe_limit_amount: purchaseInfo.protection_raise_limit, safe_purchased_amount: purchaseInfo.protection_raise_amount };
    }
    const whiteProtectionLimit = await this._sdk.Ido.getWhiteProtectionLimit(pool.white_summary.white_handle, walletAddress, false);
    if (whiteProtectionLimit) {
      return { safe_limit_amount: whiteProtectionLimit, safe_purchased_amount: "0" };
    }
    return { safe_limit_amount: "0", safe_purchased_amount: "0" };
  }
  async calculateWithdrawAmount(pool) {
    if (pool.version === "1" /* V1 */) {
      const obtained_raise_amount = await LauncpadUtil.getHistoryWithdrawRaise(this._sdk, pool);
      const refunded_sale_amount = await LauncpadUtil.getHistoryWithdrawSale(this._sdk, pool);
      return { obtained_raise_amount, refunded_sale_amount };
    }
    return IdoUtil.calculateWithdrawAmount(pool);
  }
  async buildIdoWarpPool(suiObj) {
    const type = getObjectType2(suiObj);
    console.log(this._sdk.sdkOptions.ido.package_id, extractStructTagFromType9(type).address, "buildIdoWarpPool");
    if (extractStructTagFromType9(type).address === this._sdk.sdkOptions.ido.package_id) {
      const pool = IdoUtil.buildIdoPool(suiObj);
      await IdoUtil.calculatePoolUiPrice(this._sdk, pool);
      return {
        ...pool,
        version: "2" /* V2 */
      };
    }
    if (extractStructTagFromType9(type).address === this._sdk.sdkOptions.launchpad.package_id) {
      const pool = LauncpadUtil.buildLaunchPadPool(suiObj);
      await LauncpadUtil.calculatePoolPrice(this._sdk, pool);
      return {
        ...pool,
        version: "1" /* V1 */
      };
    }
    return void 0;
  }
};

// src/utils/xtoken.ts
import { d as d10, extractStructTagFromType as extractStructTagFromType10, normalizeCoinType as normalizeCoinType2 } from "@cetusprotocol/cetus-sui-clmm-sdk";
var XTokenUtil = class {
  static buildVeNFTDividendInfo(fields) {
    const veNFTDividendInfo = {
      id: fields.id.id,
      ve_nft_id: fields.name,
      rewards: []
    };
    fields.value.fields.value.fields.dividends.fields.contents.forEach((item) => {
      const periodRewards = [];
      item.fields.value.fields.contents.forEach((reward) => {
        periodRewards.push({
          coin_type: extractStructTagFromType10(reward.fields.key.fields.name).source_address,
          amount: reward.fields.value
        });
      });
      veNFTDividendInfo.rewards.push({
        period: Number(item.fields.key),
        rewards: periodRewards
      });
    });
    return veNFTDividendInfo;
  }
  static buildDividendManager(fields) {
    const dividendManager = {
      id: fields.id.id,
      dividends: {
        id: fields.dividends.fields.id.id,
        size: fields.dividends.fields.size
      },
      venft_dividends: {
        id: fields.venft_dividends.fields.id.id,
        size: fields.venft_dividends.fields.size
      },
      bonus_types: [],
      start_time: Number(fields.start_time),
      interval_day: Number(fields.interval_day),
      balances: {
        id: fields.balances.fields.id.id,
        size: fields.balances.fields.size
      },
      is_open: fields.is_open
    };
    fields.bonus_types.forEach((item) => {
      dividendManager.bonus_types.push(extractStructTagFromType10(item.fields.name).source_address);
    });
    return dividendManager;
  }
  static buildLockUpManager(fields) {
    const lockUpManager = {
      id: fields.id.id,
      balance: fields.balance,
      treasury_manager: fields.treasury_manager,
      extra_treasury: fields.extra_treasury,
      lock_infos: {
        lock_handle_id: fields.lock_infos.fields.id.id,
        size: Number(fields.lock_infos.fields.size)
      },
      type_name: normalizeCoinType2(fields.type_name.fields.name),
      min_lock_day: Number(fields.min_lock_day),
      max_lock_day: Number(fields.max_lock_day),
      package_version: Number(fields.package_version),
      max_percent_numerator: Number(fields.max_percent_numerator),
      min_percent_numerator: Number(fields.min_percent_numerator)
    };
    return lockUpManager;
  }
  static buildLockToken(data) {
    const fields = data.fields;
    const lockToken = {
      id: fields.id.id,
      type: extractStructTagFromType10(data.type).source_address,
      locked_start_time: Number(fields.locked_start_time),
      locked_until_time: Number(fields.locked_until_time),
      token_amount: fields.balance,
      xtoken_amount: "0",
      lock_day: 0
    };
    lockToken.lock_day = (lockToken.locked_until_time - lockToken.locked_start_time) / ONE_DAY_SECONDS;
    return lockToken;
  }
  static getAvailableXToken(veNTF, locks) {
    let lockAmount = d10(0);
    locks.forEach((lock) => {
      lockAmount = lockAmount.add(lock.xtoken_amount);
    });
    return d10(veNTF.xtoken_balance).sub(lockAmount).toString();
  }
  static getWaitUnLockTokens(locks) {
    return locks.filter((lock) => {
      return !XTokenUtil.isLocked(lock);
    });
  }
  static getLockingTokens(locks) {
    return locks.filter((lock) => {
      return XTokenUtil.isLocked(lock);
    });
  }
  static buildDividendRewardTypeList(rewardList) {
    const uniqueItems = /* @__PURE__ */ new Set();
    rewardList.forEach((obj) => {
      obj.rewards.forEach((item) => {
        uniqueItems.add(item.coin_type);
      });
    });
    return Array.from(uniqueItems);
  }
  static isLocked(lock) {
    return lock.locked_until_time > Date.parse((/* @__PURE__ */ new Date()).toString()) / 1e3;
  }
  static getNextStartTime(dividendManager, intervalUnit = ONE_DAY_SECONDS) {
    const currentTime = Date.now() / 1e3;
    const { start_time, interval_day } = dividendManager;
    const currentPeriod = Math.ceil((currentTime - start_time) / (interval_day * intervalUnit));
    const nextStartTime = start_time + currentPeriod * interval_day * intervalUnit;
    return nextStartTime;
  }
};

// src/utils/liquidity_strategy.ts
import BN2 from "bn.js";
import {
  ClmmPoolUtil,
  MAX_TICK_INDEX,
  MIN_TICK_INDEX,
  Percentage,
  TickMath as TickMath2,
  ZERO,
  adjustForCoinSlippage,
  asIntN,
  buildNFT as buildNFT2,
  d as d11,
  extractStructTagFromType as extractStructTagFromType11,
  getObjectFields as getObjectFields9,
  toDecimalsAmount
} from "@cetusprotocol/cetus-sui-clmm-sdk";
var LiquidityStrategyUtils = class {
  static getDefaultStrategyConfig(strategy) {
    let config;
    switch (strategy) {
      case 4 /* BID_ASK */:
        config = defaultStrategyConfig.bidAsk;
        break;
      default:
        config = defaultStrategyConfig.curve;
        break;
    }
    return config;
  }
  static geTickIndexRange(currTickIndex, tickSpacing, floatRatio, liquiditySide) {
    let tick_lower;
    let tick_upper;
    currTickIndex = TickMath2.getInitializableTickIndex(currTickIndex, tickSpacing);
    switch (liquiditySide) {
      case "left" /* LEFT */:
        if (currTickIndex > 0) {
          tick_upper = d11(currTickIndex).sub(tickSpacing).toNumber();
          tick_lower = d11(currTickIndex).mul(d11(1).add(floatRatio)).toDecimalPlaces(0).toNumber();
        } else {
          tick_upper = d11(currTickIndex).sub(tickSpacing).toNumber();
          tick_lower = d11(currTickIndex).mul(d11(1).sub(floatRatio)).toDecimalPlaces(0).toNumber();
        }
        break;
      case "right" /* Right */:
        if (currTickIndex > 0) {
          tick_upper = d11(currTickIndex).mul(d11(1).add(floatRatio)).toDecimalPlaces(0).toNumber();
          tick_lower = d11(currTickIndex).add(tickSpacing).toNumber();
        } else {
          tick_upper = d11(currTickIndex).mul(d11(1).sub(floatRatio)).toDecimalPlaces(0).toNumber();
          tick_lower = d11(currTickIndex).add(tickSpacing).toNumber();
        }
        break;
      default:
        if (currTickIndex > 0) {
          tick_lower = d11(currTickIndex).mul(d11(1).sub(floatRatio)).toDecimalPlaces(0).toNumber();
          tick_upper = d11(currTickIndex).mul(d11(1).add(floatRatio)).toDecimalPlaces(0).toNumber();
        } else {
          tick_lower = d11(currTickIndex).mul(d11(1).add(floatRatio)).toDecimalPlaces(0).toNumber();
          tick_upper = d11(currTickIndex).mul(d11(1).sub(floatRatio)).toDecimalPlaces(0).toNumber();
        }
        break;
    }
    tick_lower = TickMath2.getInitializableTickIndex(tick_lower, tickSpacing);
    tick_upper = TickMath2.getInitializableTickIndex(tick_upper, tickSpacing);
    if (tick_lower < MIN_TICK_INDEX) {
      throw Error("Tick lower value is below the minimum allowed value.");
    }
    if (tick_upper > MAX_TICK_INDEX) {
      throw Error("Tick upper value is above the maximum allowed value.");
    }
    return { tick_lower, tick_upper };
  }
  static getCreateLiquidityBins(params, strategy) {
    let liquidityBins = [];
    switch (strategy) {
      case 4 /* BID_ASK */:
        liquidityBins = LiquidityStrategyUtils.getCreateBidAskLiquidityBins(params);
        break;
      default:
        liquidityBins = LiquidityStrategyUtils.getCreateCurveLiquidityBins(params);
        break;
    }
    return liquidityBins;
  }
  static getCreateBidAskLiquidityBins(params) {
    const tickWidth = d11(Math.floor(d11(params.tick_upper).sub(params.tick_lower).div(params.div_width).toNumber())).abs();
    const tickRangeArray = [];
    const ratioConfigs = params.ratios;
    let last_lower_tick;
    let last_upper_tick;
    for (let index = 0; index < ratioConfigs.length; index++) {
      let lower_tick;
      let upper_tick;
      if (params.tick_upper <= params.curr_tick) {
        upper_tick = d11(params.tick_upper).sub(tickWidth.mul(index)).toNumber();
        lower_tick = d11(upper_tick).sub(tickWidth).toNumber();
      } else if (params.tick_lower >= params.curr_tick) {
        lower_tick = d11(params.tick_lower).add(tickWidth.mul(index)).toNumber();
        upper_tick = d11(params.tick_lower).add(tickWidth.mul(index + 1)).toNumber();
      } else if (index === 4) {
        lower_tick = d11(params.tick_lower).add(tickWidth.mul(index)).toNumber();
        upper_tick = d11(lower_tick).add(tickWidth.mul(2)).toNumber();
      } else if (index > 4) {
        lower_tick = d11(params.tick_lower).add(tickWidth.mul(index + 1)).toNumber();
        upper_tick = d11(lower_tick).add(tickWidth).toNumber();
      } else {
        lower_tick = d11(params.tick_lower).add(tickWidth.mul(index)).toNumber();
        upper_tick = d11(lower_tick).add(tickWidth).toNumber();
      }
      last_lower_tick = TickMath2.getInitializableTickIndex(lower_tick, params.tick_spacing);
      last_upper_tick = TickMath2.getInitializableTickIndex(upper_tick, params.tick_spacing);
      tickRangeArray.push({
        tick_lower: last_lower_tick,
        tick_upper: last_upper_tick,
        ratio: ratioConfigs[index],
        liquidity: "0"
      });
    }
    return tickRangeArray;
  }
  static getCreateCurveLiquidityBins(params) {
    const tickWidth = d11(Math.floor(d11(params.tick_upper).sub(params.tick_lower).div(params.div_width).toNumber())).abs();
    const tickRangeArray = [];
    const ratioConfigs = params.ratios;
    for (let index = 0; index < ratioConfigs.length; index++) {
      let lower_tick;
      let upper_tick;
      if (params.tick_upper <= params.curr_tick) {
        upper_tick = params.tick_upper;
        lower_tick = d11(params.tick_upper).sub(tickWidth.mul(index + 1)).toNumber();
      } else if (params.tick_lower >= params.curr_tick) {
        lower_tick = params.tick_lower;
        upper_tick = d11(lower_tick).add(tickWidth.mul(index + 1)).toNumber();
      } else {
        lower_tick = d11(params.curr_tick).sub(tickWidth.mul(index + 1)).toNumber();
        upper_tick = d11(params.curr_tick).add(tickWidth.mul(index + 1)).toNumber();
      }
      tickRangeArray.push({
        tick_lower: TickMath2.getInitializableTickIndex(lower_tick, params.tick_spacing),
        tick_upper: TickMath2.getInitializableTickIndex(upper_tick, params.tick_spacing),
        ratio: ratioConfigs[index],
        liquidity: "0"
      });
    }
    return tickRangeArray;
  }
  static calculateAddLiquidityInputs(params) {
    const curSqrtPrice = new BN2(TickMath2.tickIndexToSqrtPriceX64(params.curr_tick));
    const coinRatios = LiquidityStrategyUtils.calculateCoinRatios(params.curr_tick, params.liquidity_bins);
    const liquidity_inputs = params.liquidity_bins.map((bin, index) => {
      const coinRatio = coinRatios[index];
      const amount = d11(params.coin_amount).mul(params.fix_amount_a ? coinRatio.coinRatioA : coinRatio.coinRatioB).toFixed(0).toString();
      try {
        const liquidityInput = ClmmPoolUtil.estLiquidityAndcoinAmountFromOneAmounts(
          bin.tick_lower,
          bin.tick_upper,
          new BN2(amount),
          params.fix_amount_a,
          true,
          params.slippage,
          curSqrtPrice
        );
        return { ...liquidityInput, ...bin };
      } catch (error) {
        return {
          coinAmountA: new BN2(0),
          coinAmountB: new BN2(0),
          tokenMaxA: new BN2(0),
          tokenMaxB: new BN2(0),
          liquidityAmount: new BN2(0),
          fix_amount_a: !params.fix_amount_a,
          ...bin
        };
      }
    });
    return LiquidityStrategyUtils.fixLiquidityInputs(curSqrtPrice, params.slippage, liquidity_inputs, true);
  }
  static fixLiquidityInputs(curSqrtPrice, slippage, liquidity_inputs, roundUp) {
    const filteList = liquidity_inputs.filter((item) => item.liquidityAmount.gt(ZERO));
    if (filteList.length === 0) {
      return [];
    }
    const baseliquidityInputs = filteList[0];
    return liquidity_inputs.map((input) => {
      if (input.liquidityAmount.eq(ZERO)) {
        const fixLiquidityAmount = d11(baseliquidityInputs.liquidityAmount.toString()).mul(input.ratio).div(baseliquidityInputs.ratio).toFixed(0);
        const lowerSqrtPrice = TickMath2.tickIndexToSqrtPriceX64(input.tick_lower);
        const upperSqrtPrice = TickMath2.tickIndexToSqrtPriceX64(input.tick_upper);
        const coinAmounts = ClmmPoolUtil.getCoinAmountFromLiquidity(
          new BN2(fixLiquidityAmount.toString()),
          curSqrtPrice,
          lowerSqrtPrice,
          upperSqrtPrice,
          false
        );
        return {
          coinAmountA: coinAmounts.coinA,
          coinAmountB: coinAmounts.coinB,
          liquidityAmount: new BN2(fixLiquidityAmount.toString()),
          fix_amount_a: input.fix_amount_a,
          ...adjustForCoinSlippage(coinAmounts, Percentage.fromDecimal(d11(slippage).mul(100)), roundUp)
        };
      }
      return input;
    });
  }
  static calculateCoinRatios(curr_tick, liquidityBins) {
    const curSqrtPrice = new BN2(TickMath2.tickIndexToSqrtPriceX64(curr_tick));
    const ratios = [];
    let totalAmountA = d11(0);
    let totalAmountB = d11(0);
    const liquidity_inputs = liquidityBins.map((bin) => {
      const amount = d11(toDecimalsAmount(100, 6).toString()).mul(bin.ratio).toFixed(0).toString();
      const lowerSqrtPrice = TickMath2.tickIndexToSqrtPriceX64(bin.tick_lower);
      const upperSqrtPrice = TickMath2.tickIndexToSqrtPriceX64(bin.tick_upper);
      const coinAmounts = ClmmPoolUtil.getCoinAmountFromLiquidity(new BN2(amount), curSqrtPrice, lowerSqrtPrice, upperSqrtPrice, true);
      totalAmountA = totalAmountA.add(coinAmounts.coinA.toString());
      totalAmountB = totalAmountB.add(coinAmounts.coinB.toString());
      return { ...coinAmounts, liquidity: amount };
    });
    const coinRatioAs = [];
    const coinRatioBs = [];
    liquidity_inputs.forEach((item, index) => {
      const ratio = {
        coinRatioA: totalAmountA.eq(d11(0)) ? 0 : d11(item.coinA.toString()).div(totalAmountA).toNumber(),
        coinRatioB: totalAmountB.eq(d11(0)) ? 0 : d11(item.coinB.toString()).div(totalAmountB).toNumber(),
        liquiditys: item.liquidity,
        tick_lower: liquidityBins[index].tick_lower,
        tick_upper: liquidityBins[index].tick_upper
      };
      ratios.push(ratio);
      coinRatioAs.push(totalAmountA.eq(d11(0)) ? 0 : d11(item.coinA.toString()).div(totalAmountA).toNumber());
      coinRatioBs.push(totalAmountB.eq(d11(0)) ? 0 : d11(item.coinB.toString()).div(totalAmountB).toNumber());
    });
    return ratios;
  }
  static calculateCloseMasterLiquidityInputs(params) {
    const curSqrtPrice = new BN2(TickMath2.tickIndexToSqrtPriceX64(params.curr_tick));
    const liquidity_inputs = params.positions.map((bin) => {
      const liquidityAmount = new BN2(bin.liquidity);
      const lowerSqrtPrice = TickMath2.tickIndexToSqrtPriceX64(bin.tick_lower_index);
      const upperSqrtPrice = TickMath2.tickIndexToSqrtPriceX64(bin.tick_upper_index);
      const coinAmounts = ClmmPoolUtil.getCoinAmountFromLiquidity(liquidityAmount, curSqrtPrice, lowerSqrtPrice, upperSqrtPrice, false);
      const slippageTolerance = Percentage.fromDecimal(d11(params.slippage).mul(100));
      const { tokenMaxA, tokenMaxB } = adjustForCoinSlippage(coinAmounts, slippageTolerance, false);
      const liquidityInput = {
        coinAmountA: coinAmounts.coinA,
        coinAmountB: coinAmounts.coinB,
        tokenMaxA,
        tokenMaxB,
        liquidityAmount,
        fix_amount_a: false
      };
      return liquidityInput;
    });
    return liquidity_inputs;
  }
  static calculateRemoveMasterLiquidityInputs(params) {
    const curSqrtPrice = new BN2(TickMath2.tickIndexToSqrtPriceX64(params.curr_tick));
    const { fix_token } = params;
    if (fix_token) {
      const liquidity_bins = params.positions.map((item, index) => {
        return {
          tick_lower: item.tick_lower_index,
          tick_upper: item.tick_upper_index,
          ratio: params.ratios[index]
        };
      });
      const coinRatios = LiquidityStrategyUtils.calculateCoinRatios(params.curr_tick, liquidity_bins);
      const liquidity_inputs2 = params.positions.map((bin, index) => {
        const coinRatio = coinRatios[index];
        const amount = d11(fix_token.coin_amount).mul(fix_token.fix_amount_a ? coinRatio.coinRatioA : coinRatio.coinRatioB).toFixed(0).toString();
        try {
          const liquidityInput = ClmmPoolUtil.estLiquidityAndcoinAmountFromOneAmounts(
            bin.tick_lower_index,
            bin.tick_upper_index,
            new BN2(amount),
            fix_token.fix_amount_a,
            false,
            params.slippage,
            curSqrtPrice
          );
          return {
            ...liquidityInput,
            ...{
              tick_lower: bin.tick_lower_index,
              tick_upper: bin.tick_upper_index,
              liquidity: liquidityInput.liquidityAmount.toString(),
              ratio: params.ratios[index]
            }
          };
        } catch (error) {
          return {
            coinAmountA: new BN2(0),
            coinAmountB: new BN2(0),
            tokenMaxA: new BN2(0),
            tokenMaxB: new BN2(0),
            liquidityAmount: new BN2(0),
            fix_amount_a: !fix_token.fix_amount_a,
            ...{
              tick_lower: bin.tick_lower_index,
              tick_upper: bin.tick_upper_index,
              liquidity: "0",
              ratio: params.ratios[index]
            }
          };
        }
      });
      return LiquidityStrategyUtils.fixLiquidityInputs(curSqrtPrice, params.slippage, liquidity_inputs2, false);
    }
    const liquidity_inputs = params.positions.map((bin, index) => {
      const ratio = params.ratios[index];
      let liquidityAmount = d11(params.liquidity).mul(ratio).toFixed(0);
      if (d11(liquidityAmount).greaterThan(bin.liquidity)) {
        liquidityAmount = bin.liquidity;
      }
      const lowerSqrtPrice = TickMath2.tickIndexToSqrtPriceX64(bin.tick_lower_index);
      const upperSqrtPrice = TickMath2.tickIndexToSqrtPriceX64(bin.tick_upper_index);
      const coinAmounts = ClmmPoolUtil.getCoinAmountFromLiquidity(
        new BN2(liquidityAmount),
        curSqrtPrice,
        lowerSqrtPrice,
        upperSqrtPrice,
        false
      );
      const slippageTolerance = Percentage.fromDecimal(d11(params.slippage).mul(100));
      const { tokenMaxA, tokenMaxB } = adjustForCoinSlippage(coinAmounts, slippageTolerance, false);
      const liquidityInput = {
        coinAmountA: coinAmounts.coinA,
        coinAmountB: coinAmounts.coinB,
        tokenMaxA,
        tokenMaxB,
        liquidityAmount: new BN2(liquidityAmount),
        fix_amount_a: false
      };
      return liquidityInput;
    });
    return liquidity_inputs;
  }
  static buildMasterPosition(item) {
    const position = {
      coin_type_a: extractStructTagFromType11(item.coin_type_a.fields.name).full_address,
      coin_type_b: extractStructTagFromType11(item.coin_type_b.fields.name).full_address,
      description: item.description,
      id: item.id.id,
      index: item.index,
      liquidity: item.liquidity,
      name: item.name,
      pool: item.pool,
      tick_lower_index: asIntN(BigInt(item.tick_lower_index.fields.bits)),
      tick_upper_index: asIntN(BigInt(item.tick_upper_index.fields.bits)),
      url: item.url
    };
    return position;
  }
  static buildMasterNFT(objects) {
    const fields = getObjectFields9(objects);
    if (fields) {
      const masterNFT = {
        id: fields.id.id,
        pool_id: fields.pool_id,
        index: Number(fields.index),
        strategy: Number(fields.strategy),
        tick_lower_index: asIntN(BigInt(fields.tick_lower_index)),
        tick_upper_index: asIntN(BigInt(fields.tick_upper_index)),
        positions: [],
        ...buildNFT2(objects)
      };
      masterNFT.positions = fields.positions.map((item) => {
        return LiquidityStrategyUtils.buildMasterPosition(item.fields);
      });
      return masterNFT;
    }
    return void 0;
  }
};

// src/utils/vaults.ts
import { TransactionBlock as TransactionBlock5 } from "@mysten/sui.js/transactions";
import {
  ClmmPoolUtil as ClmmPoolUtil2,
  GAS_TYPE_ARG,
  TickMath as TickMath3,
  TransactionUtil as TransactionUtil3,
  asIntN as asIntN2,
  d as d12,
  extractStructTagFromType as extractStructTagFromType12,
  getObjectFields as getObjectFields10,
  getObjectType as getObjectType3
} from "@cetusprotocol/cetus-sui-clmm-sdk";
import Decimal4 from "decimal.js";
import BN3 from "bn.js";
var VaultsUtils = class {
  static generateNextTickRange(curr_index, span, tick_spacing) {
    const lower_index = curr_index - span / 2;
    const upper_index = curr_index + span / 2;
    return {
      new_tick_lower: VaultsUtils.getValidTickIndex(lower_index, tick_spacing),
      new_tick_upper: VaultsUtils.getValidTickIndex(upper_index, tick_spacing)
    };
  }
  static calculateDepositRatio(lowerTick, upperTick, curSqrtPrice) {
    const currentTick = TickMath3.sqrtPriceX64ToTickIndex(curSqrtPrice);
    if (currentTick < lowerTick) {
      return { ratioA: new Decimal4(1), ratioB: new Decimal4(0) };
    }
    if (currentTick > upperTick) {
      return { ratioA: new Decimal4(0), ratioB: new Decimal4(1) };
    }
    const coinAmountA = new BN3(1e8);
    const { coinAmountB } = ClmmPoolUtil2.estLiquidityAndcoinAmountFromOneAmounts(
      lowerTick,
      upperTick,
      coinAmountA,
      true,
      true,
      0,
      curSqrtPrice
    );
    const currPrice = TickMath3.sqrtPriceX64ToPrice(curSqrtPrice, 0, 0);
    const transformAmountB = d12(coinAmountA.toString()).mul(currPrice);
    const totalAmount = transformAmountB.add(coinAmountB.toString());
    const ratioA = transformAmountB.div(totalAmount);
    const ratioB = d12(coinAmountB.toString()).div(totalAmount);
    return { ratioA, ratioB };
  }
  static getValidTickIndex(tickIndex, tickSpacing) {
    if (tickIndex % tickSpacing === 0) {
      return tickIndex;
    }
    let res;
    if (tickIndex > 0) {
      res = tickIndex - tickIndex % tickSpacing + tickSpacing;
    } else if (tickIndex < 0) {
      res = tickIndex + Math.abs(tickIndex) % tickSpacing - tickSpacing;
    } else {
      res = tickIndex;
    }
    if (res % tickSpacing !== 0) {
      throw new Error("Assertion failed: res % tickSpacing == 0");
    }
    if (Math.abs(res) < Math.abs(tickIndex)) {
      throw new Error("Assertion failed: res.abs() >= tickIndex");
    }
    return res;
  }
  /**
   * lp_amount = (total_lp_amount * delta_liquidity) / total_liquidity_in_vault
   * @param total_amount
   * @param current_liquidity
   * @param total_liquidity
   */
  static get_lp_amount_by_liquidity(vault, current_liquidity) {
    if (vault.total_supply === "0") {
      return "0";
    }
    return d12(vault.total_supply).mul(current_liquidity).div(vault.liquidity).toFixed(0, Decimal4.ROUND_DOWN).toString();
  }
  /**
   * delta_liquidity = (lp_token_amount * total_liquidity_in_vault) / total_lp_amount
   * @param vault
   * @param current_amount
   * @returns
   */
  static get_share_liquidity_by_amount(vault, current_amount) {
    if (vault.total_supply === "0") {
      return "0";
    }
    return d12(current_amount).mul(vault.liquidity).div(vault.total_supply).toFixed(0, Decimal4.ROUND_DOWN).toString();
  }
  static get_protocol_fee_amount(vault, amount) {
    return d12(amount).mul(vault.protocol_fee_rate).div(PROTOCOL_FEE_DENOMINATOR).toFixed(0, Decimal4.ROUND_CEIL);
  }
  static buildFramsPositionNFT(fields) {
    const clmmFields = fields.clmm_postion.fields;
    const framsPositinNft = {
      id: fields.id.id,
      url: clmmFields.url,
      pool_id: fields.pool_id,
      coinTypeA: extractStructTagFromType12(clmmFields.coin_type_a.fields.name).full_address,
      coinTypeB: extractStructTagFromType12(clmmFields.coin_type_b.fields.name).full_address,
      description: clmmFields.description,
      name: clmmFields.name,
      liquidity: clmmFields.liquidity,
      clmm_position_id: clmmFields.id.id,
      clmm_pool_id: clmmFields.pool,
      tick_lower_index: asIntN2(BigInt(clmmFields.tick_lower_index.fields.bits)),
      tick_upper_index: asIntN2(BigInt(clmmFields.tick_upper_index.fields.bits)),
      rewards: []
    };
    return framsPositinNft;
  }
  static buildPool(objects) {
    const fields = getObjectFields10(objects);
    const type = getObjectType3(objects);
    const { positions } = fields;
    if (fields && positions.length > 0) {
      const framsPosition = VaultsUtils.buildFramsPositionNFT(positions[0].fields);
      const masterNFT = {
        id: fields.id.id,
        pool_id: fields.pool,
        protocol_fee_rate: fields.protocol_fee_rate,
        is_pause: fields.is_pause,
        harvest_assets: {
          harvest_assets_handle: fields.harvest_assets.fields.id.id,
          size: Number(fields.harvest_assets.fields.size)
        },
        lp_token_type: extractStructTagFromType12(type).type_arguments[0],
        total_supply: fields.lp_token_treasury.fields.total_supply.fields.value,
        liquidity: fields.liquidity,
        max_quota: fields.max_quota,
        status: fields.status === 1 ? "STATUS_RUNNING" /* STATUS_RUNNING */ : "STATUS_REBALANCING" /* STATUS_REBALANCING */,
        quota_based_type: fields.quota_based_type.fields.name,
        position: framsPosition
      };
      return masterNFT;
    }
    return void 0;
  }
  static async getSuiCoin(sdk, amount, tx) {
    const allCoinAsset = await sdk.ClmmSDK.getOwnerCoinAssets(sdk.getVerifySenderAddress(), GAS_TYPE_ARG);
    tx = tx || new TransactionBlock5();
    let suiCoin;
    if (amount > 95e10) {
      const [fisrtCoin, ...otherCoins] = allCoinAsset;
      if (otherCoins.length > 0) {
        tx.mergeCoins(
          tx.object(fisrtCoin.coinObjectId),
          otherCoins.map((coin) => tx.object(coin.coinObjectId))
        );
      }
      suiCoin = tx.splitCoins(tx.object(fisrtCoin.coinObjectId), [amount]);
    } else {
      suiCoin = TransactionUtil3.buildCoinForAmount(tx, allCoinAsset, BigInt(amount), GAS_TYPE_ARG, false, true).targetCoin;
    }
    return suiCoin;
  }
};

// src/utils/frams.ts
import { ClmmPoolUtil as ClmmPoolUtil3, TickMath as TickMath4, asIntN as asIntN3, d as d13, extractStructTagFromType as extractStructTagFromType13, getObjectFields as getObjectFields11 } from "@cetusprotocol/cetus-sui-clmm-sdk";
import BN4 from "bn.js";
var FramsUtils = class {
  static calculatePositionShare(tick_lower, tick_upper, liquidity, price, effective_tick_lower, effective_tick_upper, current_sqrt_price) {
    if (tick_upper <= effective_tick_lower || tick_lower >= effective_tick_upper) {
      return "0";
    }
    let valid_tick_lower = 0;
    if (tick_lower < effective_tick_lower) {
      valid_tick_lower = effective_tick_lower;
    } else {
      valid_tick_lower = tick_lower;
    }
    let valid_tick_upper = 0;
    if (tick_upper < effective_tick_upper) {
      valid_tick_upper = tick_upper;
    } else {
      valid_tick_upper = effective_tick_upper;
    }
    const lowerSqrtPrice = TickMath4.tickIndexToSqrtPriceX64(valid_tick_lower);
    const upperSqrtPrice = TickMath4.tickIndexToSqrtPriceX64(valid_tick_upper);
    const { coinA, coinB } = ClmmPoolUtil3.getCoinAmountFromLiquidity(
      new BN4(liquidity),
      new BN4(current_sqrt_price),
      lowerSqrtPrice,
      upperSqrtPrice,
      true
    );
    return d13(coinA.toString()).mul(price).div(1e12).add(coinB.toString()).toString();
  }
  static buildFramsPool(data) {
    const fields = getObjectFields11(data);
    if (fields) {
      try {
        const rewarders = [];
        fields.rewarders.forEach((item) => {
          rewarders.push({
            reward_coin: extractStructTagFromType13(item.fields.name).full_address,
            last_reward_time: "",
            emission_per_second: "",
            total_allocate_point: "",
            allocate_point: ""
          });
        });
        const framsPool = {
          id: fields.id.id,
          clmm_pool_id: fields.clmm_pool_id,
          effective_tick_lower: asIntN3(BigInt(fields.effective_tick_lower.fields.bits)),
          effective_tick_upper: asIntN3(BigInt(fields.effective_tick_upper.fields.bits)),
          total_share: fields.total_share,
          rewarders,
          positions: {
            positions_handle: fields.positions.fields.id.id,
            size: fields.positions.fields.size
          },
          sqrt_price: fields.sqrt_price
        };
        return framsPool;
      } catch (error) {
        console.log(error);
      }
    }
    return void 0;
  }
  static buildFramsPositionNFT(data) {
    const fields = getObjectFields11(data);
    if (fields) {
      const clmmFields = fields.clmm_postion.fields;
      const framsPositinNft = {
        id: fields.id.id,
        url: clmmFields.url,
        pool_id: fields.pool_id,
        coinTypeA: extractStructTagFromType13(clmmFields.coin_type_a.fields.name).full_address,
        coinTypeB: extractStructTagFromType13(clmmFields.coin_type_b.fields.name).full_address,
        description: clmmFields.description,
        name: clmmFields.name,
        liquidity: clmmFields.liquidity,
        clmm_position_id: clmmFields.id.id,
        clmm_pool_id: clmmFields.pool,
        tick_lower_index: asIntN3(BigInt(clmmFields.tick_lower_index.fields.bits)),
        tick_upper_index: asIntN3(BigInt(clmmFields.tick_upper_index.fields.bits)),
        rewards: []
      };
      return framsPositinNft;
    }
    return void 0;
  }
};

// src/utils/limit_order.ts
import { d as d14, extractStructTagFromType as extractStructTagFromType14, getObjectFields as getObjectFields12, getObjectType as getObjectType4 } from "@cetusprotocol/cetus-sui-clmm-sdk";
var LimitOrderUtils = class {
  static buildOrderLimitEvent(item, filterTypes) {
    const { events } = item;
    const list = [];
    if (events) {
      events.forEach((event) => {
        const type = extractStructTagFromType14(event.type).name;
        if (filterTypes.includes(type)) {
          const info = {
            digest: item.digest,
            type,
            timestampMs: item.timestampMs,
            parsedJson: event.parsedJson
          };
          list.push(info);
        }
      });
    }
    return list;
  }
  static buildLimitOrderInfo(info) {
    const fields = getObjectFields12(info);
    const type = getObjectType4(info);
    if (fields && type) {
      try {
        const typeStruct = extractStructTagFromType14(type);
        const info2 = {
          pay_coin_type: typeStruct.type_arguments[0],
          target_coin_type: typeStruct.type_arguments[1],
          canceled_ts: fields.canceled_ts === "18446744073709551615" ? 0 : Number(fields.canceled_ts),
          id: fields.id.id,
          obtained_amount: fields.obtained_amount,
          owner: fields.owner,
          rate: fields.rate,
          rate_order_indexer_id: fields.rate_order_indexer_id,
          expire_ts: Number(fields.expire_ts),
          total_pay_amount: fields.total_pay_amount,
          pay_balance: fields.pay_balance,
          target_balance: fields.target_balance,
          claimed_amount: fields.claimed_amount,
          status: "Running" /* Running */,
          created_ts: Number(fields.created_ts)
        };
        if (info2.canceled_ts === 0) {
          info2.status = "Running" /* Running */;
          if (d14(info2.pay_balance).eq(0)) {
            info2.status = "Completed" /* Completed */;
          } else if (d14(info2.pay_balance).lessThan(info2.total_pay_amount)) {
            info2.status = "PartialCompleted" /* PartialCompleted */;
          }
        } else {
          info2.status = "Cancelled" /* Cancelled */;
        }
        return info2;
      } catch (error) {
        console.log(error);
      }
    }
    return void 0;
  }
  // static priceToRate(price: string): string {
  //   const exponent = 20
  //   return d(10).pow(exponent).div(price).toFixed(0).toString()
  // }
  // static rateToPrice(rate: string): string {
  //   const exponent = 20
  //   const price = d(10).pow(exponent).div(rate)
  //   return price.toString()
  // }
  static rateToPrice(formatRate, payDecimal, targetDecimal) {
    const exponent = 18 + payDecimal - targetDecimal;
    return d14(10 ** exponent).div(formatRate).toString();
  }
  static priceToRate(price, payDecimal, targetDecimal) {
    const exponent = 18 + payDecimal - targetDecimal;
    return d14(10).pow(exponent).div(d14(price)).toFixed(0).toString();
  }
};

// src/utils/haedal.ts
import { TransactionBlock as TransactionBlock6 } from "@mysten/sui.js/transactions";
import { d as d15, getPackagerConfigs as getPackagerConfigs5 } from "@cetusprotocol/cetus-sui-clmm-sdk";
import { bcs } from "@mysten/sui.js/bcs";
import { normalizeSuiObjectId as normalizeSuiObjectId4 } from "@mysten/sui.js/utils";
var HaedalUtils = class {
  static async getExchangeRateForStake(sdk, shouldRequestStake, swapAmount) {
    const { vaults } = sdk.sdkOptions;
    const { haedal } = getPackagerConfigs5(vaults);
    if (haedal === void 0) {
      throw Error("the haedal config is undefined");
    }
    const { simulationAccount } = sdk.ClmmSDK.sdkOptions;
    const { staking_id } = getPackagerConfigs5(haedal);
    try {
      const tx = new TransactionBlock6();
      if (shouldRequestStake) {
        await this.requestStake(sdk, swapAmount || 1e9, tx);
      } else {
        tx.moveCall({
          target: `${haedal.published_at}::staking::get_exchange_rate`,
          typeArguments: [],
          arguments: [tx.object(staking_id)]
        });
      }
      const res = await sdk.ClmmSDK.fullClient.devInspectTransactionBlock({
        transactionBlock: tx,
        sender: shouldRequestStake ? sdk.ClmmSDK.senderAddress : simulationAccount.address
      });
      console.log("getExchangeRateForStake res: ", res);
      if (shouldRequestStake) {
        const findItem = res.events.find((item) => {
          return item.type.includes("UserStaked");
        });
        const { sui_amount, st_amount } = findItem.parsedJson;
        const rate2 = d15(sui_amount).div(st_amount).toString();
        console.log({ st_amount, sui_amount, rate: rate2 });
        return rate2;
      }
      const returnValues = res.results[0].returnValues[0][0];
      const rate = bcs.de("u64", Uint8Array.from(returnValues)) / 1e6;
      console.log("rate ", bcs.de("u64", Uint8Array.from(returnValues)));
      return rate.toString();
    } catch (error) {
      console.log("getExchangeRateForHaedal", error);
    }
    return "0";
  }
  static async requestStake(sdk, amount, tx) {
    const { vaults } = sdk.sdkOptions;
    tx = tx || new TransactionBlock6();
    const suiCoin = await VaultsUtils.getSuiCoin(sdk, amount, tx);
    tx.moveCall({
      target: `${vaults.config?.haedal?.published_at}::interface::request_stake`,
      typeArguments: [],
      arguments: [
        tx.object(normalizeSuiObjectId4("0x5")),
        tx.object(vaults.config.haedal.config.staking_id),
        suiCoin,
        tx.pure(normalizeSuiObjectId4("0x0"))
      ]
    });
    return tx;
  }
  static requestStakeCoin(sdk, tx, suiCoin) {
    const { haedal } = sdk.sdkOptions.vaults.config;
    const haSuiCoin = tx.moveCall({
      target: `${haedal.published_at}::staking::request_stake_coin`,
      typeArguments: [],
      arguments: [
        tx.object(normalizeSuiObjectId4("0x5")),
        tx.object(haedal.config.staking_id),
        suiCoin,
        tx.pure(normalizeSuiObjectId4("0x0"))
      ]
    });
    return haSuiCoin;
  }
};

// src/utils/volo.ts
import { TransactionBlock as TransactionBlock7 } from "@mysten/sui.js/transactions";
import { d as d16, getPackagerConfigs as getPackagerConfigs6 } from "@cetusprotocol/cetus-sui-clmm-sdk";
import { bcs as bcs2 } from "@mysten/sui.js/bcs";
import { normalizeSuiObjectId as normalizeSuiObjectId5 } from "@mysten/sui.js/utils";
var VoloUtils = class {
  static async getExchangeRateForStake(sdk, shouldRequestStake, swapAmount) {
    const { vaults } = sdk.sdkOptions;
    const { volo } = getPackagerConfigs6(vaults);
    if (volo === void 0) {
      throw Error("the volo config is undefined");
    }
    const { simulationAccount } = sdk.ClmmSDK.sdkOptions;
    const { native_pool, vsui_metadata } = getPackagerConfigs6(volo);
    try {
      const tx = new TransactionBlock7();
      if (shouldRequestStake) {
        await this.requestStake(sdk, swapAmount || 1e9, tx);
      } else {
        tx.moveCall({
          target: `${volo.published_at}::native_pool::get_ratio`,
          typeArguments: [],
          arguments: [tx.object(native_pool), tx.object(vsui_metadata)]
        });
      }
      const res = await sdk.ClmmSDK.fullClient.devInspectTransactionBlock({
        transactionBlock: tx,
        sender: shouldRequestStake ? sdk.ClmmSDK.senderAddress : simulationAccount.address
      });
      if (shouldRequestStake) {
        const findItem = res.events.find((item) => {
          return item.type.includes("StakedEvent");
        });
        const { cert_amount, sui_amount } = findItem.parsedJson;
        return d16(sui_amount).div(cert_amount).toString();
      }
      const returnValues = res.results[0].returnValues[0][0];
      console.log({ returnValues });
      const rate = bcs2.de("u256", Uint8Array.from(returnValues)) / 1e18;
      return d16(1).div(rate).toString();
    } catch (error) {
      console.log("getExchangeRateForHaedal", error);
    }
    return "0";
  }
  static async requestStake(sdk, amount, tx) {
    const { vaults } = sdk.sdkOptions;
    const { volo } = getPackagerConfigs6(vaults);
    if (volo === void 0) {
      throw Error("the volo config is undefined");
    }
    tx = tx || new TransactionBlock7();
    const { native_pool, vsui_metadata } = getPackagerConfigs6(volo);
    const suiCoin = await VaultsUtils.getSuiCoin(sdk, amount, tx);
    tx.moveCall({
      target: `${volo.published_at}::native_pool::stake`,
      typeArguments: [],
      arguments: [tx.object(native_pool), tx.object(vsui_metadata), tx.object(normalizeSuiObjectId5("0x5")), suiCoin]
    });
    return tx;
  }
  static requestStakeCoin(sdk, tx, suiCoin) {
    const { vaults } = sdk.sdkOptions;
    const { volo } = getPackagerConfigs6(vaults);
    if (volo === void 0) {
      throw Error("the volo config is undefined");
    }
    tx = tx || new TransactionBlock7();
    const { native_pool, vsui_metadata } = getPackagerConfigs6(volo);
    const haSuiCoin = tx.moveCall({
      target: `${volo.published_at}::native_pool::stake_non_entry`,
      typeArguments: [],
      arguments: [tx.object(native_pool), tx.object(vsui_metadata), tx.object(normalizeSuiObjectId5("0x5")), suiCoin]
    });
    return haSuiCoin;
  }
};

// src/utils/aftermath.ts
import { TransactionBlock as TransactionBlock8 } from "@mysten/sui.js/transactions";
import { d as d17, getPackagerConfigs as getPackagerConfigs7 } from "@cetusprotocol/cetus-sui-clmm-sdk";
import { bcs as bcs3 } from "@mysten/sui.js/bcs";
import { normalizeSuiObjectId as normalizeSuiObjectId6 } from "@mysten/sui.js/utils";
var AftermathoUtils = class {
  static async getExchangeRateForStake(sdk, shouldRequestStake, swapAmount) {
    const { vaults } = sdk.sdkOptions;
    const { aftermath } = getPackagerConfigs7(vaults);
    if (aftermath === void 0) {
      throw Error("the aftermath config is undefined");
    }
    const { simulationAccount } = sdk.ClmmSDK.sdkOptions;
    const { staked_sui_vault, safe } = getPackagerConfigs7(aftermath);
    try {
      const tx = new TransactionBlock8();
      if (shouldRequestStake) {
        await this.requestStake(sdk, swapAmount || 1e9, tx);
      } else {
        tx.moveCall({
          target: `${aftermath.published_at}::staked_sui_vault::afsui_to_sui_exchange_rate`,
          typeArguments: [],
          arguments: [tx.object(staked_sui_vault), tx.object(safe)]
        });
      }
      const res = await sdk.ClmmSDK.fullClient.devInspectTransactionBlock({
        transactionBlock: tx,
        sender: shouldRequestStake ? sdk.ClmmSDK.senderAddress : simulationAccount.address
      });
      if (shouldRequestStake) {
        const findItem = res.events.find((item) => {
          return item.type.includes("StakedEvent");
        });
        const { afsui_amount, sui_amount } = findItem.parsedJson;
        return d17(sui_amount).div(afsui_amount).toString();
      }
      const returnValues = res.results[0].returnValues[0][0];
      const rate = bcs3.de("u128", Uint8Array.from(returnValues)) / 1e18;
      return rate.toString();
    } catch (error) {
      console.log("getExchangeRateForHaedal", error);
    }
    return "0";
  }
  static async requestStake(sdk, amount, tx) {
    const { vaults } = sdk.sdkOptions;
    const { aftermath } = getPackagerConfigs7(vaults);
    if (aftermath === void 0) {
      throw Error("the aftermath config is undefined");
    }
    tx = tx || new TransactionBlock8();
    const { staked_sui_vault, referral_vault, safe, validator_address } = getPackagerConfigs7(aftermath);
    const suiCoin = await VaultsUtils.getSuiCoin(sdk, amount, tx);
    tx.moveCall({
      target: `${aftermath.published_at}::staked_sui_vault::request_stake_and_keep`,
      typeArguments: [],
      arguments: [
        tx.object(staked_sui_vault),
        tx.object(safe),
        tx.object(normalizeSuiObjectId6("0x5")),
        tx.object(referral_vault),
        suiCoin,
        tx.pure(normalizeSuiObjectId6(validator_address))
      ]
    });
    return tx;
  }
  static requestStakeCoin(sdk, tx, suiCoin) {
    const { vaults } = sdk.sdkOptions;
    const { aftermath } = getPackagerConfigs7(vaults);
    if (aftermath === void 0) {
      throw Error("the aftermath config is undefined");
    }
    tx = tx || new TransactionBlock8();
    const { staked_sui_vault, referral_vault, safe, validator_address } = getPackagerConfigs7(aftermath);
    const haSuiCoin = tx.moveCall({
      target: `${aftermath.published_at}::staked_sui_vault::request_stake`,
      typeArguments: [],
      arguments: [
        tx.object(staked_sui_vault),
        tx.object(safe),
        tx.object(normalizeSuiObjectId6("0x5")),
        tx.object(referral_vault),
        suiCoin,
        tx.pure(normalizeSuiObjectId6(validator_address))
      ]
    });
    return haSuiCoin;
  }
};

// src/modules/idoModule.ts
var IdoModule = class {
  _sdk;
  _cache = {};
  constructor(sdk) {
    this._sdk = sdk;
  }
  get sdk() {
    return this._sdk;
  }
  async getPoolImmutables(paginationArgs = "all", forceRefresh = false) {
    const { package_id } = this._sdk.sdkOptions.ido;
    const cacheKey = `${package_id}_getPoolImmutables`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    const dataPage = {
      data: [],
      hasNextPage: false
    };
    const allPools = [];
    if (cacheData !== void 0) {
      allPools.push(...cacheData);
    }
    if (allPools.length === 0) {
      try {
        const objects = await this._sdk.ClmmSDK.fullClient.queryEventsByPage(
          { MoveEventType: `${package_id}::oversubscribe_pool::CreatePoolEvent` },
          paginationArgs
        );
        objects.data.forEach((object) => {
          const fields = object.parsedJson;
          if (fields) {
            const info = IdoUtil.buildIdoPoolImmutables(fields);
            allPools.push(info);
          }
        });
        dataPage.hasNextPage = objects.hasNextPage;
        dataPage.nextCursor = objects.nextCursor;
        this.updateCache(cacheKey, allPools, cacheTime24h5);
      } catch (error) {
        console.log("getPoolImmutables", error);
      }
    }
    dataPage.data = allPools;
    return dataPage;
  }
  async getPools(assignPools = [], forceRefresh = false) {
    const allPool = [];
    let poolObjectIds = [];
    if (assignPools.length > 0) {
      poolObjectIds = [...assignPools];
    } else {
      const poolImmutables = await this.getPoolImmutables("all", forceRefresh);
      poolImmutables.data.forEach((item) => {
        poolObjectIds.push(item.pool_address);
      });
    }
    const objectDataResponses = await this._sdk.ClmmSDK.fullClient.batchGetObjects(poolObjectIds, { showType: true, showContent: true });
    for (const suiObj of objectDataResponses) {
      const pool = IdoUtil.buildIdoPool(suiObj);
      await IdoUtil.calculatePoolUiPrice(this._sdk, pool);
      allPool.push(pool);
      const cacheKey = `${pool.pool_address}_getPoolObject`;
      this.updateCache(cacheKey, pool, cacheTime24h5);
    }
    return allPool;
  }
  /**
   * Gets a Ido pool.
   *
   * @param {string} poolObjectId The ID of the launchpad pool.
   * @param {boolean} forceRefresh Whether to force a refresh of the cache.
   * @returns {Promise<LaunchpadPool>} A promise that resolves to the launchpad pool.
   */
  async getPool(poolObjectId, forceRefresh = true) {
    const cacheKey = `${poolObjectId}_getPoolObject`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData !== void 0) {
      const pool2 = cacheData;
      return pool2;
    }
    const objects = await this._sdk.ClmmSDK.fullClient.getObject({
      id: poolObjectId,
      options: { showContent: true, showType: true }
    });
    const pool = IdoUtil.buildIdoPool(objects);
    await IdoUtil.calculatePoolUiPrice(this._sdk, pool);
    this.updateCache(cacheKey, pool);
    return pool;
  }
  /**
   * Gets the init factory event for the launchpad.
   *
   * @param {boolean} forceRefresh Whether to force a refresh of the cache.
   * @returns {Promise<LaunchpadInitEvent>} A promise that resolves to the init factory event.
   */
  async getIdoConfigs(forceRefresh = false) {
    const { package_id } = this._sdk.sdkOptions.ido;
    const cacheKey = `${package_id}_getInitEvent`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const packageObject = await this._sdk.ClmmSDK.fullClient.getObject({ id: package_id, options: { showPreviousTransaction: true } });
    const previousTx = getObjectPreviousTransactionDigest2(packageObject);
    const objects = (await this._sdk.ClmmSDK.fullClient.queryEventsByPage({ Transaction: previousTx }))?.data;
    const initEvent = {
      pools_id: "",
      admin_cap_id: "",
      package_version_id: ""
    };
    if (objects.length > 0) {
      objects.forEach((item) => {
        console.log("item: ", item);
        const fields = item.parsedJson;
        if (fields) {
          switch (extractStructTagFromType15(item.type).full_address) {
            case `${package_id}::config::InitConfigEvent`:
              initEvent.admin_cap_id = fields.admin_cap_id;
              initEvent.package_version_id = fields.package_version_id;
              break;
            case `${package_id}::factory::InitFactoryEvent`:
              initEvent.pools_id = fields.pools_id;
              break;
            default:
              break;
          }
        }
      });
      this.updateCache(cacheKey, initEvent, cacheTime24h5);
    }
    return initEvent;
  }
  /**
   * get purchase info
   * @param purchase_manager_handle
   * @param walletAddress
   */
  async getPurchaseInfo(purchase_manager_handle, walletAddress, forceRefresh = true) {
    const cacheKey = `${purchase_manager_handle}_${walletAddress}_getPurchaseInfo`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData) {
      return cacheData;
    }
    try {
      const res = await this._sdk.ClmmSDK.fullClient.getDynamicFieldObject({
        parentId: purchase_manager_handle,
        name: {
          type: "address",
          value: walletAddress
        }
      });
      const info = IdoUtil.buildPurchaseInfo(getObjectFields13(res));
      this.updateCache(cacheKey, info, cacheTime5min5);
      return info;
    } catch (error) {
      return void 0;
    }
  }
  /**
   * Gets the lock NFT for the given NFT ID.
   *
   * @param {SuiObjectIdType} nft_id The ID of the NFT.
   * @param {boolean} forceRefresh Whether to force a refresh of the cache.
   * @returns {Promise<Position | undefined>} A promise that resolves to the lock NFT or undefined if the NFT is not found.
   */
  async getLockNFT(nft_id, forceRefresh = false) {
    const cacheKey = `${nft_id}_getLockNFT`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const objects = await this._sdk.ClmmSDK.fullClient.getObject({
      id: nft_id,
      options: {
        showType: true,
        showContent: true,
        showDisplay: true,
        showOwner: true
      }
    });
    if (objects.error) {
      return void 0;
    }
    return buildPosition3(objects);
  }
  /**
   * Gets a list of lock NFTs for the given pool type and recipient.
   *
   * @param {owner} owner The owner of the lock NFTs.
   * @returns {Promise<IdoNFT[]>} A promise that resolves to an array of lock NFTs.
   */
  async getLockNFTList(owner) {
    const { clmm_pool } = this._sdk.ClmmSDK.sdkOptions;
    const { ido } = this._sdk.sdkOptions;
    const result = [];
    try {
      const objects = (await this._sdk.ClmmSDK.fullClient.getOwnedObjectsByPage(owner, {
        filter: {
          StructType: `${ido.package_id}::lock::LockedNFT<${clmm_pool.package_id}::position::Position>`
        },
        options: {
          showContent: true
        }
      }))?.data;
      for (const object of objects) {
        const fields = getObjectFields13(object);
        if (fields) {
          const nftFields = fields.nft.fields;
          const lockNFT = {
            locked_at: Number(fields.locked_at),
            lock_duration: Number(fields.lock_duration),
            lock_nft_id: fields.id.id,
            type: fields.nft.type,
            ...nftFields
          };
          lockNFT.end_lock_time = lockNFT.locked_at + lockNFT.lock_duration;
          lockNFT.coin_type_a = extractStructTagFromType15(nftFields.coin_type_a.fields.name).full_address;
          lockNFT.coin_type_b = extractStructTagFromType15(nftFields.coin_type_b.fields.name).full_address;
          lockNFT.id = nftFields.id.id;
          lockNFT.tick_lower_index = asIntN4(BigInt(nftFields.tick_lower_index.fields.bits));
          lockNFT.tick_upper_index = asIntN4(BigInt(nftFields.tick_upper_index.fields.bits));
          result.push(lockNFT);
        }
      }
      return result;
    } catch (error) {
      console.log("getLockNFTList:", error);
      return [];
    }
  }
  async creatPool(params) {
    const { ido } = this.sdk.sdkOptions;
    const isReverse = isSortedSymbols(normalizeSuiAddress3(params.coin_type_sale), normalizeSuiAddress3(params.coin_type_raise));
    if (isReverse) {
      params.price_min = d18(1).div(params.price_max).toNumber();
      params.price_max = d18(1).div(params.price_min).toNumber();
    }
    const fixPrice = LauncpadUtil.priceRealToFix(Number(params.price), params.sale_decimals, params.raise_decimals);
    const price_min = TickMath5.priceToSqrtPriceX64(d18(params.price_min), params.sale_decimals, params.raise_decimals).toString();
    const price_max = TickMath5.priceToSqrtPriceX64(d18(params.price_max), params.sale_decimals, params.raise_decimals).toString();
    const tx = new TransactionBlock9();
    const min_sale_amount = d18(params.total_supply).add(d18(params.total_supply).mul(d18(params.liquidity_injection_ratio))).toNumber();
    const coinAssets = await this._sdk.ClmmSDK.getOwnerCoinAssets(this._sdk.ClmmSDK.senderAddress, params.coin_type_sale);
    const primaryCoinInputs = TransactionUtil4.buildCoinForAmount(tx, coinAssets, BigInt(min_sale_amount), params.coin_type_sale, false);
    const args = [
      tx.pure(getPackagerConfigs8(ido).admin_cap_id),
      tx.pure(getPackagerConfigs8(ido).package_version_id),
      tx.pure(getPackagerConfigs8(ido).pools_id),
      tx.pure(normalizeSuiAddress3(params.owner)),
      tx.pure(d18(fixPrice).mul(PRICE_MULTIPLICATION).toString()),
      tx.pure(params.total_supply),
      tx.pure(params.complete_raise_limit),
      tx.pure(params.min_purchase_amount),
      tx.pure(params.max_purchase_amount),
      tx.pure(params.start_time),
      tx.pure(params.purchase_duration),
      tx.pure(params.lock_up_duration),
      tx.pure((params.liquidity_injection_ratio * LIQUIDITY_INJECTION_RATIO_DENOM).toString()),
      tx.pure(price_min),
      tx.pure(price_max),
      tx.pure(params.tick_spacing.toString()),
      primaryCoinInputs.targetCoin,
      tx.pure(params.protection_hard_cap.toString()),
      tx.pure(CLOCK_ADDRESS4)
    ];
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    tx.moveCall({
      target: `${ido.published_at}::${IdoFactoryModule}::create_oversubscribe_pool`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  /**
   * Creates a transaction payload for purchasing tokens from a launchpad pool.
   *
   * @param {IdoPurchaseParams} params The parameters for the purchase.
   * @returns {Promise<TransactionBlock>} A promise that resolves to the transaction payload.
   */
  async creatPurchasePayload(params) {
    const { ido } = this.sdk.sdkOptions;
    const tx = new TransactionBlock9();
    const purchaseInfo = await this.getPurchaseInfo(params.purchase_handle, this._sdk.ClmmSDK.senderAddress, false);
    const coinAssets = await this._sdk.ClmmSDK.getOwnerCoinAssets(this._sdk.ClmmSDK.senderAddress, params.coin_type_raise);
    const primaryCoinInputs = TransactionUtil4.buildCoinForAmount(tx, coinAssets, BigInt(params.raise_amount), params.coin_type_raise, false);
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    let args;
    let funName;
    if (purchaseInfo) {
      funName = "purchase";
      args = [
        tx.object(getPackagerConfigs8(ido).package_version_id),
        tx.object(params.pool_address),
        tx.object(purchaseInfo.purchase_certificate_id),
        tx.pure(params.raise_amount.toString()),
        primaryCoinInputs.targetCoin,
        tx.pure(CLOCK_ADDRESS4)
      ];
    } else {
      funName = "create_certificate_and_purchase";
      args = [
        tx.object(getPackagerConfigs8(ido).package_version_id),
        tx.object(params.pool_address),
        tx.pure(params.raise_amount.toString()),
        primaryCoinInputs.targetCoin,
        tx.pure(CLOCK_ADDRESS4)
      ];
    }
    tx.moveCall({
      target: `${ido.published_at}::${IdoPoolModule}::${funName}`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  /**
   * Creates a transaction payload for claiming tokens from a launchpad pool.
   *
   * @param {IdoRedeemParams} params The parameters for the claim.
   * @returns {Promise<TransactionBlock>} A promise that resolves to the transaction payload.
   */
  async redeemPayload(params) {
    const { ido } = this.sdk.sdkOptions;
    const purchaseInfo = await this.getPurchaseInfo(params.purchase_handle, this._sdk.ClmmSDK.senderAddress, false);
    if (purchaseInfo === void 0) {
      throw Error("get purchase certificate fail");
    }
    const tx = new TransactionBlock9();
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    const args = [
      tx.pure(getPackagerConfigs8(ido).package_version_id),
      tx.pure(params.pool_address),
      tx.pure(purchaseInfo.purchase_certificate_id),
      tx.pure(CLOCK_ADDRESS4)
    ];
    tx.moveCall({
      target: `${ido.published_at}::${IdoPoolModule}::redeem`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  /**
   * Creates a transaction payload for settling tokens from a launchpad pool.
   *
   * @param {InjectLiquidityParams} params The parameters for the settlement.
   * @returns {Promise<TransactionBlock>} A promise that resolves to the transaction payload.
   */
  injectLiquidityPayload(params) {
    const { ido } = this.sdk.sdkOptions;
    const { clmm_pool } = this.sdk.ClmmSDK.sdkOptions;
    const tx = new TransactionBlock9();
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    const funName = params.is_reverse ? "inject_liquidity_with_reverse_clmm_pool" : "inject_liquidity_with_forward_clmm_pool";
    const args = [
      tx.pure(getPackagerConfigs8(ido).package_version_id),
      tx.pure(params.pool_address),
      tx.pure(getPackagerConfigs8(clmm_pool).global_config_id),
      tx.pure(params.clmm_pool_address),
      tx.pure(CLOCK_ADDRESS4)
    ];
    tx.moveCall({
      target: `${ido.published_at}::${IdoPoolModule}::${funName}`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  unPauseInjectLiquidity(params) {
    const { ido } = this.sdk.sdkOptions;
    const { integrate, clmm_pool } = this.sdk.ClmmSDK.sdkOptions;
    const tx = new TransactionBlock9();
    tx.moveCall({
      target: `${integrate.published_at}::${ClmmIntegratePoolModule}::unpause_pool`,
      typeArguments: [params.coin_type_a, params.coin_type_b],
      arguments: [tx.object(getPackagerConfigs8(clmm_pool).global_config_id), tx.object(params.clmm_pool_address)]
    });
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    const funName = params.is_reverse ? "inject_liquidity_with_reverse_clmm_pool" : "inject_liquidity_with_forward_clmm_pool";
    const args = [
      tx.object(getPackagerConfigs8(ido).package_version_id),
      tx.object(params.pool_address),
      tx.object(getPackagerConfigs8(clmm_pool).global_config_id),
      tx.object(params.clmm_pool_address),
      tx.object(CLOCK_ADDRESS4)
    ];
    tx.moveCall({
      target: `${ido.published_at}::${IdoPoolModule}::${funName}`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  async findClmmPoolForLiquidity(pool) {
    let clmmPool = null;
    let is_reverse = false;
    const clmmImmutables = await this.sdk.ClmmSDK.Pool.getPoolImmutables();
    for (const item of clmmImmutables) {
      if (item.coinTypeA === pool.coin_type_sale && item.coinTypeB === pool.coin_type_raise && Number(item.tickSpacing) === pool.tick_spacing) {
        clmmPool = await this._sdk.ClmmSDK.Pool.getPool(item.poolAddress);
        break;
      }
      if (item.coinTypeA === pool.coin_type_raise && item.coinTypeB === pool.coin_type_sale && Number(item.tickSpacing) === pool.tick_spacing) {
        clmmPool = await this._sdk.ClmmSDK.Pool.getPool(item.poolAddress);
        is_reverse = true;
        break;
      }
    }
    if (clmmPool) {
      if (d18(clmmPool.current_sqrt_price).lessThanOrEqualTo(pool.current_sqrt_price_min) || d18(clmmPool.current_sqrt_price).greaterThanOrEqualTo(pool.current_sqrt_price_max)) {
        console.log("clmmPool: ", clmmPool.poolAddress);
        return { is_reverse, clmmPool: null };
      }
    }
    return { is_reverse, clmmPool };
  }
  /**
   * Creates a transaction payload for withdrawing tokens from a launchpad pool.
   *
   * @param {IdoWithdrawParams} params The parameters for the withdrawal.
   * @returns {TransactionBlock} The transaction payload.
   */
  withdrawPayload(params) {
    const { ido } = this.sdk.sdkOptions;
    const tx = new TransactionBlock9();
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    const args = [tx.object(getPackagerConfigs8(ido).package_version_id), tx.object(params.pool_address), tx.object(CLOCK_ADDRESS4)];
    tx.moveCall({
      target: `${ido.published_at}::${IdoPoolModule}::withdraw`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  /**
   * Creates a transaction payload for updating the whitelist cap.
   *
   * @param {UpdateWhitelistCapParams} params The parameters for the update.
   * @returns {TransactionBlock} The transaction payload.
   */
  updateWhitelistCapPayload(params) {
    const { ido } = this.sdk.sdkOptions;
    const tx = new TransactionBlock9();
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    if (params.add_whites && params.add_whites.addresses.length > 0) {
      tx.moveCall({
        target: `${ido.published_at}::${IdoPoolModule}::add_white_listed_addresses`,
        typeArguments,
        arguments: [
          tx.object(getPackagerConfigs8(ido).admin_cap_id),
          tx.object(getPackagerConfigs8(ido).package_version_id),
          tx.object(params.pool_address),
          tx.makeMoveVec({
            type: "address",
            objects: params.add_whites.addresses.map((arg) => tx.object(normalizeSuiObjectId7(arg)))
          }),
          tx.pure(params.add_whites.protection_limit),
          tx.object(CLOCK_ADDRESS4)
        ]
      });
    }
    if (params.remove_whites && params.remove_whites.length > 0) {
      tx.moveCall({
        target: `${ido.published_at}::${IdoPoolModule}::remove_white_listed_addresses`,
        typeArguments,
        arguments: [
          tx.object(getPackagerConfigs8(ido).admin_cap_id),
          tx.object(getPackagerConfigs8(ido).package_version_id),
          tx.object(params.pool_address),
          tx.makeMoveVec({
            type: "address",
            objects: params.remove_whites.map((arg) => tx.object(normalizeSuiObjectId7(arg)))
          }),
          tx.object(CLOCK_ADDRESS4)
        ]
      });
    }
    if (params.update_whites) {
      params.update_whites.forEach((item) => {
        tx.moveCall({
          target: `${ido.published_at}::${IdoPoolModule}::update_white_listed_address`,
          typeArguments,
          arguments: [
            tx.object(getPackagerConfigs8(ido).admin_cap_id),
            tx.object(getPackagerConfigs8(ido).package_version_id),
            tx.object(params.pool_address),
            tx.object(item.address),
            tx.pure(item.protection_limit),
            tx.object(CLOCK_ADDRESS4)
          ]
        });
      });
    }
    return tx;
  }
  /**
   * Creates a transaction payload for canceling a pool.
   *
   * @param {CancelIdoPoolParams} params The parameters for the cancellation.
   * @returns {TransactionBlock} The transaction payload.
   */
  creatCancelPoolPayload(params) {
    const { ido } = this.sdk.sdkOptions;
    const tx = new TransactionBlock9();
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    const args = [
      tx.pure(getPackagerConfigs8(ido).admin_cap_id),
      tx.pure(getPackagerConfigs8(ido).package_version_id),
      tx.pure(params.pool_address),
      tx.pure(CLOCK_ADDRESS4)
    ];
    tx.moveCall({
      target: `${ido.published_at}::${IdoPoolModule}::cancel`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  /**
   * Update pool owner address
   *
   * @param {UpdatePoolOwnerParams} params The parameters for the update.
   * @returns {TransactionBlock} The transaction payload.
   */
  updatePoolOwnerPayload(params, tx) {
    const { ido } = this.sdk.sdkOptions;
    tx = tx === void 0 ? new TransactionBlock9() : tx;
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    const args = [
      tx.object(getPackagerConfigs8(ido).admin_cap_id),
      tx.object(getPackagerConfigs8(ido).package_version_id),
      tx.object(params.pool_address),
      tx.object(params.new_owner),
      tx.object(CLOCK_ADDRESS4)
    ];
    tx.moveCall({
      target: `${ido.published_at}::${IdoPoolModule}::update_pool_owner`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  /**
   * Creates a transaction payload for updating the pool duration.
   *
   * @param {UpdateIdoPoolDurationParams} params The parameters for the update.
   * @returns {TransactionBlock} The transaction payload.
   */
  updatePoolDuractionPayload(params, tx) {
    const { ido } = this.sdk.sdkOptions;
    tx = tx === void 0 ? new TransactionBlock9() : tx;
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    const args = [
      tx.object(getPackagerConfigs8(ido).admin_cap_id),
      tx.object(getPackagerConfigs8(ido).package_version_id),
      tx.object(params.pool_address),
      tx.pure(params.start_time),
      tx.pure(params.purchase_duration),
      tx.pure(params.lock_duration),
      tx.object(CLOCK_ADDRESS4)
    ];
    tx.moveCall({
      target: `${ido.published_at}::${IdoPoolModule}::update_pool_duration`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  /**
   *  Update white listed protection hard cap
   * @param params
   * @returns
   */
  updateProtectionHardCapPayload(params, tx) {
    const { ido } = this.sdk.sdkOptions;
    tx = tx === void 0 ? new TransactionBlock9() : tx;
    const typeArguments = [params.coin_type_sale, params.coin_type_raise];
    const args = [
      tx.object(getPackagerConfigs8(ido).admin_cap_id),
      tx.object(getPackagerConfigs8(ido).package_version_id),
      tx.object(params.pool_address),
      tx.pure(params.protection_hard_cap),
      tx.object(CLOCK_ADDRESS4)
    ];
    tx.moveCall({
      target: `${ido.published_at}::${IdoPoolModule}::update_protection_hard_cap`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  /**
   * Creates a transaction payload for unlocking an NFT.
   *
   * @param {UnlockIdoNftParams} params The parameters for the unlock.
   * @returns {TransactionBlock} The transaction payload.
   */
  unlockNftPayload(params) {
    const { ido } = this.sdk.sdkOptions;
    const tx = new TransactionBlock9();
    const typeArguments = [params.nft_type];
    const args = [tx.pure(params.lock_nft_id), tx.pure(CLOCK_ADDRESS4)];
    tx.moveCall({
      target: `${ido.published_at}::lock::unlock_nft`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  /**
   * Determines if the given wallet address is the admin cap for the launchpad.
   *
   * @param {SuiObjectIdType} walletAddress The wallet address to check.
   * @returns {Promise<boolean>} A promise that resolves to true if the wallet address is the admin cap, or false otherwise.
   */
  async isAdminCap(walletAddress) {
    const { ido } = this._sdk.sdkOptions;
    const cacheKey = `${ido.package_id}_${walletAddress}_isAdminCap`;
    const cacheData = this.getCache(cacheKey);
    if (cacheData) {
      return cacheData;
    }
    const object = await this._sdk.ClmmSDK.fullClient.getObject({
      id: getPackagerConfigs8(ido).admin_cap_id,
      options: { showType: true, showOwner: true }
    });
    console.log(object);
    const type = getObjectType5(object);
    const owner = getObjectOwner2(object);
    let isAdminCap = false;
    if (owner && type && extractStructTagFromType15(type).source_address === `${ido.package_id}::config::AdminCap`) {
      const addressOwner = owner;
      isAdminCap = normalizeSuiAddress3(addressOwner.AddressOwner) === normalizeSuiAddress3(walletAddress);
    }
    this.updateCache(cacheKey, isAdminCap, cacheTime24h5);
    return isAdminCap;
  }
  async getWhiteProtectionLimit(whitetHandle, walletAddress, forceRefresh = true) {
    const cacheKey = `${whitetHandle}_${walletAddress}_getWhiteProtectionLimit`;
    const cacheData = this._sdk.getCache(cacheKey, forceRefresh);
    if (cacheData) {
      return cacheData;
    }
    try {
      const res = await this._sdk.ClmmSDK.fullClient.getDynamicFieldObject({
        parentId: whitetHandle,
        name: {
          type: "address",
          value: walletAddress
        }
      });
      if (res && res.data) {
        const fields = getObjectFields13(res);
        this._sdk.updateCache(cacheKey, fields.value);
        return fields.value;
      }
      return void 0;
    } catch (error) {
      return void 0;
    }
  }
  /**
   * Gets the purchase amount for the given wallet address.
   *
   * @param {SuiObjectIdType} purchaseHandle The handle of the purchase object.
   * @param {SuiObjectIdType} walletAddress The wallet address to get the purchase amount for.
   * @returns {Promise<{ safe_limit_amount: string; safe_purchased_amount: string }>} A promise that resolves to an object with the purchase amount fields.
   */
  async getWhiteList(purchaseHandle, walletAddress) {
    const name = {
      type: "address",
      value: walletAddress
    };
    try {
      const result = await this._sdk.ClmmSDK.fullClient.getDynamicFieldObject({ parentId: purchaseHandle, name });
      const fields = getObjectFields13(result);
      console.log(fields);
      if (fields) {
        return fields?.value?.fields;
      }
    } catch (error) {
    }
    return { safe_limit_amount: "0", safe_purchased_amount: "0" };
  }
  /**
   * get admin address
   * @returns
   */
  async getAdminAddress() {
    const { ido } = this._sdk.sdkOptions;
    const cacheKey = `${getPackagerConfigs8(ido).admin_cap_id}_getAdminAddress`;
    const cacheData = this.getCache(cacheKey);
    if (cacheData) {
      return cacheData;
    }
    const object = await this._sdk.ClmmSDK.fullClient.getObject({
      id: getPackagerConfigs8(ido).admin_cap_id,
      options: { showType: true, showOwner: true }
    });
    console.log(object);
    const type = getObjectType5(object);
    const owner = getObjectOwner2(object);
    if (owner && type && extractStructTagFromType15(type).source_address === `${ido.package_id}::config::AdminCap`) {
      const addressOwner = owner;
      const adminAddress = normalizeSuiAddress3(addressOwner.AddressOwner);
      this.updateCache(cacheKey, adminAddress, cacheTime24h5);
      return adminAddress;
    }
    return "";
  }
  updateCache(key, data, time = cacheTime24h5) {
    let cacheData = this._cache[key];
    if (cacheData) {
      cacheData.overdueTime = getFutureTime5(time);
      cacheData.value = data;
    } else {
      cacheData = new CachedContent5(data, getFutureTime5(time));
    }
    this._cache[key] = cacheData;
  }
  getCache(key, forceRefresh = false) {
    const cacheData = this._cache[key];
    if (!forceRefresh && cacheData?.isValid()) {
      return cacheData.value;
    }
    delete this._cache[key];
    return void 0;
  }
};

// src/modules/xtokenModule.ts
import Decimal5 from "decimal.js";
import {
  buildNFT as buildNFT3,
  CachedContent as CachedContent6,
  cacheTime24h as cacheTime24h6,
  cacheTime5min as cacheTime5min6,
  CLOCK_ADDRESS as CLOCK_ADDRESS5,
  d as d19,
  extractStructTagFromType as extractStructTagFromType16,
  getFutureTime as getFutureTime6,
  getMoveObjectType as getMoveObjectType6,
  getObjectFields as getObjectFields14,
  getObjectId as getObjectId4,
  getPackagerConfigs as getPackagerConfigs9,
  TransactionUtil as TransactionUtil5
} from "@cetusprotocol/cetus-sui-clmm-sdk";
import { TransactionBlock as TransactionBlock10 } from "@mysten/sui.js/transactions";
var XTokenModule = class {
  _sdk;
  _cache = {};
  constructor(sdk) {
    this._sdk = sdk;
  }
  get sdk() {
    return this._sdk;
  }
  /**
   * Gets the VeNFT object for the specified account address.
   *
   * @param accountAddress The address of the account that owns the VeNFT object.
   * @param forceRefresh Indicates whether to refresh the cache of the VeNFT object.
   * @returns A Promise that resolves to the VeNFT object or `undefined` if the object is not found.
   */
  async getOwnerVeNFT(accountAddress, forceRefresh = true) {
    const { xtoken } = this.sdk.sdkOptions;
    const cacheKey = `${accountAddress}_getLockUpManagerEvent`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData !== void 0) {
      return cacheData;
    }
    let veNFT;
    const filterType = `${xtoken.package_id}::xtoken::VeNFT`;
    const ownerRes = await this._sdk.ClmmSDK.fullClient.getOwnedObjectsByPage(accountAddress, {
      options: { showType: true, showContent: true, showDisplay: true },
      filter: { StructType: filterType }
    });
    ownerRes.data.forEach((item) => {
      const type = extractStructTagFromType16(getMoveObjectType6(item)).source_address;
      if (type === filterType) {
        if (item.data && item.data.content) {
          const { fields } = item.data.content;
          veNFT = {
            ...buildNFT3(item),
            id: fields.id.id,
            index: fields.index,
            type,
            xtoken_balance: fields.xtoken_balance
          };
          this.updateCache(cacheKey, veNFT, cacheTime24h6);
        }
      }
    });
    return veNFT;
  }
  /**
   * Gets the list of LockToken objects owned by the specified account address.
   *
   * @param accountAddress The address of the account that owns the LockToken objects.
   * @returns A Promise that resolves to a list of LockToken objects.
   */
  async getOwnerLockTokens(accountAddress) {
    const { xtoken } = this.sdk.sdkOptions;
    const lockTokens = [];
    const filterType = `${xtoken.package_id}::lock_coin::LockedCoin<${this.buileTokenCoinType()}>`;
    const ownerRes = await this._sdk.ClmmSDK.fullClient.getOwnedObjectsByPage(accountAddress, {
      options: { showType: true, showContent: true },
      filter: { StructType: filterType }
    });
    for (const item of ownerRes.data) {
      const type = extractStructTagFromType16(getMoveObjectType6(item)).source_address;
      if (type === filterType) {
        if (item.data) {
          const lockCetus = XTokenUtil.buildLockToken(item.data.content);
          const info = await this.getLockXToken(lockCetus.id);
          if (info) {
            lockCetus.xtoken_amount = info.xtoken_amount;
          }
          lockTokens.push(lockCetus);
        }
      }
    }
    return lockTokens;
  }
  /**
   * Gets the LockToken object with the specified ID.
   *
   * @param lock_id The ID of the LockToken object.
   * @returns A Promise that resolves to the LockToken object or `undefined` if the object is not found.
   */
  async getLockToken(lock_id) {
    const result = await this._sdk.ClmmSDK.fullClient.getObject({ id: lock_id, options: { showType: true, showContent: true } });
    if (result.data?.content) {
      const lockToken = XTokenUtil.buildLockToken(result.data.content);
      const info = await this.getLockXToken(lockToken.id);
      if (info) {
        lockToken.xtoken_amount = info.xtoken_amount;
      }
      return lockToken;
    }
    return void 0;
  }
  /**
   * Gets the list of Cetus coins owned by the specified account address.
   *
   * @param accountAddress The address of the account that owns the Cetus coins.
   * @returns A Promise that resolves to a list of CoinAsset objects.
   */
  async getOwnerTokenCoins(accountAddress) {
    const coins = await this._sdk.ClmmSDK.getOwnerCoinAssets(accountAddress, this.buileTokenCoinType());
    return coins;
  }
  /**
   * mint venft
   * @returns
   */
  mintVeNFTPayload() {
    const { xtoken } = this.sdk.sdkOptions;
    const tx = new TransactionBlock10();
    tx.moveCall({
      target: `${xtoken.published_at}::${XTokenRouterModule}::mint_venft`,
      typeArguments: [],
      arguments: [tx.pure(getPackagerConfigs9(xtoken).xtoken_manager_id)]
    });
    return tx;
  }
  /**
   * Convert Cetus to Xcetus.
   * @param params
   * @returns
   */
  async convertPayload(params) {
    const { xtoken } = this.sdk.sdkOptions;
    const tx = new TransactionBlock10();
    const coin_type = this.buileTokenCoinType();
    const primaryCoinInputs = await TransactionUtil5.syncBuildCoinInputForAmount(
      this._sdk.ClmmSDK,
      tx,
      BigInt(params.amount),
      coin_type
    );
    if (params.venft_id === void 0) {
      tx.moveCall({
        target: `${xtoken.published_at}::${XTokenRouterModule}::mint_and_convert`,
        typeArguments: [],
        arguments: [
          tx.object(getPackagerConfigs9(xtoken).lock_manager_id),
          tx.object(getPackagerConfigs9(xtoken).xtoken_manager_id),
          primaryCoinInputs,
          tx.pure(params.amount)
        ]
      });
    } else {
      tx.moveCall({
        target: `${xtoken.published_at}::${XTokenRouterModule}::convert`,
        typeArguments: [],
        arguments: [
          tx.object(getPackagerConfigs9(xtoken).lock_manager_id),
          tx.object(getPackagerConfigs9(xtoken).xtoken_manager_id),
          primaryCoinInputs,
          tx.pure(params.amount),
          tx.pure(params.venft_id)
        ]
      });
    }
    return tx;
  }
  /**
   * Convert Xcetus to Cetus, first step is to lock the Cetus for a period.
   * When the time is reach, cetus can be redeem and xtoken will be burned.
   * @param params
   * @returns
   */
  redeemLockPayload(params) {
    const { xtoken } = this.sdk.sdkOptions;
    const tx = new TransactionBlock10();
    tx.moveCall({
      target: `${xtoken.published_at}::${XTokenRouterModule}::redeem_lock`,
      typeArguments: [],
      arguments: [
        tx.pure(getPackagerConfigs9(xtoken).lock_manager_id),
        tx.pure(getPackagerConfigs9(xtoken).xtoken_manager_id),
        tx.pure(params.venft_id),
        tx.pure(params.amount),
        tx.pure(params.lock_day),
        tx.pure(CLOCK_ADDRESS5)
      ]
    });
    return tx;
  }
  /**
   * lock time is reach and the cetus can be redeemed, the xtoken will be burned.
   * @param params
   * @returns
   */
  redeemPayload(params) {
    const { xtoken } = this.sdk.sdkOptions;
    const tx = new TransactionBlock10();
    tx.moveCall({
      target: `${xtoken.published_at}::${XTokenRouterModule}::redeem`,
      typeArguments: [],
      arguments: [
        tx.pure(getPackagerConfigs9(xtoken).lock_manager_id),
        tx.pure(getPackagerConfigs9(xtoken).xtoken_manager_id),
        tx.pure(params.venft_id),
        tx.pure(params.lock_id),
        tx.pure(CLOCK_ADDRESS5)
      ]
    });
    return tx;
  }
  redeemDividendPayload(venft_id, bonus_types) {
    const { xtoken_dividends } = this.sdk.sdkOptions;
    const tx = new TransactionBlock10();
    bonus_types.forEach((coin) => {
      tx.moveCall({
        target: `${xtoken_dividends.published_at}::${XTokenDividendsRouterModule}::redeem`,
        typeArguments: [coin],
        arguments: [tx.object(getPackagerConfigs9(xtoken_dividends).dividend_manager_id), tx.object(venft_id)]
      });
    });
    return tx;
  }
  redeemDividendV2Payload(venft_id, bonus_types, xTokenType) {
    const { xtoken_dividends } = this.sdk.sdkOptions;
    let tx = new TransactionBlock10();
    const xTokenTypeList = bonus_types.filter((coin) => {
      return xTokenType.includes(coin);
    });
    if (xTokenTypeList.length > 0) {
      tx = this.redeemDividendXTokenPayload(venft_id, tx);
    }
    bonus_types.forEach((coin) => {
      if (!xTokenType.includes(coin)) {
        tx.moveCall({
          target: `${xtoken_dividends.published_at}::${XTokenDividendsRouterModule}::redeem_v2`,
          typeArguments: [coin],
          arguments: [tx.object(getPackagerConfigs9(xtoken_dividends).dividend_manager_id), tx.object(venft_id), tx.object(CLOCK_ADDRESS5)]
        });
      }
    });
    return tx;
  }
  redeemDividendXTokenPayload(venft_id, tx) {
    const { xtoken_dividends, xtoken } = this.sdk.sdkOptions;
    const { xtoken_manager_id, lock_manager_id } = getPackagerConfigs9(xtoken);
    const { dividend_manager_id } = getPackagerConfigs9(xtoken_dividends);
    tx = tx === void 0 ? new TransactionBlock10() : tx;
    tx.moveCall({
      target: `${xtoken_dividends.published_at}::${XTokenDividendsRouterModule}::redeem_xtoken`,
      typeArguments: [],
      arguments: [
        tx.object(lock_manager_id),
        tx.object(xtoken_manager_id),
        tx.object(dividend_manager_id),
        tx.object(venft_id),
        tx.object(CLOCK_ADDRESS5)
      ]
    });
    return tx;
  }
  buileTokenCoinType() {
    return `${this.sdk.sdkOptions.token_faucet.package_id}::token::TOKEN`;
  }
  buileXTokenCoinType(packageId = this._sdk.sdkOptions.xtoken.package_id, module = "xtoken", name = "XTOKEN") {
    return `${packageId}::${module}::${name}`;
  }
  /**
   * Cancel the redeem lock, the cetus locked will be return back to the manager and the xtoken will be available again.
   * @param params
   * @returns
   */
  cancelRedeemPayload(params) {
    const { xtoken } = this.sdk.sdkOptions;
    const tx = new TransactionBlock10();
    tx.moveCall({
      target: `${xtoken.published_at}::${XTokenRouterModule}::cancel_redeem_lock`,
      typeArguments: [],
      arguments: [
        tx.pure(getPackagerConfigs9(xtoken).lock_manager_id),
        tx.pure(getPackagerConfigs9(xtoken).xtoken_manager_id),
        tx.pure(params.venft_id),
        tx.pure(params.lock_id),
        tx.pure(CLOCK_ADDRESS5)
      ]
    });
    return tx;
  }
  /**
   * Gets the init factory event.
   *
   * @returns A Promise that resolves to the init factory event.
   */
  async getInitConfigs() {
    const { package_id } = this.sdk.sdkOptions.xtoken;
    const cacheKey = `${package_id}_getInitFactoryEvent`;
    const cacheData = this.getCache(cacheKey);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const initEventObjects = (await this._sdk.ClmmSDK.fullClient.queryEventsByPage({ MoveEventType: `${package_id}::xtoken::InitEvent` }))?.data;
    const initEvent = {
      xtoken_manager_id: "",
      lock_manager_id: "",
      lock_handle_id: ""
    };
    if (initEventObjects.length > 0) {
      initEventObjects.forEach((item) => {
        const fields = item.parsedJson;
        if (fields) {
          initEvent.xtoken_manager_id = fields.xtoken_manager;
        }
      });
    }
    const lockEventObjects = (await this._sdk.ClmmSDK.fullClient.queryEventsByPage({ MoveEventType: `${package_id}::locking::InitializeEvent` }))?.data;
    if (lockEventObjects.length > 0) {
      lockEventObjects.forEach((item) => {
        const fields = item.parsedJson;
        if (fields) {
          initEvent.lock_manager_id = fields.lock_manager;
        }
      });
    }
    initEvent.lock_handle_id = (await this.getLockUpManager(initEvent.lock_manager_id)).lock_infos.lock_handle_id;
    this.updateCache(cacheKey, initEvent, cacheTime24h6);
    return initEvent;
  }
  /**
   * Gets the lock up manager event.
   *
   * @returns A Promise that resolves to the lock up manager event.
   */
  async getLockUpManager(lock_manager_id = getPackagerConfigs9(this.sdk.sdkOptions.xtoken).lock_manager_id, forceRefresh = false) {
    const cacheKey = `${lock_manager_id}_getLockUpManagerEvent`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const lockObject = await this.sdk.ClmmSDK.fullClient.getObject({ id: lock_manager_id, options: { showContent: true } });
    const info = XTokenUtil.buildLockUpManager(getObjectFields14(lockObject));
    this.updateCache(cacheKey, info, cacheTime24h6);
    return info;
  }
  /**
   * Gets the lock info handle.
   *
   * @param lockManagerId The ID of the lock manager.
   * @returns A Promise that resolves to the lock info handle.
   */
  async getLockInfoHandle(lock_manager_id) {
    const cacheKey = `${lock_manager_id}_getLockInfoHandle`;
    const cacheData = this.getCache(cacheKey);
    if (cacheData !== void 0) {
      return cacheData;
    }
    let lockInfoHandle = "";
    const lockObjects = await this.sdk.ClmmSDK.fullClient.getObject({ id: lock_manager_id, options: { showContent: true } });
    const fields = getObjectFields14(lockObjects);
    if (fields) {
      lockInfoHandle = fields.lock_infos.fields.id.id;
      this.updateCache(cacheKey, lockInfoHandle, cacheTime24h6);
    }
    return lockInfoHandle;
  }
  /**
   * Gets the dividend manager event.
   *
   * @returns A Promise that resolves to the dividend manager event.
   */
  async getDividendConfigs() {
    const { package_id } = this.sdk.sdkOptions.xtoken_dividends;
    const cacheKey = `${package_id}_getDividendManagerEvent`;
    const cacheData = this.getCache(cacheKey);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const lockEventObjects = (await this._sdk.ClmmSDK.fullClient.queryEventsByPage({ MoveEventType: `${package_id}::dividend::InitEvent` }))?.data;
    const initEvent = {
      dividend_manager_id: "",
      dividend_admin_id: "",
      dividend_settle_id: ""
    };
    if (lockEventObjects.length > 0) {
      lockEventObjects.forEach((item) => {
        const fields = item.parsedJson;
        if (fields) {
          initEvent.dividend_manager_id = fields.manager_id;
          initEvent.dividend_admin_id = fields.admin_id;
          initEvent.dividend_settle_id = fields.settle_id;
          this.updateCache(cacheKey, initEvent, cacheTime24h6);
        }
      });
    }
    return initEvent;
  }
  /**
   * Gets the dividend manager object.
   *
   * @param forceRefresh Whether to force a refresh of the cache.
   * @returns A Promise that resolves to the dividend manager object.
   */
  async getDividendManager(forceRefresh = false) {
    const { dividend_manager_id } = getPackagerConfigs9(this.sdk.sdkOptions.xtoken_dividends);
    const cacheKey = `${dividend_manager_id}_getDividendManager`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const objects = await this._sdk.ClmmSDK.fullClient.getObject({ id: dividend_manager_id, options: { showContent: true } });
    const fields = getObjectFields14(objects);
    const dividendManager = XTokenUtil.buildDividendManager(fields);
    this.updateCache(cacheKey, dividendManager, cacheTime24h6);
    return dividendManager;
  }
  /**
   * Gets the Xcetus manager object.
   *
   * @returns A Promise that resolves to the Xcetus manager object.
   */
  async getXcetusManager(forceRefresh = true) {
    const { xtoken_manager_id } = getPackagerConfigs9(this.sdk.sdkOptions.xtoken);
    const cacheKey = `${xtoken_manager_id}_getXcetusManager`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData) {
      return cacheData;
    }
    const result = await this._sdk.ClmmSDK.fullClient.getObject({ id: xtoken_manager_id, options: { showContent: true } });
    const fields = getObjectFields14(result);
    const xcetusManager = {
      id: fields.id.id,
      index: Number(fields.index),
      has_venft: {
        handle: fields.has_venft.fields.id.id,
        size: fields.has_venft.fields.size
      },
      nfts: {
        handle: fields.nfts.fields.id.id,
        size: fields.nfts.fields.size
      },
      total_locked: fields.total_locked,
      treasury: fields.treasury.fields.total_supply.fields.value
    };
    this.updateCache(cacheKey, xcetusManager);
    return xcetusManager;
  }
  /**
   * Gets the VeNFT dividend information for the specified VeNFT dividend handle and VeNFT ID.
   *
   * @param venft_dividends_handle The VeNFT dividend handle.
   * @param venft_id The VeNFT ID.
   * @returns A Promise that resolves to the VeNFT dividend information or undefined if an error occurs.
   */
  async getVeNFTDividendInfo(venft_dividends_handle, venft_id) {
    try {
      const venft_dividends = await this._sdk.ClmmSDK.fullClient.getDynamicFieldObject({
        parentId: venft_dividends_handle,
        name: {
          type: "0x2::object::ID",
          value: venft_id
        }
      });
      const fields = getObjectFields14(venft_dividends);
      const veNFTDividendInfo = XTokenUtil.buildVeNFTDividendInfo(fields);
      return veNFTDividendInfo;
    } catch (error) {
      return void 0;
    }
  }
  /**
   * Calculates the redeem number for the specified amount and lock day.
   *
   * @param redeemAmount The amount to redeem.
   * @param lockDay The number of days to lock the amount for.
   * @returns A Promise that resolves to an object with the amount out and percent.
   */
  async redeemNum(redeemAmount, lock_day) {
    if (BigInt(redeemAmount) === BigInt(0)) {
      return { amountOut: "0", percent: "0" };
    }
    const lockUpManager = await this.getLockUpManager();
    const mid = d19(REDEEM_NUM_MULTIPER).mul(d19(lockUpManager.max_lock_day).sub(d19(lock_day))).mul(d19(lockUpManager.max_percent_numerator).sub(d19(lockUpManager.min_percent_numerator))).div(d19(lockUpManager.max_lock_day).sub(d19(lockUpManager.min_lock_day)));
    const percent = d19(REDEEM_NUM_MULTIPER).mul(d19(lockUpManager.max_percent_numerator)).sub(mid).div(d19(EXCHANGE_RATE_MULTIPER)).div(REDEEM_NUM_MULTIPER);
    return { amountOut: d19(percent).mul(d19(redeemAmount)).round().toString(), percent: percent.toString() };
  }
  /**
   * Reverses the redeem number for the specified amount and lock day.
   *
   * @param amount The amount to redeem.
   * @param lockDay The number of days to lock the amount for.
   * @returns A Promise that resolves to an object with the reversed amount and percent.
   */
  async reverseRedeemNum(amount, lock_day) {
    if (BigInt(amount) === BigInt(0)) {
      return { amountOut: "0", percent: "0" };
    }
    const lockUpManager = await this.getLockUpManager();
    const mid = d19(REDEEM_NUM_MULTIPER).mul(d19(lockUpManager.max_lock_day).sub(d19(lock_day))).mul(d19(lockUpManager.max_percent_numerator).sub(d19(lockUpManager.min_percent_numerator))).div(d19(lockUpManager.max_lock_day).sub(d19(lockUpManager.min_lock_day)));
    const percent = d19(REDEEM_NUM_MULTIPER).mul(d19(lockUpManager.max_percent_numerator)).sub(mid).div(d19(EXCHANGE_RATE_MULTIPER)).div(REDEEM_NUM_MULTIPER);
    return { amountOut: d19(amount).div(percent).toFixed(0, Decimal5.ROUND_UP), percent: percent.toString() };
  }
  /**
   * Gets the XCetus amount for the specified lock ID.
   *
   * @param lockId The ID of the lock.
   * @returns A Promise that resolves to the XCetus amount.
   */
  async getLockXToken(lock_id) {
    const { lock_handle_id } = getPackagerConfigs9(this._sdk.sdkOptions.xtoken);
    const cacheKey = `${lock_id}_getXTokenAmount`;
    const cacheData = this.getCache(cacheKey);
    if (cacheData) {
      return cacheData;
    }
    try {
      const response = await this.sdk.ClmmSDK.fullClient.getDynamicFieldObject({
        parentId: lock_handle_id,
        name: {
          type: "0x2::object::ID",
          value: lock_id
        }
      });
      const fields = getObjectFields14(response);
      if (fields) {
        const info = fields.value.fields.value.fields;
        this.updateCache(cacheKey, info, cacheTime24h6);
        return info;
      }
    } catch (error) {
    }
    return void 0;
  }
  /**
   * Gets the amount of XCetus and lock for the specified VENFT.
   *
   * @param nftHandleId The ID of the NFT handle.
   * @param veNftId The ID of the VENFT.
   * @returns A Promise that resolves to an object with the XCetus amount and lock amount.
   */
  async getVeNftAmount(nft_handle_id, ve_nft_id) {
    try {
      const response = await this.sdk.ClmmSDK.fullClient.getDynamicFieldObject({
        parentId: nft_handle_id,
        name: {
          type: "0x2::object::ID",
          value: ve_nft_id
        }
      });
      const fields = getObjectFields14(response);
      if (fields) {
        const { lock_amount, xtoken_amount } = fields.value.fields.value.fields;
        return { lock_amount, xtoken_amount };
      }
    } catch (error) {
    }
    return { lock_amount: "0", xtoken_amount: "0" };
  }
  /**
   * @param phase_handle
   * @param phase
   * @param forceRefresh
   * @returns
   */
  async getPhaseDividendInfo(phase, forceRefresh = false) {
    const dividendManager = await this.getDividendManager();
    const phase_handle = dividendManager.dividends.id;
    const cacheKey = `${phase_handle}_${phase}_getPhaseDividendInfo`;
    const cacheData = this._sdk.getCache(cacheKey, forceRefresh);
    if (cacheData) {
      return cacheData;
    }
    try {
      const res = await this._sdk.ClmmSDK.fullClient.getDynamicFieldObject({
        parentId: phase_handle,
        name: {
          type: "u64",
          value: phase
        }
      });
      const fields = getObjectFields14(res);
      const valueFields = fields.value.fields.value.fields;
      const redeemed_num = valueFields.redeemed_num.fields.contents.map((item) => {
        return {
          name: item.fields.key.fields.name,
          value: item.fields.value
        };
      });
      const bonus_types = valueFields.bonus_types.map((item) => {
        return item.fields.name;
      });
      const bonus = valueFields.bonus.fields.contents.map((item) => {
        return {
          name: item.fields.key.fields.name,
          value: item.fields.value
        };
      });
      const info = {
        id: getObjectId4(res),
        phase: fields.name,
        settled_num: valueFields.settled_num,
        register_time: valueFields.register_time,
        redeemed_num,
        is_settled: valueFields.is_settled,
        bonus_types,
        bonus,
        phase_end_time: ""
      };
      this.updateCache(cacheKey, info);
      return info;
    } catch (error) {
    }
    return void 0;
  }
  updateCache(key, data, time = cacheTime5min6) {
    let cacheData = this._cache[key];
    if (cacheData) {
      cacheData.overdueTime = getFutureTime6(time);
      cacheData.value = data;
    } else {
      cacheData = new CachedContent6(data, getFutureTime6(time));
    }
    this._cache[key] = cacheData;
  }
  getCache(key, forceRefresh = false) {
    const cacheData = this._cache[key];
    if (!forceRefresh && cacheData?.isValid()) {
      return cacheData.value;
    }
    delete this._cache[key];
    return void 0;
  }
};

// src/modules/LiquidityStrategyModule.ts
import { TransactionBlock as TransactionBlock11 } from "@mysten/sui.js/transactions";
import {
  CLOCK_ADDRESS as CLOCK_ADDRESS6,
  CachedContent as CachedContent7,
  ClmmFetcherModule,
  TransactionUtil as TransactionUtil6,
  asUintN,
  cacheTime24h as cacheTime24h7,
  cacheTime5min as cacheTime5min7,
  d as d20,
  extractStructTagFromType as extractStructTagFromType17,
  getFutureTime as getFutureTime7,
  getObjectFields as getObjectFields15,
  getPackagerConfigs as getPackagerConfigs10
} from "@cetusprotocol/cetus-sui-clmm-sdk";
var LiquidityStrategyModule = class {
  _sdk;
  _cache = {};
  constructor(sdk) {
    this._sdk = sdk;
  }
  get sdk() {
    return this._sdk;
  }
  async addMasterLiquidity(params) {
    const { liquidity_stratefy } = this._sdk.sdkOptions;
    const { clmm_pool } = this._sdk.ClmmSDK.sdkOptions;
    const allCoinAsset = await this._sdk.ClmmSDK.getOwnerCoinAssets(this._sdk.ClmmSDK.senderAddress);
    let totalAmountA = d20(0);
    let totalAmountB = d20(0);
    const amounts_a = [];
    const amounts_b = [];
    const tick_lowers = [];
    const tick_uppers = [];
    const fix_amount_a_vec = [];
    params.liquidity_bins.forEach((bins, index) => {
      const amount_a = params.amounts_a[index];
      const amount_b = params.amounts_b[index];
      const fix_amount_a = params.bins_fix_amount_a[index];
      totalAmountA = totalAmountA.add(amount_a);
      totalAmountB = totalAmountB.add(amount_b);
      amounts_a.push(amount_a);
      amounts_b.push(amount_b);
      tick_lowers.push(asUintN(BigInt(bins.tick_lower)).toString());
      tick_uppers.push(asUintN(BigInt(bins.tick_upper)).toString());
      fix_amount_a_vec.push(fix_amount_a);
    });
    const typeArguments = [params.coinTypeA, params.coinTypeB];
    const tx = new TransactionBlock11();
    const primaryCoinAInputs = TransactionUtil6.buildAddLiquidityFixTokenCoinInput(
      tx,
      !params.fix_amount_a,
      totalAmountA.toString(),
      params.slippage,
      params.coinTypeA,
      allCoinAsset,
      false
    )?.targetCoin;
    const primaryCoinBInputs = TransactionUtil6.buildAddLiquidityFixTokenCoinInput(
      tx,
      params.fix_amount_a,
      totalAmountB.toString(),
      params.slippage,
      params.coinTypeB,
      allCoinAsset,
      false
    )?.targetCoin;
    if (isAddMasterLiquidityParams(params)) {
      tx.moveCall({
        target: `${liquidity_stratefy.published_at}::router::increase_liquidity`,
        typeArguments,
        arguments: [
          tx.object(getPackagerConfigs10(liquidity_stratefy).master_manger_id),
          tx.object(params.master_address),
          tx.object(getPackagerConfigs10(clmm_pool).global_config_id),
          tx.object(params.pool_address),
          primaryCoinAInputs,
          primaryCoinBInputs,
          tx.pure(amounts_a.map((item) => tx.pure(item, "u64"))),
          tx.pure(amounts_b.map((item) => tx.pure(item, "u64"))),
          tx.pure(fix_amount_a_vec.map((item) => tx.pure(item, "bool"))),
          // tx.makeMoveVec({ type: 'u64', objects: amounts_a.map((item) => tx.pure(item, 'u64')) }),
          // tx.makeMoveVec({ type: 'u64', objects: amounts_b.map((item) => tx.pure(item, 'u64')) }),
          // tx.makeMoveVec({ type: 'bool', objects: fix_amount_a_vec.map((item) => tx.pure(item, 'bool')) }),
          tx.object(CLOCK_ADDRESS6)
        ]
      });
    } else {
      tx.moveCall({
        target: `${liquidity_stratefy.published_at}::router::create_master_liquidity`,
        typeArguments,
        arguments: [
          tx.object(getPackagerConfigs10(liquidity_stratefy).master_manger_id),
          tx.object(getPackagerConfigs10(clmm_pool).global_config_id),
          tx.object(params.pool_address),
          primaryCoinAInputs,
          primaryCoinBInputs,
          tx.pure(tick_lowers.map((item) => tx.pure(item, "u32"))),
          tx.pure(tick_uppers.map((item) => tx.pure(item, "u32"))),
          tx.pure(amounts_a.map((item) => tx.pure(item, "u64"))),
          tx.pure(amounts_b.map((item) => tx.pure(item, "u64"))),
          tx.pure(fix_amount_a_vec.map((item) => tx.pure(item, "bool"))),
          // tx.makeMoveVec({ type: 'u32', objects: tick_lowers.map((item) => tx.pure(item, 'u32')) }),
          // tx.makeMoveVec({ type: 'u32', objects: tick_uppers.map((item) => tx.pure(item, 'u32')) }),
          // tx.makeMoveVec({ type: 'u64', objects: amounts_a.map((item) => tx.pure(item, 'u64')) }),
          // tx.makeMoveVec({ type: 'u64', objects: amounts_b.map((item) => tx.pure(item, 'u64')) }),
          // tx.makeMoveVec({ type: 'bool', objects: fix_amount_a_vec.map((item) => tx.pure(item, 'bool')) }),
          tx.pure(params.strategy),
          tx.pure(asUintN(BigInt(params.tick_lower)).toString()),
          tx.pure(asUintN(BigInt(params.tick_upper)).toString()),
          tx.object(CLOCK_ADDRESS6)
        ]
      });
    }
    return tx;
  }
  /**
   * Builds the full address of the Position type.
   * @returns The full address of the Position type.
   */
  buildMasterNFTType() {
    return `${this._sdk.sdkOptions.liquidity_stratefy.package_id}::master_liquidity::MasterLiquidity`;
  }
  /**
   * Gets a list of MasterPositions for the given account address.
   * @param accountAddress The account address to get positions for.
   * @param assignPoolIds An array of pool IDs to filter the positions by.
   * @returns array of MasterPosition objects.
   */
  async getMasterNFTList(accountAddress, assignPoolIds = []) {
    const allPosition = [];
    const ownerRes = await this._sdk.ClmmSDK.fullClient.getOwnedObjectsByPage(accountAddress, {
      options: { showType: true, showContent: true, showDisplay: true, showOwner: true },
      filter: { Package: this._sdk.sdkOptions.liquidity_stratefy.package_id }
    });
    const hasAssignPoolIds = assignPoolIds.length > 0;
    for (const item of ownerRes.data) {
      const type = extractStructTagFromType17(item.data.type);
      if (type.full_address === this.buildMasterNFTType()) {
        const masterNFT = LiquidityStrategyUtils.buildMasterNFT(item);
        if (masterNFT) {
          const cacheKey = `${masterNFT.id}_masterNFT`;
          this.updateCache(cacheKey, masterNFT, cacheTime24h7);
          if (hasAssignPoolIds) {
            if (assignPoolIds.includes(masterNFT.pool_id)) {
              allPosition.push(masterNFT);
            }
          } else {
            allPosition.push(masterNFT);
          }
        }
      }
    }
    return allPosition;
  }
  async getMasterNFT(masterId, forceRefresh = false) {
    const cacheKey = `$masterId}_masterNFT`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData) {
      return cacheData;
    }
    try {
      const item = await this._sdk.ClmmSDK.fullClient.getObject({
        id: masterId,
        options: { showType: true, showContent: true, showDisplay: true, showOwner: true }
      });
      const type = extractStructTagFromType17(item.data.type);
      if (type.full_address === this.buildMasterNFTType()) {
        const masterNFT = LiquidityStrategyUtils.buildMasterNFT(item);
        if (masterNFT) {
          this.updateCache(cacheKey, masterNFT, cacheTime24h7);
        }
        return masterNFT;
      }
    } catch (error) {
      console.log(error);
    }
    return void 0;
  }
  async fetchMasterPositionInfos(params) {
    const positionMap = {};
    const { clmm_pool, integrate, simulationAccount } = this.sdk.ClmmSDK.sdkOptions;
    const tx = new TransactionBlock11();
    params.forEach((pool) => {
      pool.position_ids.forEach((pos_id) => {
        const typeArguments = [pool.coinTypeA, pool.coinTypeB];
        const args = [tx.object(getPackagerConfigs10(clmm_pool).global_config_id), tx.object(pool.pool_id), tx.object(pos_id)];
        tx.moveCall({
          target: `${integrate.published_at}::${ClmmFetcherModule}::fetch_position_fees`,
          arguments: args,
          typeArguments
        });
        tx.moveCall({
          target: `${integrate.published_at}::${ClmmFetcherModule}::fetch_position_rewards`,
          arguments: [...args, tx.object(CLOCK_ADDRESS6)],
          typeArguments
        });
      });
    });
    const simulateRes = await this.sdk.ClmmSDK.fullClient.devInspectTransactionBlock({
      transactionBlock: tx,
      sender: simulationAccount.address
    });
    simulateRes.events?.forEach((item) => {
      const { parsedJson } = item;
      const { position_id } = parsedJson;
      let info = positionMap[position_id];
      if (info === void 0) {
        info = {
          position_id,
          feeOwedA: "0",
          feeOwedB: "0",
          rewarders: []
        };
        positionMap[position_id] = info;
      }
      switch (extractStructTagFromType17(item.type).name) {
        case "FetchPositionFeesEvent":
          info.feeOwedA = parsedJson.fee_owned_a;
          info.feeOwedB = parsedJson.fee_owned_b;
          break;
        case "FetchPositionRewardsEvent":
          info.rewarders = parsedJson.data;
          break;
      }
    });
    return positionMap;
  }
  async getLiquidityStrategyConfigs(forceRefresh = false) {
    const { package_id } = this._sdk.sdkOptions.liquidity_stratefy;
    const cacheKey = `${package_id}_getLiquidityStrategyConfigst`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const objects = (await this._sdk.ClmmSDK.fullClient.queryEventsByPage({
      MoveEventType: `${package_id}::master_liquidity::MasterLiquidityManagerCreatedEvent`
    })).data;
    const config = {
      master_manger_id: "",
      master_liquidity_handle: ""
    };
    if (objects.length > 0) {
      for (const item of objects) {
        const fields = item.parsedJson;
        config.master_manger_id = fields.id;
        const masterObj = await this._sdk.ClmmSDK.fullClient.getObject({ id: fields.id, options: { showContent: true } });
        const masterFields = getObjectFields15(masterObj);
        config.master_liquidity_handle = masterFields.position_maps.fields.id.id;
        break;
      }
      this.updateCache(cacheKey, config, cacheTime24h7);
    }
    return config;
  }
  /**
   * Remove liquidity from master liquidity.
   * @param {RemoveLiquidityParams} params
   * @returns {TransactionBlock}
   */
  removeMasterLiquidity(params) {
    const { clmm_pool } = this.sdk.ClmmSDK.sdkOptions;
    const { liquidity_stratefy } = this.sdk.sdkOptions;
    const tx = new TransactionBlock11();
    const typeArguments = [params.coinTypeA, params.coinTypeB];
    this.collectReward(
      {
        coinTypeA: params.coinTypeA,
        coinTypeB: params.coinTypeB,
        collect_fee: false,
        rewarder_coin_types: params.rewarder_coin_types,
        master_address: params.master_address,
        pool_address: params.pool_address
      },
      tx
    );
    const args = [
      tx.object(getPackagerConfigs10(liquidity_stratefy).master_manger_id),
      tx.object(params.pool_address),
      tx.object(params.master_address),
      tx.object(getPackagerConfigs10(clmm_pool).global_config_id),
      tx.pure(params.delta_liquidities.map((item) => tx.pure(item, "u128"))),
      tx.pure(params.min_amounts_a.map((item) => tx.pure(item, "u64"))),
      tx.pure(params.min_amounts_b.map((item) => tx.pure(item, "u64"))),
      // tx.makeMoveVec({ type: 'u128', objects: params.delta_liquidities.map((item) => tx.pure(item, 'u128')) }),
      // tx.makeMoveVec({ type: 'u64', objects: params.min_amounts_a.map((item) => tx.pure(item, 'u64')) }),
      // tx.makeMoveVec({ type: 'u64', objects: params.min_amounts_b.map((item) => tx.pure(item, 'u64')) }),
      tx.object(CLOCK_ADDRESS6)
    ];
    tx.moveCall({
      target: `${liquidity_stratefy.published_at}::${LiquidityStrategyRouterModule}::remove_liquidity`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  /**
   * Close master liquidity and remove all liquidity and collect_reward
   * @param {CloseMasterLiquidityParams} params
   * @returns {TransactionBlock}
   */
  closeMasterLiquidity(params) {
    const { clmm_pool } = this.sdk.ClmmSDK.sdkOptions;
    const { liquidity_stratefy } = this.sdk.sdkOptions;
    const tx = new TransactionBlock11();
    const typeArguments = [params.coinTypeA, params.coinTypeB];
    this.collectReward(
      {
        coinTypeA: params.coinTypeA,
        coinTypeB: params.coinTypeB,
        collect_fee: false,
        rewarder_coin_types: params.rewarder_coin_types,
        master_address: params.master_address,
        pool_address: params.pool_address
      },
      tx
    );
    tx.moveCall({
      target: `${liquidity_stratefy.published_at}::${LiquidityStrategyRouterModule}::close_master_liquidity`,
      typeArguments,
      arguments: [
        tx.object(getPackagerConfigs10(liquidity_stratefy).master_manger_id),
        tx.object(params.master_address),
        tx.object(getPackagerConfigs10(clmm_pool).global_config_id),
        tx.object(params.pool_address),
        tx.pure(params.min_amounts_a.map((item) => tx.pure(item, "u64"))),
        tx.pure(params.min_amounts_b.map((item) => tx.pure(item, "u64"))),
        tx.object(CLOCK_ADDRESS6)
      ]
    });
    return tx;
  }
  /**
   * Collect Fee for master liquidity.
   * @param {CollectFeeParams} params
   * @param {TransactionBlock} tx
   * @returns {TransactionBlock}
   */
  collectFee(params, tx) {
    const { clmm_pool } = this.sdk.ClmmSDK.sdkOptions;
    const { liquidity_stratefy } = this.sdk.sdkOptions;
    tx = tx === void 0 ? new TransactionBlock11() : tx;
    const typeArguments = [params.coinTypeA, params.coinTypeB];
    const args = [
      tx.object(getPackagerConfigs10(liquidity_stratefy).master_manger_id),
      tx.object(params.master_address),
      tx.object(getPackagerConfigs10(clmm_pool).global_config_id),
      tx.object(params.pool_address)
    ];
    tx.moveCall({
      target: `${liquidity_stratefy.published_at}::${LiquidityStrategyRouterModule}::collect_fee`,
      typeArguments,
      arguments: args
    });
    return tx;
  }
  /**
   * Collect Reward for master liquidity
   * @param params
   * @param tx
   * @returns
   */
  collectReward(params, tx) {
    const { clmm_pool } = this.sdk.ClmmSDK.sdkOptions;
    const { liquidity_stratefy } = this.sdk.sdkOptions;
    tx = tx === void 0 ? new TransactionBlock11() : tx;
    if (params.collect_fee) {
      this.collectFee({ ...params }, tx);
    }
    params.rewarder_coin_types.forEach((coinType) => {
      const typeArguments = [params.coinTypeA, params.coinTypeB, coinType];
      if (tx) {
        const args = [
          tx.object(getPackagerConfigs10(liquidity_stratefy).master_manger_id),
          tx.object(params.master_address),
          tx.object(getPackagerConfigs10(clmm_pool).global_config_id),
          tx.object(params.pool_address),
          tx.object(getPackagerConfigs10(clmm_pool).global_vault_id),
          tx.object(CLOCK_ADDRESS6)
        ];
        tx.moveCall({
          target: `${liquidity_stratefy.published_at}::${LiquidityStrategyRouterModule}::collect_reward`,
          typeArguments,
          arguments: args
        });
      }
    });
    return tx;
  }
  /**
   * Updates the cache for the given key.
   * @param {string} key The key of the cache entry to update.
   * @param {SuiResource} data The data to store in the cache.
   * @param {cacheTime5min} time The time in minutes after which the cache entry should expire.
   */
  updateCache(key, data, time = cacheTime5min7) {
    let cacheData = this._cache[key];
    if (cacheData) {
      cacheData.overdueTime = getFutureTime7(time);
      cacheData.value = data;
    } else {
      cacheData = new CachedContent7(data, getFutureTime7(time));
    }
    this._cache[key] = cacheData;
  }
  /**
   * Gets the cache entry for the given key.
   * @param {string} key The key of the cache entry to get.
   * @param {boolean} forceRefresh Whether to force a refresh of the cache entry.
   * @returns The cache entry for the given key, or undefined if the cache entry does not exist or is expired.
   */
  getCache(key, forceRefresh = false) {
    const cacheData = this._cache[key];
    const isValid = cacheData?.isValid();
    if (!forceRefresh && isValid) {
      return cacheData.value;
    }
    if (!isValid) {
      delete this._cache[key];
    }
    return void 0;
  }
};

// src/modules/VaultsModule.ts
import { TransactionBlock as TransactionBlock12 } from "@mysten/sui.js/transactions";
import {
  CLOCK_ADDRESS as CLOCK_ADDRESS7,
  CachedContent as CachedContent8,
  ClmmFetcherModule as ClmmFetcherModule2,
  ClmmIntegrateRouterModule,
  ClmmPoolUtil as ClmmPoolUtil4,
  CoinAssist as CoinAssist2,
  SwapUtils,
  TickMath as TickMath6,
  TransactionUtil as TransactionUtil7,
  cacheTime24h as cacheTime24h8,
  cacheTime5min as cacheTime5min8,
  d as d21,
  extractStructTagFromType as extractStructTagFromType18,
  getFutureTime as getFutureTime8,
  getObjectFields as getObjectFields16,
  getPackagerConfigs as getPackagerConfigs11
} from "@cetusprotocol/cetus-sui-clmm-sdk";
import BN5 from "bn.js";
import Decimal6 from "decimal.js";
var VaultsModule = class {
  _sdk;
  _cache = {};
  constructor(sdk) {
    this._sdk = sdk;
  }
  get sdk() {
    return this._sdk;
  }
  buildCoinInput(tx, coinAssets, coinType, amount, appendCoin) {
    const { selectedCoins, remainingCoins } = CoinAssist2.selectCoinAssetGreaterThanOrEqual(coinAssets, BigInt(amount));
    const selectAmountTotal = CoinAssist2.calculateTotalBalance(selectedCoins);
    if (selectAmountTotal < BigInt(amount)) {
      throw new Error(`The amount(${selectAmountTotal}) is Insufficient balance for ${coinType} , expect ${amount} `);
    }
    const selectedUnusedAmount = selectAmountTotal - BigInt(amount);
    let coinInput;
    if (CoinAssist2.isSuiCoin(coinType)) {
      coinInput = tx.splitCoins(tx.gas, [tx.pure(amount)]);
    } else {
      const [primaryCoinA, ...mergeCoinAs] = selectedCoins.map((item) => item.coinObjectId);
      coinInput = tx.object(primaryCoinA);
      if (mergeCoinAs.length > 0) {
        if (appendCoin) {
          tx.mergeCoins(coinInput, [...mergeCoinAs.map((coin) => tx.object(coin)), appendCoin]);
        } else {
          tx.mergeCoins(coinInput, [...mergeCoinAs.map((coin) => tx.object(coin))]);
        }
      } else if (appendCoin) {
        tx.mergeCoins(coinInput, [appendCoin]);
      }
    }
    return {
      selectedUnusedAmount,
      coinInput,
      remainingCoins
    };
  }
  async deposit(params) {
    console.log("1110 deposit \u53C2\u6570: ", { params });
    const { vaults, frams } = this._sdk.sdkOptions;
    const { clmm_pool } = this._sdk.ClmmSDK.sdkOptions;
    const allCoinAsset = await this._sdk.ClmmSDK.getOwnerCoinAssets(this._sdk.getVerifySenderAddress());
    const tx = new TransactionBlock12();
    let primaryCoinAInputs;
    let primaryCoinBInputs;
    if (params.swapParams) {
      const res = await this.handleDepositSwap(params, tx, allCoinAsset);
      primaryCoinAInputs = res.primaryCoinAInputs;
      primaryCoinBInputs = res.primaryCoinBInputs;
    } else {
      primaryCoinAInputs = TransactionUtil7.buildAddLiquidityFixTokenCoinInput(
        tx,
        !params.fix_amount_a,
        params.amount_a,
        params.slippage,
        params.coinTypeA,
        allCoinAsset,
        false
      )?.targetCoin;
      primaryCoinBInputs = TransactionUtil7.buildAddLiquidityFixTokenCoinInput(
        tx,
        params.fix_amount_a,
        params.amount_b,
        params.slippage,
        params.coinTypeB,
        allCoinAsset,
        false
      )?.targetCoin;
    }
    const vaultsConfigs = getPackagerConfigs11(vaults);
    const framsConfigs = getPackagerConfigs11(frams);
    const clmmPoolConfigs = getPackagerConfigs11(clmm_pool);
    tx.moveCall({
      target: `${vaults.published_at}::${VaultsRouterModule}::deposit`,
      typeArguments: [params.coinTypeA, params.coinTypeB, params.lp_token_type],
      arguments: [
        tx.object(vaultsConfigs.vaults_manager_id),
        tx.object(params.vault_id),
        tx.object(framsConfigs.rewarder_manager_id),
        tx.object(framsConfigs.global_config_id),
        tx.object(params.farming_pool),
        tx.object(clmmPoolConfigs.global_config_id),
        tx.object(params.clmm_pool),
        primaryCoinAInputs,
        primaryCoinBInputs,
        tx.pure(params.amount_a),
        tx.pure(params.amount_b),
        tx.pure(params.fix_amount_a),
        tx.object(CLOCK_ADDRESS7)
      ]
    });
    return tx;
  }
  async handleDepositSwap(params, tx, allCoinAsset) {
    const { clmm_pool, integrate } = this._sdk.ClmmSDK.sdkOptions;
    const { swap_amount, a2b, route_obj, stakeProtocol } = params.swapParams;
    const fromCoinType = a2b ? params.coinTypeA : params.coinTypeB;
    const swapCoinInputFrom = TransactionUtil7.buildCoinForAmount(tx, allCoinAsset, BigInt(swap_amount), fromCoinType, false);
    const selectedUnusedAmount = BigInt(swapCoinInputFrom.tragetCoinAmount) - BigInt(swap_amount);
    let coinABs = [];
    if (stakeProtocol) {
      const haSuiCoin = this.requestStakeCoin(stakeProtocol, tx, swapCoinInputFrom.targetCoin);
      const suiCoin = TransactionUtil7.buildCoinForAmount(tx, swapCoinInputFrom.remainCoins, BigInt(0), fromCoinType, false).targetCoin;
      coinABs = a2b ? [suiCoin, haSuiCoin] : [haSuiCoin, suiCoin];
    } else if (route_obj) {
      const swapCoinInputTo = TransactionUtil7.buildCoinForAmount(tx, allCoinAsset, 0n, a2b ? params.coinTypeB : params.coinTypeA, false);
      const res = await TransactionUtil7.buildAggregatorSwapReturnCoins(
        this._sdk.ClmmSDK,
        route_obj,
        swapCoinInputFrom,
        swapCoinInputTo,
        "",
        params.slippage,
        tx
      );
      coinABs = a2b ? [res.fromCoin, res.toCoin] : [res.toCoin, res.fromCoin];
      console.log("swapCoinInputTo.isMintZeroCoin: ", swapCoinInputTo);
      if (!swapCoinInputTo.isMintZeroCoin && swapCoinInputTo.originalSplitedCoin === void 0) {
        tx.transferObjects([swapCoinInputTo.targetCoin], tx.pure(this._sdk.getVerifySenderAddress()));
      }
    } else {
      const swapCoinInputTo = TransactionUtil7.buildCoinForAmount(tx, allCoinAsset, 0n, a2b ? params.coinTypeB : params.coinTypeA, false);
      const sqrtPriceLimit = SwapUtils.getDefaultSqrtPriceLimit(a2b).toString();
      coinABs = tx.moveCall({
        target: `${integrate.published_at}::${ClmmIntegrateRouterModule}::swap`,
        typeArguments: [params.coinTypeA, params.coinTypeB],
        arguments: [
          tx.object(getPackagerConfigs11(clmm_pool).global_config_id),
          tx.object(params.clmm_pool),
          a2b ? swapCoinInputFrom.targetCoin : swapCoinInputTo.targetCoin,
          a2b ? swapCoinInputTo.targetCoin : swapCoinInputFrom.targetCoin,
          tx.pure(a2b),
          tx.pure(true),
          tx.pure(swap_amount),
          tx.pure(sqrtPriceLimit),
          tx.pure(false),
          tx.object(CLOCK_ADDRESS7)
        ]
      });
    }
    let primaryCoinAInputs;
    let primaryCoinBInputs;
    const coinAObj = coinABs[0];
    const coinBObj = coinABs[1];
    if (a2b) {
      const additionalRequiredAmount = BigInt(params.amount_a) - selectedUnusedAmount;
      if (CoinAssist2.isSuiCoin(fromCoinType)) {
        tx.transferObjects([coinAObj], tx.pure(this._sdk.getVerifySenderAddress()));
        primaryCoinAInputs = tx.splitCoins(tx.gas, [tx.pure(params.amount_a)]);
      } else if (additionalRequiredAmount > 0n) {
        const coinAResult = this.buildCoinInput(tx, swapCoinInputFrom.remainCoins, fromCoinType, additionalRequiredAmount, coinAObj);
        primaryCoinAInputs = coinAResult.coinInput;
      } else {
        primaryCoinAInputs = coinAObj;
      }
      primaryCoinBInputs = coinBObj;
    } else {
      primaryCoinAInputs = coinAObj;
      const additionalRequiredAmount = BigInt(params.amount_b) - selectedUnusedAmount;
      if (CoinAssist2.isSuiCoin(fromCoinType)) {
        tx.transferObjects([coinBObj], tx.pure(this._sdk.getVerifySenderAddress()));
        primaryCoinBInputs = tx.splitCoins(tx.gas, [tx.pure(params.amount_b)]);
      } else if (additionalRequiredAmount > 0n) {
        const coinBResult = this.buildCoinInput(tx, swapCoinInputFrom.remainCoins, fromCoinType, additionalRequiredAmount, coinBObj);
        primaryCoinBInputs = coinBResult.coinInput;
      } else {
        primaryCoinBInputs = coinBObj;
      }
    }
    return { primaryCoinAInputs, primaryCoinBInputs };
  }
  async remove(params) {
    console.log({ params });
    const { vaults, frams } = this.sdk.sdkOptions;
    const { clmm_pool, integrate } = this._sdk.ClmmSDK.sdkOptions;
    const tx = new TransactionBlock12();
    const allCoinAsset = await this._sdk.ClmmSDK.getOwnerCoinAssets(this._sdk.getVerifySenderAddress(), params.lp_token_type);
    const primaryCoinInputs = TransactionUtil7.buildCoinForAmount(
      tx,
      allCoinAsset,
      BigInt(params.lp_token_amount),
      params.lp_token_type,
      false
    ).targetCoin;
    const vaultsConfigs = getPackagerConfigs11(vaults);
    const framsConfigs = getPackagerConfigs11(frams);
    const clmmPoolConfigs = getPackagerConfigs11(clmm_pool);
    const typeArguments = [params.coinTypeA, params.coinTypeB, params.lp_token_type];
    const removeCoinABs = tx.moveCall({
      target: `${vaults.published_at}::${VaultsVaultModule}::remove`,
      typeArguments,
      arguments: [
        tx.object(vaultsConfigs.vaults_manager_id),
        tx.object(params.vault_id),
        tx.object(framsConfigs.rewarder_manager_id),
        tx.object(framsConfigs.global_config_id),
        tx.object(params.farming_pool),
        tx.object(clmmPoolConfigs.global_config_id),
        tx.object(params.clmm_pool),
        primaryCoinInputs,
        tx.pure(params.lp_token_amount),
        tx.pure(params.min_amount_a),
        tx.pure(params.min_amount_b),
        tx.object(CLOCK_ADDRESS7)
      ]
    });
    const reciveCoinA = removeCoinABs[0];
    const reciveCoinB = removeCoinABs[1];
    if (params.swapParams) {
      const { a2b, swap_amount, route_obj } = params.swapParams;
      if (route_obj) {
        const swapCoinInputFrom = {
          targetCoin: a2b ? reciveCoinA : reciveCoinB,
          remainCoins: [],
          isMintZeroCoin: false,
          tragetCoinAmount: ""
        };
        const swapCoinInputTo = {
          targetCoin: a2b ? reciveCoinB : reciveCoinA,
          remainCoins: [],
          isMintZeroCoin: false,
          tragetCoinAmount: ""
        };
        const res = await TransactionUtil7.buildAggregatorSwapReturnCoins(
          this._sdk.ClmmSDK,
          route_obj,
          swapCoinInputFrom,
          swapCoinInputTo,
          "",
          params.slippage,
          tx
        );
        const coinABs = a2b ? [res.fromCoin, res.toCoin] : [res.toCoin, res.fromCoin];
        if (a2b) {
          tx.mergeCoins(coinABs[1], [reciveCoinB]);
        } else {
          tx.mergeCoins(coinABs[0], [reciveCoinA]);
        }
        tx.transferObjects([...coinABs], tx.pure(this._sdk.getVerifySenderAddress()));
      } else {
        const sqrtPriceLimit = SwapUtils.getDefaultSqrtPriceLimit(a2b).toString();
        const coinABs = tx.moveCall({
          target: `${integrate.published_at}::${ClmmIntegrateRouterModule}::swap`,
          typeArguments: [params.coinTypeA, params.coinTypeB],
          arguments: [
            tx.object(getPackagerConfigs11(clmm_pool).global_config_id),
            tx.object(params.clmm_pool),
            reciveCoinA,
            reciveCoinB,
            tx.pure(a2b),
            tx.pure(true),
            tx.pure(swap_amount),
            tx.pure(sqrtPriceLimit),
            tx.pure(true),
            tx.object(CLOCK_ADDRESS7)
          ]
        });
        tx.transferObjects([coinABs[0], coinABs[1]], tx.pure(this._sdk.getVerifySenderAddress()));
      }
    } else {
      tx.transferObjects([reciveCoinA, reciveCoinB], tx.pure(this._sdk.getVerifySenderAddress()));
    }
    return tx;
  }
  async getVaultList(paginationArgs = "all") {
    const { vaults } = this._sdk.sdkOptions;
    const res = await this._sdk.ClmmSDK.fullClient.queryEventsByPage(
      { MoveEventType: `${vaults.package_id}::vaults::CreateEvent` },
      paginationArgs
    );
    const warpIds = res.data.map((item) => item.parsedJson.id);
    const objectList = await this._sdk.ClmmSDK.fullClient.batchGetObjects(warpIds, {
      showType: true,
      showContent: true,
      showDisplay: true,
      showOwner: true
    });
    const poolList = [];
    objectList.forEach((item) => {
      const pool = VaultsUtils.buildPool(item);
      if (pool) {
        pool.stake_protocol = this.findStakeProtocol(pool.position.coinTypeA, pool.position.coinTypeB);
        this.savePoolToCache(pool);
        poolList.push(pool);
      }
    });
    res.data = poolList;
    return res;
  }
  findStakeProtocol(coinTypeA, coinTypeB) {
    const { haedal, volo, aftermath } = getPackagerConfigs11(this._sdk.sdkOptions.vaults);
    const coinTypeAFormat = extractStructTagFromType18(coinTypeA).full_address;
    const coinTypeBFormat = extractStructTagFromType18(coinTypeB).full_address;
    if (!(CoinAssist2.isSuiCoin(coinTypeAFormat) || CoinAssist2.isSuiCoin(coinTypeBFormat))) {
      return void 0;
    }
    if (haedal) {
      const coinType = extractStructTagFromType18(getPackagerConfigs11(haedal).coin_type).full_address;
      if (coinTypeAFormat === coinType || coinTypeBFormat === coinType) {
        return "Haedal" /* Haedal */;
      }
    }
    if (volo) {
      const coinType = extractStructTagFromType18(getPackagerConfigs11(volo).coin_type).full_address;
      if (coinTypeAFormat === coinType || coinTypeBFormat === coinType) {
        return "Volo" /* Volo */;
      }
    }
    if (aftermath) {
      const coinType = extractStructTagFromType18(getPackagerConfigs11(aftermath).coin_type).full_address;
      if (coinTypeAFormat === coinType || coinTypeBFormat === coinType) {
        return "aftermath" /* Aftermath */;
      }
    }
    return void 0;
  }
  savePoolToCache(pool) {
    const cacheKey = `${pool.id}_mirrorPool`;
    this.updateCache(cacheKey, pool, cacheTime24h8);
  }
  readPoolFromCache(id, forceRefresh = false) {
    const cacheKey = `${id}_mirrorPool`;
    return this.getCache(cacheKey, forceRefresh);
  }
  /**
   * 计算最大可用
   */
  async calculateMaxAvailable(params) {
    const { integrate, simulationAccount } = this.sdk.ClmmSDK.sdkOptions;
    const tx = new TransactionBlock12();
    const lowerSqrtPrice = TickMath6.tickIndexToSqrtPriceX64(params.lowerTick);
    const upperSqrtPrice = TickMath6.tickIndexToSqrtPriceX64(params.upperTick);
    const curSqrtPrice = new BN5(params.curSqrtPrice);
    const coinAmounts = ClmmPoolUtil4.getCoinAmountFromLiquidity(
      new BN5(params.input_amount),
      curSqrtPrice,
      lowerSqrtPrice,
      upperSqrtPrice,
      false
    );
    const typeArguments = [params.coinTypeA, params.coinTypeB];
    tx.moveCall({
      target: `${integrate.published_at}::${ClmmFetcherModule2}::calculate_swap_result`,
      arguments: [tx.object(params.clmm_pool), tx.pure(true), tx.pure(true), tx.pure(coinAmounts.coinA.toString())],
      typeArguments
    });
    tx.moveCall({
      target: `${integrate.published_at}::${ClmmFetcherModule2}::calculate_swap_result`,
      arguments: [tx.object(params.clmm_pool), tx.pure(false), tx.pure(true), tx.pure(coinAmounts.coinB.toString())],
      typeArguments
    });
    const simulateRes = await this.sdk.ClmmSDK.fullClient.devInspectTransactionBlock({
      transactionBlock: tx,
      sender: simulationAccount.address
    });
    const valueData = simulateRes.events?.filter((item) => {
      return extractStructTagFromType18(item.type).name === `CalculatedSwapResultEvent`;
    });
    if (valueData.length === 0) {
      return null;
    }
    const dataA2b = valueData[0].parsedJson.data;
    const dataB2a = valueData[1].parsedJson.data;
    const maxAmountB = d21(coinAmounts.coinB.toString()).add(dataA2b.amount_out);
    const maxAmountA = d21(coinAmounts.coinA.toString()).add(dataB2a.amount_out);
    return { maxAmountB, maxAmountA };
  }
  async calculateRemoveSwapAmount(params, range) {
    const ratios = VaultsUtils.calculateDepositRatio(params.lowerTick, params.upperTick, new BN5(params.curSqrtPrice));
    const fixRatio = params.fix_amount_a ? ratios.ratioA : ratios.ratioB;
    console.log({ fixRatio });
    let fixAmount = d21(params.receive_amount).mul(fixRatio);
    let otherSideAmount;
    let liquidity;
    if (params.removeLiquidity) {
      const lowerSqrtPrice = TickMath6.tickIndexToSqrtPriceX64(params.lowerTick);
      const upperSqrtPrice = TickMath6.tickIndexToSqrtPriceX64(params.upperTick);
      const removeParams = ClmmPoolUtil4.getCoinAmountFromLiquidity(
        new BN5(params.removeLiquidity),
        new BN5(params.curSqrtPrice),
        lowerSqrtPrice,
        upperSqrtPrice,
        false
      );
      liquidity = params.removeLiquidity;
      otherSideAmount = params.fix_amount_a ? removeParams.coinB.toString() : removeParams.coinA.toString();
      fixAmount = params.fix_amount_a ? d21(removeParams.coinA.toString()) : d21(removeParams.coinB.toString());
    } else {
      const removeParams = ClmmPoolUtil4.estLiquidityAndcoinAmountFromOneAmounts(
        params.lowerTick,
        params.upperTick,
        new BN5(fixAmount.toFixed(0)),
        params.fix_amount_a,
        false,
        0.2,
        new BN5(params.curSqrtPrice)
      );
      liquidity = removeParams.liquidityAmount.toString();
      otherSideAmount = params.fix_amount_a ? removeParams.coinAmountB.toString() : removeParams.coinAmountA.toString();
    }
    console.log({ fixAmount, otherSideAmount });
    const a2b = !params.fix_amount_a;
    const data = await this.clmmPreSwap(
      params.clmm_pool,
      params.curSqrtPrice,
      a2b,
      d21(otherSideAmount),
      params.coinTypeA,
      params.coinTypeB,
      true,
      params.priceSplitPoint,
      params.use_route,
      [
        {
          pool_id: params.clmm_pool,
          tick_lower: params.lowerTick,
          tick_upper: params.upperTick,
          delta_liquidity: Number(liquidity),
          is_increase: false
        }
      ]
    );
    console.log({ data, range });
    const rclAmount = fixAmount.add(data.amount_out);
    const expectAmount = d21(params.receive_amount);
    const ramainAmount = expectAmount.sub(rclAmount);
    console.log(`\u5B9E\u9645\u5F97\u5230: ${rclAmount.toString()} , \u671F\u671B\u5F97\u5230: ${params.receive_amount}, \u5269\u4F59: ${ramainAmount.toString()} `);
    if (!params.removeLiquidity || params.removeLiquidity && range) {
      if (ramainAmount.abs().greaterThan(expectAmount.mul(0.01))) {
        const amountInsufficient = rclAmount.lessThan(expectAmount);
        let left;
        let right;
        if (!range) {
          left = amountInsufficient ? d21(liquidity) : d21(0);
          right = amountInsufficient ? d21(params.maxLiquidity) : d21(liquidity);
        } else if (amountInsufficient) {
          left = d21(liquidity);
          right = range.right;
        } else {
          left = range.left;
          right = d21(liquidity);
        }
        const midLiquidity = d21(left).add(right).div(2).toFixed(0);
        console.log({ left: left.toString(), right: right.toString(), midLiquidity: midLiquidity.toString() });
        if (!range || range && range.count < 15 && left < right) {
          const swapResut = await this.calculateRemoveSwapAmount(
            {
              ...params,
              removeLiquidity: midLiquidity
            },
            {
              left,
              right,
              count: range ? range.count + 1 : 0
            }
          );
          return swapResut;
        }
      }
    }
    const swapInAmount = data.amount_in && data.fee_amount ? new BN5(data.amount_in).add(new BN5(data.fee_amount)).toString() : data.amount_in;
    const swapOutAmount = data.amount_out;
    const { is_exceed } = data;
    return { swapInAmount, swapOutAmount, liquidity, is_exceed, routeObj: data.route_obj, request_id: params.request_id };
  }
  /**
   * 只支持固定输入sui
   * @param params
   */
  async calculateStakeDepositFixSui(params) {
    const remainSuiLimit = params.inputSuiAmount.mul(params.remainRate);
    const remainSui = params.inputSuiAmount.sub(params.swapSuiAmount);
    const exchangeRate = params.exchangeRate ? params.exchangeRate : await this.getExchangeRateForStake(params.stakeProtocol, params.shouldRequestStake, Number(params.swapSuiAmount.toFixed(0)));
    const hasuiAmount = params.swapSuiAmount.div(exchangeRate).toFixed(0);
    console.log({
      swapAmount: params.swapSuiAmount.toString(),
      hasuiAmount: hasuiAmount.toString(),
      leftSuiAmount: params.leftSuiAmount.toString(),
      rightSuiAmount: params.rightSuiAmount.toString(),
      exchangeRate
    });
    const liquidityInput = ClmmPoolUtil4.estLiquidityAndcoinAmountFromOneAmounts(
      params.lowerTick,
      params.upperTick,
      new BN5(hasuiAmount.toString()),
      !params.fixCoinA,
      true,
      0,
      new BN5(params.curSqrtPrice)
    );
    const useSuiAmount = params.fixCoinA ? liquidityInput.coinAmountA.toString() : liquidityInput.coinAmountB.toString();
    const actRemainSui = d21(remainSui).sub(useSuiAmount);
    console.log({
      useSuiAmount: useSuiAmount.toString(),
      hasuiAmount: hasuiAmount.toString(),
      remainSui: remainSui.toString(),
      remainSuiLimit: remainSuiLimit.toString(),
      actRemainSui: actRemainSui.toString(),
      rebalanceCount: params.rebalanceCount
    });
    if (actRemainSui.greaterThanOrEqualTo(0) && actRemainSui.lessThanOrEqualTo(remainSuiLimit) || params.rebalanceCount > 12 || params.leftSuiAmount.greaterThanOrEqualTo(params.rightSuiAmount)) {
      return {
        swapInAmount: params.swapSuiAmount.toFixed(0),
        swapOutAmount: hasuiAmount,
        afterSqrtPrice: params.curSqrtPrice,
        fixAmountA: !params.fixCoinA,
        is_exceed: true,
        request_id: "",
        stake_protocol: params.stakeProtocol
      };
    }
    if (actRemainSui.lessThan(0)) {
      return await this.calculateStakeDepositFixSui({
        ...params,
        rightSuiAmount: params.swapSuiAmount,
        swapSuiAmount: params.swapSuiAmount.add(params.leftSuiAmount).div(2),
        exchangeRate,
        rebalanceCount: params.rebalanceCount + 1
      });
    }
    if (actRemainSui.greaterThan(remainSuiLimit)) {
      return await this.calculateStakeDepositFixSui({
        ...params,
        leftSuiAmount: params.swapSuiAmount,
        swapSuiAmount: params.swapSuiAmount.add(params.rightSuiAmount).div(2),
        exchangeRate,
        rebalanceCount: params.rebalanceCount + 1
      });
    }
    return null;
  }
  /**
   *
   * @param params
   * @param adjustBestAmount true 强制二分法找到最佳sawp比例  false: 采用池子DepositRatio比例，若价格区间发送变化(双边变单边/单边变双边)，才用二分法找
   * @returns
   */
  async calculateDepositSwapAmount(params, adjustBestAmount = false) {
    const firstTick = TickMath6.sqrtPriceX64ToTickIndex(new BN5(params.curSqrtPrice));
    const { ratioA, ratioB } = VaultsUtils.calculateDepositRatio(params.lowerTick, params.upperTick, new BN5(params.curSqrtPrice));
    console.log("1110 calculateDepositSwapAmount params: ", { ...params, currentTick: firstTick, adjustBestAmount });
    const fixAmount = d21(params.input_amount).mul(params.fix_amount_a ? ratioA : ratioB);
    let swapAmount = d21(params.input_amount).sub(fixAmount);
    console.log("1110 ", { fixAmount: fixAmount.toString(), swapAmount: swapAmount.toString() });
    const a2b = params.fix_amount_a;
    if (swapAmount.toFixed(0) === "0") {
      return null;
    }
    if (params.stakeProtocol) {
      if (CoinAssist2.isSuiCoin(params.coinTypeA) && params.fix_amount_a || CoinAssist2.isSuiCoin(params.coinTypeB) && !params.fix_amount_a) {
        const res = await this.calculateStakeDepositFixSui({
          inputSuiAmount: d21(params.input_amount),
          swapSuiAmount: swapAmount,
          lowerTick: params.lowerTick,
          upperTick: params.upperTick,
          curSqrtPrice: params.curSqrtPrice,
          remainRate: params.remainRate ? params.remainRate : 0.01,
          fixCoinA: params.fix_amount_a,
          rebalanceCount: 0,
          shouldRequestStake: params.shouldRequestStake,
          leftSuiAmount: a2b ? new Decimal6(swapAmount.toFixed(0)) : new Decimal6(0),
          rightSuiAmount: a2b ? new Decimal6(params.input_amount) : new Decimal6(swapAmount.toFixed(0)),
          stakeProtocol: params.stakeProtocol
        });
        if (res) {
          return { ...res, request_id: params.request_id };
        }
        return null;
      }
      throw Error("Only support fix sui");
    }
    const firstTickFlag = firstTick < params.lowerTick || firstTick > params.upperTick;
    const rebalanceParams = {
      clmm_pool: params.clmm_pool,
      curSqrtPrice: params.curSqrtPrice,
      a2b,
      amount_a: a2b ? new Decimal6(params.input_amount) : new Decimal6(0),
      amount_b: a2b ? new Decimal6(0) : new Decimal6(params.input_amount),
      amount_left: a2b ? new Decimal6(swapAmount.toFixed(0)) : new Decimal6(0),
      amount_right: a2b ? new Decimal6(params.input_amount) : new Decimal6(swapAmount.toFixed(0)),
      lowerTick: params.lowerTick,
      upperTick: params.upperTick,
      tick_spacing: 2,
      coinTypeA: params.coinTypeA,
      coinTypeB: params.coinTypeB,
      useRoute: params.use_route,
      remainRate: params.remainRate ? params.remainRate : 0.01,
      priceSplitPoint: params.priceSplitPoint
    };
    const useRebalance = adjustBestAmount && firstTick <= params.upperTick;
    let data = useRebalance ? await this.calculateRebalance(rebalanceParams) : await this.clmmPreSwap(
      params.clmm_pool,
      params.curSqrtPrice,
      a2b,
      swapAmount,
      params.coinTypeA,
      params.coinTypeB,
      true,
      params.priceSplitPoint,
      params.use_route
    );
    let afterSqrtPrice = data.after_sqrt_price;
    const currentTick = TickMath6.sqrtPriceX64ToTickIndex(new BN5(afterSqrtPrice));
    console.log("1110 \u9996\u6B21\u8BA1\u7B97 \u7ED3\u679C: ", { ...data, currentTick }, null, 2);
    const afterTickFlag = currentTick < params.lowerTick || currentTick > params.upperTick;
    if (firstTickFlag !== afterTickFlag && !useRebalance) {
      if (currentTick <= params.upperTick) {
        console.log("1110 \u533A\u95F4\u53D1\u751F\u53D8\u5316, \u518D\u6B21\u8FDB\u884Cswap: ");
        const { ratioA: newAfterRatioA, ratioB: newAfterRatioB } = VaultsUtils.calculateDepositRatio(
          params.lowerTick,
          params.upperTick,
          new BN5(afterSqrtPrice)
        );
        console.log("1110 ", { newAfterRatioA, newAfterRatioB });
        swapAmount = d21(params.input_amount).sub(d21(params.input_amount).mul(params.fix_amount_a ? newAfterRatioA : newAfterRatioB));
        rebalanceParams.amount_left = a2b ? new Decimal6(swapAmount.toFixed(0)) : new Decimal6(0);
        rebalanceParams.amount_right = a2b ? new Decimal6(params.input_amount) : new Decimal6(swapAmount.toFixed(0));
        data = await this.calculateRebalance(rebalanceParams);
        afterSqrtPrice = data.after_sqrt_price;
        const currentTick2 = TickMath6.sqrtPriceX64ToTickIndex(new BN5(afterSqrtPrice));
        console.log("1110 \u518D\u6B21\u8BA1\u7B97\u53D8\u5316\u540E: ", { lowerTick: params.lowerTick, upperTick: params.upperTick, currentTick: currentTick2, afterSqrtPrice });
      }
    }
    const { ratioA: afterRatioA, ratioB: afterRatioB } = VaultsUtils.calculateDepositRatio(
      params.lowerTick,
      params.upperTick,
      new BN5(afterSqrtPrice)
    );
    console.log("1110 1", { afterRatioA, afterRatioB, data });
    let fixAmountA = afterRatioB.div(afterRatioA).sub(ratioB.div(ratioA)).greaterThan("0");
    const swapInAmount = data.amount_in && data.fee_amount ? new BN5(data.amount_in).add(new BN5(data.fee_amount)).toString() : data.amount_in;
    const swapOutAmount = data.amount_out;
    const { is_exceed } = data;
    if (currentTick < params.lowerTick) {
      fixAmountA = true;
    } else if (currentTick > params.upperTick) {
      fixAmountA = false;
    } else {
      const coinAmount = fixAmountA === a2b ? new BN5(d21(params.input_amount).sub(swapInAmount).toString()) : new BN5(swapOutAmount);
      const liquidityInput = ClmmPoolUtil4.estLiquidityAndcoinAmountFromOneAmounts(
        params.lowerTick,
        params.upperTick,
        coinAmount,
        fixAmountA,
        true,
        0,
        new BN5(afterSqrtPrice)
      );
      const amount_a = liquidityInput.coinAmountA;
      const amount_b = liquidityInput.coinAmountB;
      const preAmountTotal = d21(params.fix_amount_a ? amount_a.toString() : amount_b.toString()).add(swapInAmount);
      console.log("1110 \u5B9E\u9645\u6D88\u8017:", { preAmountTotal: preAmountTotal.toString(), fixAmountA });
      if (preAmountTotal.greaterThanOrEqualTo(params.input_amount)) {
        fixAmountA = !fixAmountA;
      }
    }
    const result = {
      swapInAmount,
      swapOutAmount,
      afterSqrtPrice,
      is_exceed,
      fixAmountA,
      routeObj: data.route_obj,
      request_id: params.request_id,
      use_Haedal_stake: false
    };
    console.log(result);
    return result;
  }
  async calculateHaedalDepositSwapAmount(params) {
    const firstTick = TickMath6.sqrtPriceX64ToTickIndex(new BN5(params.curSqrtPrice));
    const { ratioA, ratioB } = VaultsUtils.calculateDepositRatio(params.lowerTick, params.upperTick, new BN5(params.curSqrtPrice));
    console.log("1110 calculateDepositSwapAmount params: ", { ...params, currentTick: firstTick });
    const fixAmount = d21(params.input_amount).mul(params.fix_amount_a ? ratioA : ratioB);
    const swapAmount = d21(params.input_amount).sub(fixAmount);
    console.log("1110 ", { fixAmount: fixAmount.toString(), swapAmount: swapAmount.toString() });
    const a2b = params.fix_amount_a;
    if (swapAmount.toFixed(0) === "0") {
      return null;
    }
    if (params.stakeProtocol) {
      if (CoinAssist2.isSuiCoin(params.coinTypeA) && params.fix_amount_a || CoinAssist2.isSuiCoin(params.coinTypeB) && !params.fix_amount_a) {
        const res = await this.calculateStakeDepositFixSui({
          inputSuiAmount: d21(params.input_amount),
          swapSuiAmount: swapAmount,
          lowerTick: params.lowerTick,
          upperTick: params.upperTick,
          curSqrtPrice: params.curSqrtPrice,
          remainRate: params.remainRate ? params.remainRate : 0.01,
          fixCoinA: params.fix_amount_a,
          rebalanceCount: 0,
          shouldRequestStake: params.shouldRequestStake,
          leftSuiAmount: a2b ? new Decimal6(swapAmount.toFixed(0)) : new Decimal6(0),
          rightSuiAmount: a2b ? new Decimal6(params.input_amount) : new Decimal6(swapAmount.toFixed(0)),
          stakeProtocol: params.stakeProtocol
        });
        if (res) {
          return { ...res, request_id: params.request_id };
        }
        return null;
      }
      throw Error("Only support fix sui");
    }
    return null;
  }
  async calculateRebalance(params) {
    const {
      clmm_pool,
      a2b,
      curSqrtPrice,
      amount_a,
      amount_b,
      lowerTick,
      upperTick,
      amount_left,
      amount_right,
      coinTypeA,
      coinTypeB,
      useRoute,
      priceSplitPoint,
      remainRate
    } = params;
    const calculateRebalanceRecursively = async (left, right, count) => {
      const mid = left.plus(right).div(2);
      const preRes = await this.clmmPreSwap(clmm_pool, curSqrtPrice, a2b, mid, coinTypeA, coinTypeB, true, priceSplitPoint, useRoute);
      if (preRes.amount_out === "0") {
        return preRes;
      }
      if (!preRes.after_sqrt_price) {
        preRes.after_sqrt_price = params.curSqrtPrice;
      }
      if (preRes.is_exceed) {
        right = mid;
        return calculateRebalanceRecursively(left, right, count + 1);
      }
      const afterA = a2b ? amount_a.sub(preRes.amount_in) : amount_a.add(preRes.amount_out);
      const afterB = a2b ? amount_b.add(preRes.amount_out) : amount_b.sub(preRes.amount_in);
      const afterPoolTickIndex = TickMath6.sqrtPriceX64ToTickIndex(new BN5(preRes.after_sqrt_price));
      const liquidityInput = ClmmPoolUtil4.estLiquidityAndcoinAmountFromOneAmounts(
        lowerTick,
        upperTick,
        new BN5(afterA.toString()),
        true,
        true,
        1,
        new BN5(preRes.after_sqrt_price)
      );
      const usedA = new Decimal6(liquidityInput.coinAmountA.toString());
      const usedB = new Decimal6(liquidityInput.coinAmountB.toString());
      console.log("1110 swap\u9012\u5F52\u8BA1\u7B97 ", {
        preRes,
        left: left.toString(),
        right: right.toString(),
        mid: mid.toString(),
        afterPoolTickIndex,
        afterA: afterA.toString(),
        afterB: afterB.toString(),
        usedA: usedA.toString(),
        usedB: toString(),
        count
      });
      if (usedA.toString() !== afterA.toString()) {
        throw new Error("usedA does not match afterA");
      }
      if (afterB.lessThan(usedB)) {
        if (a2b) {
          left = mid;
        } else {
          right = mid;
        }
      } else {
        if (a2b) {
          right = mid;
        } else {
          left = mid;
        }
        const remainingB = afterB.sub(usedB);
        const maxRemainingB = afterB.mul(remainRate);
        console.log("1110 \u5269\u4F59\u672A\u4F7F\u7528amount ", {
          remainingB: remainingB.toString(),
          maxRemainingB: maxRemainingB.toString(),
          remainRate,
          count
        });
        if (maxRemainingB.sub(remainingB).greaterThanOrEqualTo(0)) {
          console.log("break");
          return { ...preRes, remainingB };
        }
      }
      if (left.greaterThan(right) || right.sub(left).lessThan(10)) {
        return preRes;
      }
      return calculateRebalanceRecursively(left, right, count + 1);
    };
    if (amount_left.greaterThanOrEqualTo(amount_right)) {
      return calculateRebalanceRecursively(new Decimal6(0), amount_left, 0);
    }
    return calculateRebalanceRecursively(amount_left, amount_right, 0);
  }
  // private async clmmPreSwap(clmm_pool: string, a2b: boolean, amount: Decimal, coinTypeA: string, coinTypeB: string) {
  async clmmPreSwap(clmm_pool, curSqrtPrice, a2b, amount, coinTypeA, coinTypeB, byAmountIn, priceSplitPoint, useRoute, lpChanges) {
    if (useRoute) {
      try {
        const res = await this._sdk.ClmmSDK.RouterV2.getBestRouter(
          a2b ? coinTypeA : coinTypeB,
          a2b ? coinTypeB : coinTypeA,
          Number(amount.toFixed(0)),
          byAmountIn,
          priceSplitPoint,
          "",
          void 0,
          void 0,
          true,
          false,
          lpChanges
        );
        if (!res.result) {
          throw new Error("route unavailable");
        }
        const { result } = res;
        let after_sqrt_price = curSqrtPrice;
        result.splitPaths.forEach((splitPath) => {
          const basePath = splitPath.basePaths.find((basePath2) => basePath2.poolAddress.toLowerCase() === clmm_pool.toLowerCase());
          if (basePath) {
            after_sqrt_price = basePath.afterSqrtPrice.toString();
          }
        });
        return {
          amount_in: result.inputAmount.toString(),
          amount_out: result.outputAmount.toString(),
          after_sqrt_price,
          is_exceed: result.isExceed,
          route_obj: result
        };
      } catch (error) {
        throw new Error("route unavailable");
      }
    }
    const { integrate, simulationAccount } = this.sdk.ClmmSDK.sdkOptions;
    const tx = new TransactionBlock12();
    const typeArguments = [coinTypeA, coinTypeB];
    const args = [tx.pure(clmm_pool), tx.pure(a2b), tx.pure(byAmountIn), tx.pure(amount.toFixed(0))];
    tx.moveCall({
      target: `${integrate.published_at}::${ClmmFetcherModule2}::calculate_swap_result`,
      arguments: args,
      typeArguments
    });
    const simulateRes = await this.sdk.ClmmSDK.fullClient.devInspectTransactionBlock({
      transactionBlock: tx,
      sender: simulationAccount.address
    });
    const valueData = simulateRes.events?.filter((item) => {
      return extractStructTagFromType18(item.type).name === `CalculatedSwapResultEvent`;
    });
    if (valueData.length === 0) {
      return null;
    }
    return valueData[0].parsedJson.data;
  }
  async getVault(id, forceRefresh = false) {
    const cachePool = this.readPoolFromCache(id, forceRefresh);
    if (cachePool) {
      return cachePool;
    }
    try {
      const item = await this._sdk.ClmmSDK.fullClient.getObject({
        id,
        options: { showType: true, showContent: true, showDisplay: true, showOwner: true }
      });
      const pool = VaultsUtils.buildPool(item);
      if (pool) {
        pool.stake_protocol = this.findStakeProtocol(pool.position.coinTypeA, pool.position.coinTypeB);
        this.savePoolToCache(pool);
        return pool;
      }
    } catch (error) {
      console.log(error);
    }
    return void 0;
  }
  async getVaultsConfigs(forceRefresh = false) {
    const { package_id } = this._sdk.sdkOptions.vaults;
    const cacheKey = `${package_id}_getMirrorPoolConfigs`;
    const cacheData = this.getCache(cacheKey, forceRefresh);
    if (cacheData !== void 0) {
      return cacheData;
    }
    const objects = (await this._sdk.ClmmSDK.fullClient.queryEventsByPage({
      MoveEventType: `${package_id}::vaults::InitEvent`
    })).data;
    const config = {
      admin_cap_id: "",
      vaults_manager_id: "",
      vaults_pool_handle: ""
    };
    if (objects.length > 0) {
      for (const item of objects) {
        const fields = item.parsedJson;
        config.admin_cap_id = fields.admin_cap_id;
        config.vaults_manager_id = fields.manager_id;
        const masterObj = await this._sdk.ClmmSDK.fullClient.getObject({ id: config.vaults_manager_id, options: { showContent: true } });
        const masterFields = getObjectFields16(masterObj);
        config.vaults_pool_handle = masterFields.vault_to_pool_maps.fields.id.id;
        break;
      }
      this.updateCache(cacheKey, config, cacheTime24h8);
    }
    return config;
  }
  /**
   * 获取haSUI:SUI的汇率
   * @param shouldRequestStake  为true 时  通过走质押逻辑,进行模拟计算
   * @returns
   */
  async getExchangeRateForStake(stakingProtocol, shouldRequestStake, swapAmount) {
    if (stakingProtocol === "Haedal" /* Haedal */) {
      return await HaedalUtils.getExchangeRateForStake(this._sdk, shouldRequestStake, swapAmount);
    }
    if (stakingProtocol === "Volo" /* Volo */) {
      return await VoloUtils.getExchangeRateForStake(this._sdk, shouldRequestStake, swapAmount);
    }
    if (stakingProtocol === "aftermath" /* Aftermath */) {
      return await AftermathoUtils.getExchangeRateForStake(this._sdk, shouldRequestStake, swapAmount);
    }
    return "0";
  }
  /**
   * the haSUI is just returned
   * @param tx
   * @param suiCoin
   * @returns
   */
  requestStakeCoin(stakingProtocol, tx, suiCoin) {
    if (stakingProtocol === "Haedal" /* Haedal */) {
      return HaedalUtils.requestStakeCoin(this._sdk, tx, suiCoin);
    }
    if (stakingProtocol === "Volo" /* Volo */) {
      return VoloUtils.requestStakeCoin(this._sdk, tx, suiCoin);
    }
    if (stakingProtocol === "aftermath" /* Aftermath */) {
      return AftermathoUtils.requestStakeCoin(this._sdk, tx, suiCoin);
    }
    return void 0;
  }
  async requestStake(stakingProtocol, amount, tx) {
    if (stakingProtocol === "Haedal" /* Haedal */) {
      return await HaedalUtils.requestStake(this._sdk, amount, tx);
    }
    if (stakingProtocol === "Volo" /* Volo */) {
      return await VoloUtils.requestStake(this._sdk, amount, tx);
    }
    if (stakingProtocol === "aftermath" /* Aftermath */) {
      return await AftermathoUtils.requestStake(this._sdk, amount, tx);
    }
    return void 0;
  }
  /**
   * Updates the cache for the given key.
   * @param {string} key The key of the cache entry to update.
   * @param {SuiResource} data The data to store in the cache.
   * @param {cacheTime5min} time The time in minutes after which the cache entry should expire.
   */
  updateCache(key, data, time = cacheTime5min8) {
    let cacheData = this._cache[key];
    if (cacheData) {
      cacheData.overdueTime = getFutureTime8(time);
      cacheData.value = data;
    } else {
      cacheData = new CachedContent8(data, getFutureTime8(time));
    }
    this._cache[key] = cacheData;
  }
  /**
   * Gets the cache entry for the given key.
   * @param {string} key The key of the cache entry to get.
   * @param {boolean} forceRefresh Whether to force a refresh of the cache entry.
   * @returns The cache entry for the given key, or undefined if the cache entry does not exist or is expired.
   */
  getCache(key, forceRefresh = false) {
    const cacheData = this._cache[key];
    const isValid = cacheData?.isValid();
    if (!forceRefresh && isValid) {
      return cacheData.value;
    }
    if (!isValid) {
      delete this._cache[key];
    }
    return void 0;
  }
};

// src/modules/LimitOrderModule.ts
import { TransactionBlock as TransactionBlock13 } from "@mysten/sui.js/transactions";
import {
  CLOCK_ADDRESS as CLOCK_ADDRESS8,
  CachedContent as CachedContent9,
  TransactionUtil as TransactionUtil8,
  cacheTime24h as cacheTime24h9,
  composeType as composeType2,
  extractStructTagFromType as extractStructTagFromType19,
  getFutureTime as getFutureTime9,
  getObjectFields as getObjectFields17,
  getPackagerConfigs as getPackagerConfigs12
} from "@cetusprotocol/cetus-sui-clmm-sdk";
var LimitOrderModule = class {
  _sdk;
  _cache = {};
  constructor(sdk) {
    this._sdk = sdk;
  }
  get sdk() {
    return this._sdk;
  }
  /**
   * 获取限价单支持的token list
   */
  async getLimitOrderTokenList() {
    const { limit_order } = this._sdk.sdkOptions;
    const { token_list_handle } = getPackagerConfigs12(limit_order);
    const { fullClient } = this._sdk.ClmmSDK;
    const res = await fullClient.getDynamicFieldsByPage(token_list_handle);
    const warpIds = res.data.map((item) => item.objectId);
    const objectRes = await fullClient.batchGetObjects(warpIds, { showContent: true });
    return objectRes.map((item) => {
      const { fields } = item.data.content;
      const info = {
        coin_type: extractStructTagFromType19(fields.name.fields.name).full_address,
        min_trade_amount: Number(fields.value)
      };
      return info;
    });
  }
  /**
   * 获取限价单池子列表
   * @returns
   */
  async getLimitOrderPoolList() {
    const { limit_order } = this._sdk.sdkOptions;
    const { rate_orders_indexer_handle } = getPackagerConfigs12(limit_order);
    const { fullClient } = this._sdk.ClmmSDK;
    const res = await fullClient.getDynamicFieldsByPage(rate_orders_indexer_handle);
    const warpIds = res.data.map((item) => item.objectId);
    const objectRes = await fullClient.batchGetObjects(warpIds, { showContent: true });
    return objectRes.map((item) => {
      const { fields } = item.data.content.fields.value;
      const info = {
        pay_coin_type: extractStructTagFromType19(fields.pay_coin.fields.name).full_address,
        target_coin_type: extractStructTagFromType19(fields.target_coin.fields.name).full_address,
        indexer_id: fields.indexer_id
      };
      this._sdk.updateCache(`${info.pay_coin_type}_${info.target_coin_type}`, info.indexer_id);
      return info;
    });
  }
  async getPoolIndexerId(pay_coin_type, target_coin_type) {
    const indexer_id = this._sdk.getCache(`${pay_coin_type}_${target_coin_type}`);
    try {
      if (indexer_id === void 0) {
        const list = await this.getLimitOrderPoolList();
        const filterInfoList = list.filter((item) => {
          return extractStructTagFromType19(item.pay_coin_type).full_address === extractStructTagFromType19(pay_coin_type).full_address && extractStructTagFromType19(item.target_coin_type).full_address === extractStructTagFromType19(target_coin_type).full_address;
        });
        if (filterInfoList.length > 0) {
          return filterInfoList[0].indexer_id;
        }
      }
    } catch (error) {
    }
    return indexer_id;
  }
  /**
   * 获取限价单列表
   * @returns
   */
  async getOwnerLimitOrderList(ownerAddress, paginationArgs) {
    const { fullClient } = this._sdk.ClmmSDK;
    const dataPage = {
      data: [],
      hasNextPage: false
    };
    try {
      const userIndexerHandle = await this.getUserIndexerHandle(ownerAddress);
      if (!userIndexerHandle) {
        return dataPage;
      }
      const res = await fullClient.getDynamicFieldsByPage(userIndexerHandle, paginationArgs);
      dataPage.hasNextPage = res.hasNextPage;
      dataPage.nextCursor = res.nextCursor;
      const orderIds = res.data.map((item) => item.name.value);
      const objectRes = await fullClient.batchGetObjects(orderIds, { showContent: true });
      const data = objectRes.map((item) => LimitOrderUtils.buildLimitOrderInfo(item)).filter((info) => info !== void 0);
      dataPage.data = data;
    } catch (error) {
      console.error("Error in getOwnerLimitOrderList:", error);
    }
    return dataPage;
  }
  async getLimitOrderConfigs() {
    const { package_id } = this._sdk.sdkOptions.limit_order;
    const config = {
      rate_orders_indexer_id: "",
      rate_orders_indexer_handle: "",
      global_config_id: "",
      token_list_handle: "",
      user_orders_indexer_id: "",
      user_orders_indexer_handle: ""
    };
    const initEventObjs = (await this._sdk.ClmmSDK.fullClient.queryEventsByPage({ MoveEventType: `${package_id}::limit_order::InitEvent` })).data;
    if (initEventObjs.length > 0) {
      const fields = initEventObjs[0].parsedJson;
      config.rate_orders_indexer_id = fields.rate_orders_indexer_id;
      config.user_orders_indexer_id = fields.user_orders_indexer_id;
      const orderPoolObj = await this._sdk.ClmmSDK.fullClient.getObject({
        id: config.rate_orders_indexer_id,
        options: { showContent: true }
      });
      config.rate_orders_indexer_handle = getObjectFields17(orderPoolObj).list.fields.id.id;
      const useOrderObj = await this._sdk.ClmmSDK.fullClient.getObject({
        id: config.user_orders_indexer_id,
        options: { showContent: true }
      });
      config.user_orders_indexer_handle = getObjectFields17(useOrderObj).indexer.fields.id.id;
    }
    const globalEventObjs = (await this._sdk.ClmmSDK.fullClient.queryEventsByPage({ MoveEventType: `${package_id}::config::InitFactoryEvent` })).data;
    if (globalEventObjs.length > 0) {
      const fields = globalEventObjs[0].parsedJson;
      config.global_config_id = fields.global_config_id;
      const globalObj = await this._sdk.ClmmSDK.fullClient.getObject({ id: config.global_config_id, options: { showContent: true } });
      const globalObjFields = getObjectFields17(globalObj);
      config.token_list_handle = globalObjFields.token_white_list.fields.id.id;
    }
    return config;
  }
  /**
   * 获取订单信息
   * @param orderId
   * @returns
   */
  async getLimitOrder(orderId) {
    const { fullClient } = this._sdk.ClmmSDK;
    try {
      const res = await fullClient.getObject({ id: orderId, options: { showContent: true } });
      return LimitOrderUtils.buildLimitOrderInfo(res);
    } catch (error) {
      console.error("Error in getLimitOrder:", error);
      return void 0;
    }
  }
  /**
   * 获取订单操作日志
   * @param orderId
   * @returns
   */
  async getLimitOrderLogs(orderId) {
    const { fullClient } = this._sdk.ClmmSDK;
    try {
      const res = await fullClient.queryTransactionBlocks({ filter: { ChangedObject: orderId }, options: { showEvents: true } });
      const list = [];
      res.data.forEach((item) => {
        list.push(...LimitOrderUtils.buildOrderLimitEvent(item, ["OrderPlacedEvent", "OrderCanceledEvent", "FlashLoanEvent"]));
      });
      return list;
    } catch (error) {
      console.error("Error in getLimitOrderLogs:", error);
      return [];
    }
  }
  /**
   * 获取订单的Claim日志
   * @param orderId
   * @returns
   */
  async getLimitOrderClaimLogs(orderId) {
    const { fullClient } = this._sdk.ClmmSDK;
    try {
      const res = await fullClient.queryTransactionBlocks({ filter: { ChangedObject: orderId }, options: { showEvents: true } });
      const list = [];
      res.data.forEach((item) => {
        list.push(...LimitOrderUtils.buildOrderLimitEvent(item, ["ClaimTargetCoinEvent"]));
      });
      return list;
    } catch (error) {
      console.error("Error in getLimitOrderLogs:", error);
      return [];
    }
  }
  buildRateOrdersIndexerType(params) {
    const { limit_order } = this._sdk.sdkOptions;
    return composeType2(`${limit_order.package_id}::limit_order::RateOrdersIndexer`, [params.pay_coin_type, params.target_coin_type]);
  }
  /**
   * 下单
   * @param payCoinType
   * @param targetCoinType
   * @returns
   */
  async placeLimitOrder(params) {
    const { limit_order } = this._sdk.sdkOptions;
    const { user_orders_indexer_id, global_config_id, rate_orders_indexer_id } = getPackagerConfigs12(limit_order);
    const coinAssets = await this._sdk.ClmmSDK.getOwnerCoinAssets(this._sdk.ClmmSDK.senderAddress, params.pay_coin_type);
    const tx = new TransactionBlock13();
    const indexerId = await this.getPoolIndexerId(params.pay_coin_type, params.target_coin_type);
    const payCoinObj = TransactionUtil8.buildCoinForAmount(tx, coinAssets, BigInt(params.pay_coin_amount), params.pay_coin_type, false, true);
    tx.moveCall({
      target: `${limit_order.published_at}::limit_order::${indexerId === void 0 ? "create_indexer_and_place_limit_order" : "place_limit_order"}`,
      typeArguments: [params.pay_coin_type, params.target_coin_type],
      arguments: [
        tx.object(global_config_id),
        indexerId === void 0 ? tx.object(rate_orders_indexer_id) : tx.object(indexerId),
        tx.object(user_orders_indexer_id),
        payCoinObj.targetCoin,
        tx.pure(LimitOrderUtils.priceToRate(params.price, params.pay_decimal, params.target_decimal)),
        tx.pure(params.expired_ts),
        tx.object(CLOCK_ADDRESS8)
      ]
    });
    return tx;
  }
  /**
   * claim target coin
   * @param params
   * @returns
   */
  async claimTargetCoin(params) {
    const { limit_order } = this._sdk.sdkOptions;
    const { global_config_id } = getPackagerConfigs12(limit_order);
    const tx = new TransactionBlock13();
    tx.moveCall({
      target: `${limit_order.published_at}::limit_order::claim_target_coin`,
      typeArguments: [params.pay_coin_type, params.target_coin_type],
      arguments: [tx.object(global_config_id), tx.pure(params.order_id)]
    });
    return tx;
  }
  /**
   * 取消订单
   * @param payCoinType
   * @param targetCoinType
   * @returns
   */
  async cancelOrdersByOwner(params) {
    const { limit_order } = this._sdk.sdkOptions;
    const { global_config_id } = getPackagerConfigs12(limit_order);
    const tx = new TransactionBlock13();
    const indexerIdMap = {};
    for (let index = 0; index < params.length; index++) {
      const element = params[index];
      const indexerId = await this.getPoolIndexerId(element.pay_coin_type, element.target_coin_type);
      if (indexerId === void 0) {
        throw Error("not found indexerId");
      }
      indexerIdMap[element.order_id] = indexerId;
    }
    params.forEach((item) => {
      tx.moveCall({
        target: `${limit_order.published_at}::limit_order::cancel_order_by_owner`,
        typeArguments: [item.pay_coin_type, item.target_coin_type],
        arguments: [
          tx.object(global_config_id),
          tx.object(indexerIdMap[item.order_id]),
          tx.object(item.order_id),
          tx.object(CLOCK_ADDRESS8)
        ]
      });
    });
    return tx;
  }
  async getUserIndexerHandle(ownerAddress) {
    const { fullClient } = this._sdk.ClmmSDK;
    const { limit_order } = this._sdk.sdkOptions;
    let userIndexerHandle = this.getUserIndexerHandleByCache(ownerAddress);
    if (userIndexerHandle === void 0) {
      const tx = new TransactionBlock13();
      tx.moveCall({
        target: `${limit_order.published_at}::limit_order::get_orders_indexer_by_owner`,
        typeArguments: [],
        arguments: [tx.pure(ownerAddress), tx.object(getPackagerConfigs12(limit_order).user_orders_indexer_id)]
      });
      const res = await fullClient.devInspectTransactionBlock({
        transactionBlock: tx,
        sender: this._sdk.ClmmSDK.sdkOptions.simulationAccount.address
      });
      userIndexerHandle = res.events[0].parsedJson.orders_table_id;
      if (userIndexerHandle) {
        this.saveUserIndexerHandleByCache(ownerAddress, userIndexerHandle);
      }
    }
    return userIndexerHandle;
  }
  getUserIndexerHandleByCache(ownerAddress) {
    const cacheKey = `getUserIndexerHandleByCache_${ownerAddress}`;
    return this.getCache(cacheKey);
  }
  saveUserIndexerHandleByCache(ownerAddress, id) {
    const cacheKey = `getUserIndexerHandleByCache_${ownerAddress}`;
    return this.updateCache(cacheKey, id);
  }
  /**
   * Updates the cache for the given key.
   * @param {string} key The key of the cache entry to update.
   * @param {SuiResource} data The data to store in the cache.
   * @param {cacheTime5min} time The time in minutes after which the cache entry should expire.
   */
  updateCache(key, data, time = cacheTime24h9) {
    let cacheData = this._cache[key];
    if (cacheData) {
      cacheData.overdueTime = getFutureTime9(time);
      cacheData.value = data;
    } else {
      cacheData = new CachedContent9(data, getFutureTime9(time));
    }
    this._cache[key] = cacheData;
  }
  /**
   * Gets the cache entry for the given key.
   * @param {string} key The key of the cache entry to get.
   * @param {boolean} forceRefresh Whether to force a refresh of the cache entry.
   * @returns The cache entry for the given key, or undefined if the cache entry does not exist or is expired.
   */
  getCache(key, forceRefresh = false) {
    const cacheData = this._cache[key];
    const isValid = cacheData?.isValid();
    if (!forceRefresh && isValid) {
      return cacheData.value;
    }
    if (!isValid) {
      delete this._cache[key];
    }
    return void 0;
  }
};

// src/modules/farmsModule.ts
import {
  CachedContent as CachedContent10,
  cacheTime24h as cacheTime24h10,
  getFutureTime as getFutureTime10,
  getPackagerConfigs as getPackagerConfigs13,
  CLOCK_ADDRESS as CLOCK_ADDRESS9,
  extractStructTagFromType as extractStructTagFromType20,
  TransactionUtil as TransactionUtil9,
  removeHexPrefix,
  asUintN as asUintN2,
  ClmmIntegratePoolV2Module,
  normalizeCoinType as normalizeCoinType3,
  checkInvalidSuiAddress
} from "@cetusprotocol/cetus-sui-clmm-sdk";
import { TransactionBlock as TransactionBlock14 } from "@mysten/sui.js/transactions";
var FarmsModule = class {
  _sdk;
  _cache = {};
  constructor(sdk) {
    this._sdk = sdk;
  }
  get sdk() {
    return this._sdk;
  }
  /**
   * 获取frams 池子列表
   * @param paginationArgs
   * @returns
   */
  async getFramsPoolList(paginationArgs = "all") {
    const { package_id } = this._sdk.sdkOptions.frams;
    const dataPage = {
      data: [],
      hasNextPage: false
    };
    try {
      const objects = await this._sdk.ClmmSDK.fullClient.queryEventsByPage(
        { MoveEventType: `${package_id}::pool::CreatePoolEvent` },
        paginationArgs
      );
      const poolObjectIds = objects.data.map((object) => object.parsedJson.pool_id);
      dataPage.hasNextPage = objects.hasNextPage;
      dataPage.nextCursor = objects.nextCursor;
      if (poolObjectIds.length > 0) {
        const objectDataResponses = await this._sdk.ClmmSDK.fullClient.batchGetObjects(poolObjectIds, { showType: true, showContent: true });
        for (const item of objectDataResponses) {
          const pool = FramsUtils.buildFramsPool(item);
          if (pool) {
            pool.rewarders = await this.getFramsRewarderConfig(
              pool.clmm_pool_id,
              pool.rewarders.map((item2) => item2.reward_coin)
            );
            dataPage.data.push(pool);
            this.updateCache(pool.id, pool);
          }
        }
      }
    } catch (error) {
      console.log("getFramsPoolList error ", error);
    }
    return dataPage;
  }
  /**
   * 获取frams 池子
   * @param id
   * @param forceRefresh
   * @returns
   */
  async getFramsPool(id, forceRefresh = false) {
    const cacheData = this.getCache(id, forceRefresh);
    if (cacheData) {
      return cacheData;
    }
    const res = await this._sdk.ClmmSDK.fullClient.getObject({ id, options: { showType: true, showContent: true } });
    const pool = FramsUtils.buildFramsPool(res.data);
    if (pool) {
      pool.rewarders = await this.getFramsRewarderConfig(
        pool.clmm_pool_id,
        pool.rewarders.map((item) => item.reward_coin)
      );
      this.updateCache(pool.id, pool);
    }
    return pool;
  }
  async getFramsRewarderConfig(poolId, rewardCoins, forceRefresh = false) {
    const { frams } = this._sdk.sdkOptions;
    const rewarderConfigs = [];
    for (const coinType of rewardCoins) {
      const cacheKey = `getFramsRewarderConfig_${coinType}_${poolId}`;
      const cacheData = this.getCache(cacheKey, forceRefresh);
      if (cacheData) {
        rewarderConfigs.push(cacheData);
      } else {
        try {
          const res = await this._sdk.ClmmSDK.fullClient.getDynamicFieldObject({
            parentId: getPackagerConfigs13(frams).rewarder_manager_handle,
            name: {
              type: "0x1::type_name::TypeName",
              value: removeHexPrefix(coinType)
            }
          });
          const { fields } = res.data.content.fields.value.fields.value;
          const config = {
            reward_coin: coinType,
            last_reward_time: fields.last_reward_time,
            emission_per_second: fields.emission_per_second,
            total_allocate_point: fields.total_allocate_point,
            allocate_point: ""
          };
          const poolsHandler = fields.pools.fields.id.id;
          const res1 = await this._sdk.ClmmSDK.fullClient.getDynamicFieldObject({
            parentId: poolsHandler,
            name: {
              type: "0x2::object::ID",
              value: poolId
            }
          });
          const { allocate_point } = res1.data.content.fields.value.fields.value.fields;
          config.allocate_point = allocate_point;
          this.updateCache(cacheKey, config);
          rewarderConfigs.push(config);
        } catch (error) {
          console.log(error);
        }
      }
    }
    return rewarderConfigs;
  }
  /**
   * 获取拥有的frams 仓位列表
   * @param owner
   * @param assignPools
   * @param calculateFarmingRewards
   * @param paginationArgs
   * @returns 若assignPools 不为空 ，则返回 Record<string, FramsPositionNFT[]>
   */
  async getOwnedFramsPositionNFTList(owner, assignPools = [], calculateFarmingRewards = false, useDevInspect = true, paginationArgs = "all") {
    const { package_id } = this._sdk.sdkOptions.frams;
    const dataPage = {
      data: [],
      hasNextPage: false
    };
    let dataMap = {};
    const filterList = [];
    const isAssignPools = assignPools.length > 0;
    const objects = await this._sdk.ClmmSDK.fullClient.getOwnedObjectsByPage(
      owner,
      {
        filter: {
          StructType: `${package_id}::pool::WrappedPositionNFT`
        },
        options: {
          showContent: true
        }
      },
      paginationArgs
    );
    dataPage.hasNextPage = objects.hasNextPage;
    dataPage.nextCursor = objects.nextCursor;
    for (const object of objects.data) {
      const framsPositinNft = FramsUtils.buildFramsPositionNFT(object);
      if (framsPositinNft) {
        if (isAssignPools) {
          if (assignPools.includes(framsPositinNft.pool_id)) {
            filterList.push(framsPositinNft);
            let poolNftList = dataMap[framsPositinNft.pool_id];
            if (poolNftList) {
              poolNftList.push(framsPositinNft);
            } else {
              poolNftList = [framsPositinNft];
            }
            dataMap[framsPositinNft.pool_id] = poolNftList;
          }
        } else {
          filterList.push(framsPositinNft);
        }
      }
    }
    if (calculateFarmingRewards && filterList.length > 0) {
      const rewardMap = await this.calculateFarmingRewards(
        filterList.map((item) => {
          return {
            pool_id: item.pool_id,
            position_nft_id: item.id,
            clmm_pool_id: item.clmm_pool_id
          };
        }),
        useDevInspect
      );
      if (isAssignPools) {
        dataMap = {};
        filterList.forEach((item) => {
          const rewards = rewardMap[item.id];
          if (rewards) {
            item.rewards = rewardMap[item.id];
          }
          let poolNftList = dataMap[item.pool_id];
          if (poolNftList) {
            poolNftList.push(item);
          } else {
            poolNftList = [item];
          }
          dataMap[item.pool_id] = poolNftList;
        });
      } else {
        filterList.forEach((item) => {
          const rewards = rewardMap[item.id];
          if (rewards) {
            item.rewards = rewardMap[item.id];
          }
        });
      }
    }
    if (isAssignPools) {
      return dataMap;
    }
    dataPage.data = filterList;
    return dataPage;
  }
  /**
   * 得到仓位信息
   * @param owner
   * @param paginationArgs
   * @param calculateFarmingRewards
   * @returns
   */
  async getFramsPositionNFT(positionNftId, calculateFarmingRewards = true) {
    const object = await this._sdk.ClmmSDK.fullClient.getObject({ id: positionNftId, options: { showContent: true, showType: true } });
    const framsPositinNft = FramsUtils.buildFramsPositionNFT(object);
    if (calculateFarmingRewards && framsPositinNft) {
      const rewardMap = await this.calculateFarmingRewards([
        {
          pool_id: framsPositinNft.pool_id,
          position_nft_id: framsPositinNft.id
        }
      ]);
      const rewards = rewardMap[framsPositinNft.id];
      if (rewards) {
        framsPositinNft.rewards = rewardMap[framsPositinNft.id];
      }
    }
    return framsPositinNft;
  }
  /**
   * 计算frams 奖励
   * @param owner
   * @param paginationArgs
   * @returns
   */
  async calculateFarmingRewards(params, useDevInspect = true) {
    const { frams } = this._sdk.sdkOptions;
    const tx = new TransactionBlock14();
    const { rewarder_manager_id, global_config_id } = getPackagerConfigs13(frams);
    params.forEach((item) => {
      tx.moveCall({
        target: `${frams.published_at}::router::accumulated_position_rewards`,
        typeArguments: [],
        arguments: [
          tx.object(global_config_id),
          tx.object(rewarder_manager_id),
          tx.object(item.pool_id),
          tx.pure(item.position_nft_id, "address"),
          tx.object(CLOCK_ADDRESS9)
        ]
      });
    });
    const rewardMap = {};
    try {
      const simulateRes = await this._sdk.ClmmSDK.fullClient.sendSimulationTransaction(tx, this._sdk.ClmmSDK.senderAddress, useDevInspect);
      console.log({ simulateRes });
      const valueData = simulateRes?.events?.filter((item) => {
        return extractStructTagFromType20(item.type).name === `AccumulatedPositionRewardsEvent`;
      });
      if (valueData && valueData.length > 0) {
        for (let i = 0; i < valueData.length; i += 1) {
          const { parsedJson } = valueData[i];
          const rewardInfos = [];
          parsedJson.rewards.contents.forEach((item) => {
            rewardInfos.push({
              rewarder_type: extractStructTagFromType20(item.key.name).full_address,
              rewarder_amount: item.value
            });
          });
          rewardMap[parsedJson.wrapped_position_id] = rewardInfos;
        }
      }
    } catch (error) {
      console.log(error);
    }
    return rewardMap;
  }
  /**
   * 质押clmm 仓位
   * @param params
   * @returns
   */
  depositPayload(params, tx) {
    const { frams } = this.sdk.sdkOptions;
    tx = tx || new TransactionBlock14();
    const { global_config_id, rewarder_manager_id } = getPackagerConfigs13(frams);
    tx.moveCall({
      target: `${frams.published_at}::router::deposit`,
      typeArguments: [],
      arguments: [
        tx.object(global_config_id),
        tx.object(rewarder_manager_id),
        tx.object(params.pool_id),
        tx.object(params.clmm_position_id),
        tx.object(CLOCK_ADDRESS9)
      ]
    });
    return tx;
  }
  /**
   * 解除质押
   * @param params
   * @returns
   */
  async withdrawPayload(params, tx) {
    const { frams } = this.sdk.sdkOptions;
    const { global_config_id, rewarder_manager_id } = getPackagerConfigs13(frams);
    tx = tx || new TransactionBlock14();
    tx = await this.harvestPayload(
      {
        pool_id: params.pool_id,
        position_nft_id: params.position_nft_id
      },
      tx
    );
    tx.moveCall({
      target: `${frams.published_at}::router::withdraw`,
      typeArguments: [],
      arguments: [
        tx.object(global_config_id),
        tx.object(rewarder_manager_id),
        tx.object(params.pool_id),
        tx.object(params.position_nft_id),
        tx.object(CLOCK_ADDRESS9)
      ]
    });
    return tx;
  }
  /**
   * 批量收割Frams奖励
   * @param paramss
   * @returns
   */
  async batchHarvestPayload(paramss, tx) {
    tx = tx || new TransactionBlock14();
    for (const params of paramss) {
      await this.harvestPayload(params, tx);
    }
    return tx;
  }
  /**
   * 批量收割Frams奖励 和  clmm 奖励
   * @param paramss
   * @returns
   */
  async batchHarvestAndClmmFeePayload(framsList, clmmList) {
    if (!checkInvalidSuiAddress(this._sdk.ClmmSDK.senderAddress)) {
      throw new Error("this config sdk senderAddress is not set right");
    }
    const tx = new TransactionBlock14();
    const coinIdMaps = {};
    const allCoinAsset = await this._sdk.ClmmSDK.getOwnerCoinAssets(this._sdk.ClmmSDK.senderAddress, null);
    for (const item of clmmList) {
      this.collectClmmFeeInternal(item, coinIdMaps, tx, allCoinAsset);
      this.collectClmmRewardInternal(item, coinIdMaps, tx, allCoinAsset);
    }
    const { frams } = this.sdk.sdkOptions;
    const { global_config_id, rewarder_manager_id } = getPackagerConfigs13(frams);
    for (const item of framsList) {
      const framsPool = await this.getFramsPool(item.pool_id, false);
      if (framsPool === void 0) {
        throw Error(`not found ${item.pool_id}`);
      }
      if (item.collect_fee) {
        this.collectFeeInternal(item, coinIdMaps, tx, allCoinAsset);
      }
      if (item.clmm_rewarder_types.length > 0) {
        await this.collectRewardInternal(item, coinIdMaps, tx, allCoinAsset);
      }
      if (item.collect_frams_rewarder) {
        framsPool.rewarders.forEach((rewardCoin) => {
          if (tx) {
            tx.moveCall({
              target: `${frams.published_at}::router::harvest`,
              typeArguments: [rewardCoin.reward_coin],
              arguments: [
                tx.object(global_config_id),
                tx.object(rewarder_manager_id),
                tx.object(item.pool_id),
                tx.object(item.position_nft_id),
                tx.object(CLOCK_ADDRESS9)
              ]
            });
          }
        });
      }
    }
    Object.keys(coinIdMaps).forEach((key) => {
      const value = coinIdMaps[key];
      if (value.isMintZeroCoin || value.originalSplitedCoin && value.originalSplitedCoin.kind === "GasCoin") {
        TransactionUtil9.buildTransferCoin(this._sdk.ClmmSDK, tx, value.targetCoin, key, this._sdk.ClmmSDK.senderAddress);
      }
    });
    return tx;
  }
  async collectRewardInternal(item, coinIdMaps, tx, allCoinAsset) {
    const primaryCoinInputs = [];
    item.clmm_rewarder_types.forEach((type) => {
      const coinType = normalizeCoinType3(type);
      let coinInput = coinIdMaps[type];
      if (coinInput === void 0) {
        coinInput = TransactionUtil9.buildCoinForAmount(tx, allCoinAsset, BigInt(0), coinType, false);
        coinIdMaps[coinType] = coinInput;
      }
      primaryCoinInputs.push(coinInput.targetCoin);
    });
    await this.collectClmmRewardNoSendPayload(item, tx);
  }
  collectFeeInternal(item, coinIdMaps, tx, allCoinAsset) {
    const { frams } = this.sdk.sdkOptions;
    const { global_config_id } = getPackagerConfigs13(frams);
    const { clmm_pool, integrate } = this.sdk.ClmmSDK.sdkOptions;
    const clmmConfig = getPackagerConfigs13(clmm_pool);
    const coinTypeA = normalizeCoinType3(item.coinTypeA);
    const coinTypeB = normalizeCoinType3(item.coinTypeB);
    let coinAInput = coinIdMaps[coinTypeA];
    if (coinAInput === void 0) {
      coinAInput = TransactionUtil9.buildCoinForAmount(tx, allCoinAsset, BigInt(0), coinTypeA, false);
      coinIdMaps[coinTypeA] = coinAInput;
    }
    let coinBInput = coinIdMaps[coinTypeB];
    if (coinBInput === void 0) {
      coinBInput = TransactionUtil9.buildCoinForAmount(tx, allCoinAsset, BigInt(0), coinTypeB, false);
      coinIdMaps[coinTypeB] = coinBInput;
    }
    tx.moveCall({
      target: `${integrate.published_at}::stable_farming::collect_fee`,
      typeArguments: [item.coinTypeA, item.coinTypeB],
      arguments: [
        tx.object(global_config_id),
        tx.object(clmmConfig.global_config_id),
        tx.object(item.clmm_pool_id),
        tx.object(item.position_nft_id),
        coinAInput.targetCoin,
        coinBInput.targetCoin
      ]
    });
  }
  collectClmmRewardInternal(item, coinIdMaps, tx, allCoinAsset) {
    const primaryCoinInputs = [];
    item.rewarder_coin_types.forEach((type) => {
      const coinType = normalizeCoinType3(type);
      let coinInput = coinIdMaps[type];
      if (coinInput === void 0) {
        coinInput = TransactionUtil9.buildCoinForAmount(tx, allCoinAsset, BigInt(0), coinType, false);
        coinIdMaps[coinType] = coinInput;
      }
      primaryCoinInputs.push(coinInput.targetCoin);
    });
    this._sdk.ClmmSDK.Rewarder.createCollectRewarderNoSendPaylod(item, tx, primaryCoinInputs);
  }
  collectClmmFeeInternal(item, coinIdMaps, tx, allCoinAsset) {
    if (item.collect_fee) {
      const coinTypeA = normalizeCoinType3(item.coinTypeA);
      const coinTypeB = normalizeCoinType3(item.coinTypeB);
      let coinAInput = coinIdMaps[coinTypeA];
      if (coinAInput === void 0) {
        coinAInput = TransactionUtil9.buildCoinForAmount(tx, allCoinAsset, BigInt(0), coinTypeA, false);
        coinIdMaps[coinTypeA] = coinAInput;
      }
      let coinBInput = coinIdMaps[coinTypeB];
      if (coinBInput === void 0) {
        coinBInput = TransactionUtil9.buildCoinForAmount(tx, allCoinAsset, BigInt(0), coinTypeB, false);
        coinIdMaps[coinTypeB] = coinBInput;
      }
      this._sdk.ClmmSDK.Position.createCollectFeeNoSendPaylod(
        {
          pool_id: item.pool_id,
          pos_id: item.pos_id,
          coinTypeA: item.coinTypeA,
          coinTypeB: item.coinTypeB
        },
        tx,
        coinAInput.targetCoin,
        coinBInput.targetCoin
      );
    }
  }
  /**
   * 收割Frams奖励
   * @param paramss
   * @returns
   */
  async harvestPayload(params, tx) {
    const { frams } = this.sdk.sdkOptions;
    tx = tx || new TransactionBlock14();
    const framsPool = await this.getFramsPool(params.pool_id, false);
    if (framsPool === void 0) {
      throw Error(`not found ${params.pool_id}`);
    }
    const { global_config_id, rewarder_manager_id } = getPackagerConfigs13(frams);
    const isCollectFee = "collect_fee" in params;
    if (isCollectFee) {
      if (params.collect_fee || params.clmm_rewarder_types.length > 0) {
        const allCoinAsset = await this._sdk.ClmmSDK.getOwnerCoinAssets(this._sdk.ClmmSDK.senderAddress);
        tx = await this.buildCollectRewarderAndFeeParams(
          {
            clmm_pool_id: params.clmm_pool_id,
            position_nft_id: params.position_nft_id,
            coinTypeA: params.coinTypeA,
            coinTypeB: params.coinTypeB,
            collectFee: params.collect_fee,
            clmm_rewarder_types: params.clmm_rewarder_types
          },
          tx,
          allCoinAsset
        );
      }
    }
    framsPool.rewarders.forEach((rewardCoin) => {
      if (tx) {
        tx.moveCall({
          target: `${frams.published_at}::router::harvest`,
          typeArguments: [rewardCoin.reward_coin],
          arguments: [
            tx.object(global_config_id),
            tx.object(rewarder_manager_id),
            tx.object(params.pool_id),
            tx.object(params.position_nft_id),
            tx.object(CLOCK_ADDRESS9)
          ]
        });
      }
    });
    return tx;
  }
  async claimFeeAndClmmReward(params) {
    const tx = new TransactionBlock14();
    const allCoinAsset = await this._sdk.ClmmSDK.getOwnerCoinAssets(this._sdk.ClmmSDK.senderAddress);
    await this.buildCollectRewarderAndFeeParams(
      {
        clmm_pool_id: params.clmm_pool_id,
        position_nft_id: params.position_nft_id,
        coinTypeA: params.coinTypeA,
        coinTypeB: params.coinTypeB,
        collectFee: params.collect_fee,
        clmm_rewarder_types: params.clmm_rewarder_types
      },
      tx,
      allCoinAsset
    );
    return tx;
  }
  /**
   * 开仓/添加流动性/并质押
   * @param params
   */
  async openPositionAddLiquidityStakePaylod(params) {
    const tx = new TransactionBlock14();
    const { clmm_pool, integrate } = this._sdk.ClmmSDK.sdkOptions;
    const clmmPoolConfig = getPackagerConfigs13(clmm_pool);
    const posId = tx.moveCall({
      target: `${clmm_pool.published_at}::pool::open_position`,
      typeArguments: [params.coinTypeA, params.coinTypeB],
      arguments: [
        tx.object(clmmPoolConfig.global_config_id),
        tx.object(params.clmm_pool_id),
        tx.pure(asUintN2(BigInt(params.tick_lower)).toString()),
        tx.pure(asUintN2(BigInt(params.tick_upper)).toString())
      ]
    });
    const allCoinAsset = await this._sdk.ClmmSDK.getOwnerCoinAssets(this._sdk.ClmmSDK.senderAddress);
    const primaryCoinAInputs = TransactionUtil9.buildCoinForAmount(tx, allCoinAsset, BigInt(params.amount_a), params.coinTypeA, false);
    const primaryCoinBInputs = TransactionUtil9.buildCoinForAmount(tx, allCoinAsset, BigInt(params.amount_b), params.coinTypeB, false);
    tx.moveCall({
      target: `${integrate.published_at}::${ClmmIntegratePoolV2Module}::add_liquidity_by_fix_coin`,
      typeArguments: [params.coinTypeA, params.coinTypeB],
      arguments: [
        tx.object(clmmPoolConfig.global_config_id),
        tx.object(params.clmm_pool_id),
        posId,
        primaryCoinAInputs.targetCoin,
        primaryCoinBInputs.targetCoin,
        tx.pure(params.amount_a.toString()),
        tx.pure(params.amount_b.toString()),
        tx.pure(params.fix_amount_a),
        tx.object(CLOCK_ADDRESS9)
      ]
    });
    const { frams } = this.sdk.sdkOptions;
    const { global_config_id, rewarder_manager_id } = getPackagerConfigs13(frams);
    tx.moveCall({
      target: `${frams.published_at}::router::deposit`,
      typeArguments: [],
      arguments: [tx.object(global_config_id), tx.object(rewarder_manager_id), tx.object(params.pool_id), posId, tx.object(CLOCK_ADDRESS9)]
    });
    return tx;
  }
  /**
   * 添加流动性
   * @param paramss
   * @param tx
   * @returns
   */
  async addLiquidityPayload(params, tx) {
    const { frams } = this.sdk.sdkOptions;
    const { clmm_pool } = this.sdk.ClmmSDK.sdkOptions;
    tx = tx || new TransactionBlock14();
    const framsConfig = getPackagerConfigs13(frams);
    const clmmConfig = getPackagerConfigs13(clmm_pool);
    const allCoinAsset = await this._sdk.ClmmSDK.getOwnerCoinAssets(this._sdk.ClmmSDK.senderAddress);
    const primaryCoinAInputs = TransactionUtil9.buildCoinForAmount(tx, allCoinAsset, BigInt(params.amount_limit_a), params.coinTypeA, false);
    const primaryCoinBInputs = TransactionUtil9.buildCoinForAmount(tx, allCoinAsset, BigInt(params.amount_limit_b), params.coinTypeB, false);
    if (params.collect_rewarder) {
      tx = await this.harvestPayload(
        {
          pool_id: params.pool_id,
          position_nft_id: params.position_nft_id,
          clmm_pool_id: params.clmm_pool_id
        },
        tx
      );
    }
    if (params.collect_fee || params.clmm_rewarder_types.length > 0) {
      const allCoinAsset2 = await this._sdk.ClmmSDK.getOwnerCoinAssets(this._sdk.ClmmSDK.senderAddress);
      tx = await this.buildCollectRewarderAndFeeParams(
        {
          clmm_pool_id: params.clmm_pool_id,
          position_nft_id: params.position_nft_id,
          coinTypeA: params.coinTypeA,
          coinTypeB: params.coinTypeB,
          collectFee: params.collect_fee,
          clmm_rewarder_types: params.clmm_rewarder_types
        },
        tx,
        allCoinAsset2,
        primaryCoinAInputs.remainCoins,
        primaryCoinBInputs.remainCoins
      );
    }
    tx.moveCall({
      target: `${frams.published_at}::router::add_liquidity`,
      typeArguments: [params.coinTypeA, params.coinTypeB],
      arguments: [
        tx.object(framsConfig.global_config_id),
        tx.object(clmmConfig.global_config_id),
        tx.object(framsConfig.rewarder_manager_id),
        tx.object(params.pool_id),
        tx.object(params.clmm_pool_id),
        tx.object(params.position_nft_id),
        primaryCoinAInputs.targetCoin,
        primaryCoinBInputs.targetCoin,
        tx.pure(params.amount_limit_a),
        tx.pure(params.amount_limit_b),
        tx.pure(params.delta_liquidity),
        tx.object(CLOCK_ADDRESS9)
      ]
    });
    return tx;
  }
  /**
   * 固定token 添加流动性
   * @param params
   * @param tx
   * @returns
   */
  async addLiquidityFixCoinPayload(params, tx) {
    const { frams } = this.sdk.sdkOptions;
    const { clmm_pool } = this.sdk.ClmmSDK.sdkOptions;
    tx = tx || new TransactionBlock14();
    const framsConfig = getPackagerConfigs13(frams);
    const clmmConfig = getPackagerConfigs13(clmm_pool);
    const allCoinAsset = await this._sdk.ClmmSDK.getOwnerCoinAssets(this._sdk.ClmmSDK.senderAddress);
    const primaryCoinAInputs = TransactionUtil9.buildCoinForAmount(tx, allCoinAsset, BigInt(params.amount_a), params.coinTypeA, false);
    const primaryCoinBInputs = TransactionUtil9.buildCoinForAmount(tx, allCoinAsset, BigInt(params.amount_b), params.coinTypeB, false);
    if (params.collect_rewarder) {
      tx = await this.harvestPayload(
        {
          pool_id: params.pool_id,
          position_nft_id: params.position_nft_id,
          clmm_pool_id: params.clmm_pool_id
        },
        tx
      );
    }
    if (params.collect_fee || params.clmm_rewarder_types.length > 0) {
      const allCoinAsset2 = await this._sdk.ClmmSDK.getOwnerCoinAssets(this._sdk.ClmmSDK.senderAddress);
      tx = await this.buildCollectRewarderAndFeeParams(
        {
          clmm_pool_id: params.clmm_pool_id,
          position_nft_id: params.position_nft_id,
          coinTypeA: params.coinTypeA,
          coinTypeB: params.coinTypeB,
          collectFee: params.collect_fee,
          clmm_rewarder_types: params.clmm_rewarder_types
        },
        tx,
        allCoinAsset2,
        primaryCoinAInputs.remainCoins,
        primaryCoinBInputs.remainCoins
      );
    }
    tx.moveCall({
      target: `${frams.published_at}::router::add_liquidity_fix_coin`,
      typeArguments: [params.coinTypeA, params.coinTypeB],
      arguments: [
        tx.object(framsConfig.global_config_id),
        tx.object(clmmConfig.global_config_id),
        tx.object(framsConfig.rewarder_manager_id),
        tx.object(params.pool_id),
        tx.object(params.clmm_pool_id),
        tx.object(params.position_nft_id),
        primaryCoinAInputs.targetCoin,
        primaryCoinBInputs.targetCoin,
        tx.pure(params.amount_a.toString()),
        tx.pure(params.amount_b.toString()),
        tx.pure(params.fix_amount_a),
        tx.object(CLOCK_ADDRESS9)
      ]
    });
    return tx;
  }
  /**
   * 移除流动性流动性
   * @param params
   * @param tx
   * @returns
   */
  async removeLiquidityPayload(params, tx) {
    const { frams } = this.sdk.sdkOptions;
    const { clmm_pool } = this.sdk.ClmmSDK.sdkOptions;
    tx = tx || new TransactionBlock14();
    const framsConfig = getPackagerConfigs13(frams);
    const clmmConfig = getPackagerConfigs13(clmm_pool);
    if (params.collect_rewarder && (!params.unstake || params.close_position)) {
      tx = await this.harvestPayload(
        {
          pool_id: params.pool_id,
          position_nft_id: params.position_nft_id,
          clmm_pool_id: params.clmm_pool_id
        },
        tx
      );
    }
    if (params.clmm_rewarder_types.length > 0) {
      const allCoinAsset = await this._sdk.ClmmSDK.getOwnerCoinAssets(this._sdk.ClmmSDK.senderAddress);
      tx = await this.buildCollectRewarderAndFeeParams(
        {
          clmm_pool_id: params.clmm_pool_id,
          position_nft_id: params.position_nft_id,
          coinTypeA: params.coinTypeA,
          coinTypeB: params.coinTypeB,
          collectFee: false,
          // 移除流动性 会收割fee
          clmm_rewarder_types: params.clmm_rewarder_types
        },
        tx,
        allCoinAsset
      );
    }
    if (params.close_position) {
      if (params.clmm_position_id === void 0) {
        throw Error(`close_position need clmm_position_id`);
      }
      tx.moveCall({
        target: `${frams.published_at}::router::close_position`,
        typeArguments: [params.coinTypeA, params.coinTypeB],
        arguments: [
          tx.object(framsConfig.global_config_id),
          tx.object(framsConfig.rewarder_manager_id),
          tx.object(params.pool_id),
          tx.object(clmmConfig.global_config_id),
          tx.object(params.clmm_pool_id),
          tx.object(params.position_nft_id),
          tx.pure(params.min_amount_a),
          tx.pure(params.min_amount_b),
          tx.object(CLOCK_ADDRESS9)
        ]
      });
    } else {
      tx.moveCall({
        target: `${frams.published_at}::router::remove_liquidity`,
        typeArguments: [params.coinTypeA, params.coinTypeB],
        arguments: [
          tx.object(framsConfig.global_config_id),
          tx.object(clmmConfig.global_config_id),
          tx.object(framsConfig.rewarder_manager_id),
          tx.object(params.pool_id),
          tx.object(params.clmm_pool_id),
          tx.object(params.position_nft_id),
          tx.pure(params.delta_liquidity),
          tx.pure(params.min_amount_a),
          tx.pure(params.min_amount_b),
          tx.object(CLOCK_ADDRESS9)
        ]
      });
      if (params.unstake) {
        this.withdrawPayload({
          pool_id: params.pool_id,
          position_nft_id: params.position_nft_id
        });
      }
    }
    return tx;
  }
  async buildCollectRewarderAndFeeParams(params, tx, allCoinAsset, allCoinAssetA, allCoinAssetB) {
    if (allCoinAssetA === void 0) {
      allCoinAssetA = [...allCoinAsset];
    }
    if (allCoinAssetB === void 0) {
      allCoinAssetB = [...allCoinAsset];
    }
    const coinTypeA = normalizeCoinType3(params.coinTypeA);
    const coinTypeB = normalizeCoinType3(params.coinTypeB);
    if (params.collectFee) {
      this.collectFeePayload(
        {
          clmm_pool_id: params.clmm_pool_id,
          position_nft_id: params.position_nft_id,
          // coin_a: primaryCoinAInput.targetCoin,
          // coin_b: primaryCoinBInput.targetCoin,
          coinTypeA: params.coinTypeA,
          coinTypeB: params.coinTypeB
        },
        tx
      );
    }
    const primaryCoinInputs = [];
    params.clmm_rewarder_types.forEach((type) => {
      switch (normalizeCoinType3(type)) {
        case coinTypeA:
          primaryCoinInputs.push(TransactionUtil9.buildCoinForAmount(tx, allCoinAssetA, BigInt(0), type, false).targetCoin);
          break;
        case coinTypeB:
          primaryCoinInputs.push(TransactionUtil9.buildCoinForAmount(tx, allCoinAssetB, BigInt(0), type, false).targetCoin);
          break;
        default:
          primaryCoinInputs.push(TransactionUtil9.buildCoinForAmount(tx, allCoinAsset, BigInt(0), type, false).targetCoin);
          break;
      }
    });
    this.collectClmmRewardPayload(
      {
        clmm_pool_id: params.clmm_pool_id,
        position_nft_id: params.position_nft_id,
        clmm_rewarder_types: params.clmm_rewarder_types,
        coinTypeA: params.coinTypeA,
        coinTypeB: params.coinTypeB,
        reward_coins: primaryCoinInputs
      },
      tx
    );
    return tx;
  }
  /**
   * 收割fee
   * @param params
   * @param tx
   * @returns
   */
  collectFeePayload(params, tx) {
    const { frams } = this.sdk.sdkOptions;
    const { clmm_pool } = this.sdk.ClmmSDK.sdkOptions;
    tx = tx || new TransactionBlock14();
    const framsConfig = getPackagerConfigs13(frams);
    const clmmConfig = getPackagerConfigs13(clmm_pool);
    tx.moveCall({
      target: `${frams.published_at}::router::collect_fee`,
      typeArguments: [params.coinTypeA, params.coinTypeB],
      arguments: [
        tx.object(framsConfig.global_config_id),
        tx.object(clmmConfig.global_config_id),
        tx.object(params.clmm_pool_id),
        tx.object(params.position_nft_id)
        // params.coin_a!,
        // params.coin_b!,
      ]
    });
    return tx;
  }
  /**
   * 收割Clmm 奖励
   * @param paramss
   * @param tx
   * @returns
   */
  async collectClmmRewardPayload(paramss, tx) {
    const { frams } = this.sdk.sdkOptions;
    const { clmm_pool } = this.sdk.ClmmSDK.sdkOptions;
    tx = tx || new TransactionBlock14();
    let primaryCoinInputs = paramss.reward_coins;
    if (paramss.clmm_rewarder_types.length > 0 && primaryCoinInputs === void 0) {
      primaryCoinInputs = [];
      const allCoinAsset = await this._sdk.ClmmSDK.getOwnerCoinAssets(this._sdk.ClmmSDK.senderAddress);
      paramss.clmm_rewarder_types.forEach((type) => {
        primaryCoinInputs.push(TransactionUtil9.buildCoinForAmount(tx, allCoinAsset, BigInt(0), type, false).targetCoin);
      });
    }
    const framsConfig = getPackagerConfigs13(frams);
    const clmmConfig = getPackagerConfigs13(clmm_pool);
    paramss.clmm_rewarder_types?.forEach((type, index) => {
      if (tx) {
        tx.moveCall({
          target: `${frams.published_at}::router::collect_clmm_reward`,
          typeArguments: [type, paramss.coinTypeA, paramss.coinTypeB],
          arguments: [
            tx.object(framsConfig.global_config_id),
            tx.object(clmmConfig.global_config_id),
            tx.object(paramss.clmm_pool_id),
            tx.object(paramss.position_nft_id),
            tx.object(clmmConfig.global_vault_id),
            primaryCoinInputs[index],
            tx.object(CLOCK_ADDRESS9)
          ]
        });
      }
    });
    return tx;
  }
  async collectClmmRewardNoSendPayload(paramss, tx) {
    const { frams } = this.sdk.sdkOptions;
    const { clmm_pool, integrate } = this.sdk.ClmmSDK.sdkOptions;
    let primaryCoinInputs = paramss.reward_coins;
    if (paramss.clmm_rewarder_types.length > 0 && primaryCoinInputs === void 0) {
      primaryCoinInputs = [];
      const allCoinAsset = await this._sdk.ClmmSDK.getOwnerCoinAssets(this._sdk.ClmmSDK.senderAddress);
      paramss.clmm_rewarder_types.forEach((type) => {
        primaryCoinInputs.push(TransactionUtil9.buildCoinForAmount(tx, allCoinAsset, BigInt(0), type, false).targetCoin);
      });
    }
    const framsConfig = getPackagerConfigs13(frams);
    const clmmConfig = getPackagerConfigs13(clmm_pool);
    paramss.clmm_rewarder_types?.forEach((type, index) => {
      if (tx) {
        tx.moveCall({
          target: `${integrate.published_at}::stable_farming::collect_clmm_reward`,
          typeArguments: [type, paramss.coinTypeA, paramss.coinTypeB],
          arguments: [
            tx.object(framsConfig.global_config_id),
            tx.object(clmmConfig.global_config_id),
            tx.object(paramss.clmm_pool_id),
            tx.object(paramss.position_nft_id),
            tx.object(clmmConfig.global_vault_id),
            primaryCoinInputs[index],
            tx.object(CLOCK_ADDRESS9)
          ]
        });
      }
    });
    return tx;
  }
  async getFrarmsConfigs() {
    const { package_id } = this._sdk.sdkOptions.frams;
    const config = {
      global_config_id: "",
      rewarder_manager_id: "",
      rewarder_manager_handle: ""
    };
    const initEventObjs = (await this._sdk.ClmmSDK.fullClient.queryEventsByPage({ MoveEventType: `${package_id}::config::InitConfigEvent` })).data;
    if (initEventObjs.length > 0) {
      const fields = initEventObjs[0].parsedJson;
      config.global_config_id = fields.global_config_id;
      config.admin_cap_id = fields.admin_cap_id;
    }
    const rewarderEventObjs = (await this._sdk.ClmmSDK.fullClient.queryEventsByPage({ MoveEventType: `${package_id}::rewarder::InitRewarderManagerEvent` })).data;
    if (rewarderEventObjs.length > 0) {
      const fields = rewarderEventObjs[0].parsedJson;
      config.rewarder_manager_id = fields.id;
      const res = await this._sdk.ClmmSDK.fullClient.getObject({ id: config.rewarder_manager_id, options: { showContent: true } });
      config.rewarder_manager_handle = res.data.content.fields.rewarders.fields.id.id;
    }
    return config;
  }
  updateCache(key, data, time = cacheTime24h10) {
    let cacheData = this._cache[key];
    if (cacheData) {
      cacheData.overdueTime = getFutureTime10(time);
      cacheData.value = data;
    } else {
      cacheData = new CachedContent10(data, getFutureTime10(time));
    }
    this._cache[key] = cacheData;
  }
  getCache(key, forceRefresh = false) {
    const cacheData = this._cache[key];
    if (!forceRefresh && cacheData?.isValid()) {
      return cacheData.value;
    }
    delete this._cache[key];
    return void 0;
  }
};

// src/sdk.ts
var CetusPeripherySDK = class {
  _cache = {};
  _sdkOptions;
  _launchpad;
  _ido;
  _xcetusModule;
  _xtokenModule;
  _boosterModule;
  _makerModule;
  _clmmSDK;
  _liquidityStrategy;
  _vaults;
  _limitOrder;
  _farmsModule;
  constructor(options, clmmSDK) {
    this._sdkOptions = options;
    this._clmmSDK = clmmSDK;
    this._launchpad = new LaunchpadModule(this);
    this._ido = new IdoModule(this);
    this._xcetusModule = new XCetusModule(this);
    this._xtokenModule = new XTokenModule(this);
    this._boosterModule = new BoosterModule(this);
    this._makerModule = new MakerModule(this);
    this._vaults = new VaultsModule(this);
    this._limitOrder = new LimitOrderModule(this);
    this._farmsModule = new FarmsModule(this);
    this._liquidityStrategy = new LiquidityStrategyModule(this);
    patchFixSuiObjectId(this._sdkOptions);
  }
  get ClmmSDK() {
    return this._clmmSDK;
  }
  get sdkOptions() {
    return this._sdkOptions;
  }
  get Launchpad() {
    return this._launchpad;
  }
  get Ido() {
    return this._ido;
  }
  get XToken() {
    return this._xtokenModule;
  }
  get XCetusModule() {
    return this._xcetusModule;
  }
  get BoosterModule() {
    return this._boosterModule;
  }
  get MakerModule() {
    return this._makerModule;
  }
  get LiquidityStrategy() {
    return this._liquidityStrategy;
  }
  get Vaults() {
    return this._vaults;
  }
  get LimitOrder() {
    return this._limitOrder;
  }
  get Farms() {
    return this._farmsModule;
  }
  getVerifySenderAddress() {
    const { senderAddress } = this.ClmmSDK;
    if (senderAddress.length === 0 || !isValidSuiAddress(senderAddress)) {
      throw Error("this clmm config sdk senderAddess is illegal");
    }
    return senderAddress;
  }
  updateCache(key, data, time = cacheTime24h11) {
    let cacheData = this._cache[key];
    if (cacheData) {
      cacheData.overdueTime = getFutureTime11(time);
      cacheData.value = data;
    } else {
      cacheData = new CachedContent11(data, getFutureTime11(time));
    }
    this._cache[key] = cacheData;
  }
  getCache(key, forceRefresh = false) {
    const cacheData = this._cache[key];
    if (!forceRefresh && cacheData?.isValid()) {
      return cacheData.value;
    }
    delete this._cache[key];
    return void 0;
  }
};

// src/index.ts
var src_default = CetusPeripherySDK;
export {
  AftermathoUtils,
  BoosterRouterModule,
  BoosterUtil,
  CONFIG_PERCENT_MULTIPER,
  CONST_DENOMINATOR,
  CetusPeripherySDK,
  DividendsRouterModule,
  EXCHANGE_RATE_MULTIPER,
  FramsUtils,
  HaedalUtils,
  IdoFactoryModule,
  IdoPoolActivityState,
  IdoPoolModule,
  IdoRouter,
  IdoUtil,
  IdoVersion,
  LIQUIDITY_INJECTION_RATIO_DENOM,
  LaunchpadPoolActivityState,
  LaunchpadRouterModule,
  LauncpadUtil,
  LimitOrderStatus,
  LimitOrderUtils,
  LiquiditySide,
  LiquidityStrategyRouterModule,
  LiquidityStrategyUtils,
  MakerRouterModule,
  MakerUtil,
  ONE_DAY_SECONDS,
  PRICE_MULTIPLICATION,
  PROTOCOL_FEE_DENOMINATOR,
  REDEEM_NUM_MULTIPER,
  StakeProtocol,
  Strategy,
  VaultStatus,
  VaultsRouterModule,
  VaultsUtils,
  VaultsVaultModule,
  VoloUtils,
  XCetusUtil,
  XTokenDividendsRouterModule,
  XTokenRouterModule,
  XTokenUtil,
  XcetusRouterModule,
  src_default as default,
  defaultStrategyConfig,
  isAddMasterLiquidityParams
};
//# sourceMappingURL=index.mjs.map